import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, ReactNode, UIEvent } from 'react';
import { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrgChangefeedEffect } from '@app/context/org-changefeed';
import { useAuthSession } from '@app/context/auth-session';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useMetricsApi, useVisibilityApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { formatDateTime } from '@app/utils/format';
import { buildOpenAiResponsesLogsUrl } from '@app/utils/openaiLogs';
import { useConfig } from '@config/provider';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth, resolveBasePath } from '@app/utils/api';
import { browserTimeZone } from '@app/utils/chatTimeContext';
import { streamSse } from '@app/utils/sse';
import { RUN_TRACE_LEVEL_OPTIONS, type RunTraceLevel } from '@app/utils/chatRunTrace';
import type { ChatThreadSummary, CursorListResponse, SiteSummary } from '@app/types/org-console';
import type {
  ChatActiveArtifactContextschemaSurfaceEnum as ChatArtifactSurface,
  ChatInteractContextschema,
  ChatRunEventArtifactUpdateschema,
  MetricsIntentQueryRangeRequest,
  MetricsSeriesResponse,
  TopologyV2ViewCreateRequest,
  TopologyV2ViewSaveResponse,
  TopologyV2ViewScopeModeschema
} from '@app/types/sdk';
import {
  ChatActiveArtifactContextschemaSurfaceEnum,
  ChatInteractContextschemaToJSON,
  ChatRunEventArtifactUpdateschemaArtifactKindEnum,
  ChatRunEventArtifactUpdateschemaUpdateKindEnum,
  ChatRunEventArtifactUpdateschemaFromJSON
} from '@app/types/sdk';
import type {
  ChatRunEventAgentRefschema,
  ChatRunEventCallRefschema,
  ChatRunPlanStepschema,
  ChatRunPlanStepschemaStatusEnum,
  ChatRunEventProgressschema,
  ChatRunEventStatusschema,
  ChatRunEventArtifact,
  ChatRunEventError,
  ChatRunEventPlan,
  ChatRunEventPlanStep,
  ChatRunEventTrace,
  ArtifactDocumentschema,
  EvidencePointerschema,
  ChatMessageContentBlock as ApiChatMessageContentBlock,
  ChatInteractRequestContentBlock as ApiChatInteractRequestContentBlock,
  ChatChoiceOptionschema,
  ChatFormFieldschema,
  ChatFormResponseEntryschema
} from '@reap/openapi-public-v1';
import {
  ChatRunEventAgentRefschemaFromJSON,
  ChatRunEventCallRefschemaFromJSON,
  ChatRunEventProgressschemaFromJSON,
  ChatRunEventStatusschemaFromJSON,
  ChatRunEventArtifactFromJSON,
  ChatRunEventErrorFromJSON,
  ChatRunEventPlanFromJSON,
  ChatRunEventPlanStepFromJSON,
  ChatRunEventTraceFromJSON,
  ArtifactDocumentschemaFromJSON,
  TopologyV2ResolveRequestToJSON,
  TopologyV2ResolveResponseFromJSON
} from '@reap/openapi-public-v1';
import { ChartArtifactView } from '@app/components/ChartArtifactView';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { TopologyArtifactView } from '@app/components/TopologyArtifactView';
import ChatFilePreviewPane from '@app/components/chat/ChatFilePreviewPane';
import ComposerAttachmentPicker from '@app/components/chat/ComposerAttachmentPicker';
import {
  ChatMessageContentBlocks,
  getPromptAnswerState,
  resolveChatMessageText,
  type ChatMessageContentEvent
} from '@app/components/chat/ChatMessageContentBlocks';
import { useChatPromptInteractions } from '@app/components/chat/useChatPromptInteractions';
import { formatArtifactSize, isFileArtifactKind, mapAttachmentsToArtifacts } from '@app/components/chat/chatAttachments';
import { resolveErrorMessage } from '@app/utils/errors';
import {
  buildFileDownloadPath,
  buildFileMetadataPath,
  buildFileTextContentPath,
  deriveFileDownloadName,
  downloadBlobAsFile,
  formatFilePreviewErrorMessage,
  hasMoreFileText,
  inferReadableUploadMime,
  loadFileArtifactPreview,
  parseArtifactDocument,
  parseFileTextSlice,
  parseUploadedFileRecord,
  resolveArtifactPathWithContent,
  type ChatArtifactLike,
  type FileTextSliceRecord,
  type UploadedFileRecord
} from '@app/components/chat/chatArtifactUtils';
import {
  createAndDownloadOutputExport,
  OUTPUT_EXPORT_FORMAT_LABELS,
  resolveArtifactExportFormats,
  resolveDefaultArtifactExportFormat,
  type OutputExportFormat,
} from '@app/components/chat/outputExportTransport';
import {
  buildTopologyActiveArtifactContext,
  buildTopologyStreamUrl,
  isTopologyArtifactContent,
  topologyArtifactKey,
  type TopologyArtifactContent,
  type TopologyResolveRequest
} from '@app/utils/topologyArtifact';
import { hasChatThreadTag, TOPOLOGY_WORKBENCH_THREAD_TAG } from '@app/utils/chatThreadTags';
import { chatThreadMatchesFilters } from '@app/utils/chatThreadFilters';
import type { TopologyV2ResolveResponse } from '@app/types/sdk';
import type { TopologyArtifactOverlaySnapshot } from '@app/utils/topologyOverlay';
import {
  buildFallbackArtifactRunByMessageId,
  collectThreadRunIds,
  hasFetchedRunArtifacts,
  resolvePreferredThreadRunId
} from './chatThreadArtifacts';
import { artifactIdentityKey, findArtifactRecord, pickArtifactSummary } from './chatArtifactMerge';
import { resolveArtifactSummaryParts } from './chatArtifactTitle';
import { buildArtifactCollections } from './chatArtifactCollection';
import { buildTopologyViewHref, topologyViewSaveSummaryLines } from './topology/topologyCustomViews';

interface ChatArtifact {
  id: string;
  kind: string;
  summary?: string;
  size?: number;
  mimeType?: string;
  createdAt?: string;
  runId?: string;
  labels?: Record<string, string>;
  evidence?: EvidencePointerschema[];
  isFinalAttachment?: boolean;
}

interface ArtifactPayload {
  command?: string;
  output?: string;
}

interface ActiveTopologyArtifactState {
  artifactId: string;
  topology: TopologyArtifactContent;
  surface: ChatArtifactSurface;
  key: string;
}

interface TopologySelectionState {
  nodeIds: string[];
  edgeIds: string[];
}

interface ArtifactTopologySaveState {
  open: boolean;
  name: string;
  description: string;
  saveMode: TopologyV2ViewScopeModeschema;
  busy: boolean;
  error: string | null;
  result: TopologyV2ViewSaveResponse | null;
}

interface ArtifactPreviewRecord {
  content: string;
  loading: boolean;
  error?: string;
  doc?: ArtifactDocumentschema;
  isFull?: boolean;
  objectUrl?: string;
  fileText?: FileTextSliceRecord;
  fileRecord?: UploadedFileRecord;
}

const terminalRunStatuses = new Set(['succeeded', 'failed', 'cancelled']);
const pausedRunStatuses = new Set(['needs_input']);
const INSPECTOR_AUTO_FULL_MAX_BYTES = 128 * 1024;
const THREAD_PAGE_SIZE = 15;
const MAX_ACTIVE_THREAD_SESSION_CACHE = 3;
const THREAD_SCROLL_THRESHOLD_PX = 64;
const CHAT_CHANGEFEED_REFRESH_DEBOUNCE_MS = 700;
const CHAT_TRANSIENT_RETRY_DELAY_MS = 1500;
const CHAT_TRANSIENT_NOTICE_DURATION_MS = 4000;
const REQUESTED_THREAD_MAX_RETRIES = 3;
const SITE_LOOKUP_PAGE_LIMIT = 200;
const SITE_LOOKUP_MAX_PAGES = 25;

const createClientRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ui-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeThreadTitle = (title?: string | null): string => {
  const trimmed = title?.trim();
  if (trimmed) {
    return trimmed;
  }
  return 'Untitled thread';
};

const resourceWithCursor = (resource: string, cursor: string): string => {
  const [path, rawQuery = ''] = resource.split('?');
  const params = new URLSearchParams(rawQuery);
  params.set('after', cursor);
  return `${path}?${params.toString()}`;
};

type EvidencePointer = EvidencePointerschema;

type AgentRef = ChatRunEventAgentRefschema;
type CallRef = ChatRunEventCallRefschema;

interface AgentContext {
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatStatusEvent {
  id: string;
  type: 'status';
  status: 'running' | 'succeeded' | 'failed';
  detail?: string;
  startedAt?: string;
  finishedAt?: string;
  timestamp: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatProgressEvent {
  id: string;
  type: 'progress';
  stage: string;
  detail?: string;
  percent?: number;
  event?: string;
  meta?: Record<string, unknown>;
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatMessageEvent {
  id: string;
  type: 'message';
  role: 'user' | 'assistant';
  text: string;
  contentBlocks?: ChatMessageContentBlock[];
  reactions?: string[];
  artifacts?: ChatArtifact[];
  timestamp: string;
  runId?: string;
  isLivePreview?: boolean;
}

interface ChatArtifactEvent {
  id: string;
  type: 'artifact';
  artifactId?: string;
  kind: string;
  summary?: string;
  mimeType?: string;
  size?: number;
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatArtifactUpdateEvent extends ChatRunEventArtifactUpdateschema {
  id: string;
  type: 'artifact_update';
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatErrorEvent {
  id: string;
  type: 'error';
  title?: string;
  message: string;
  code?: string;
  timestamp: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

type ChatPlanStep = ChatRunPlanStepschema;

interface ChatPlanEvent {
  id: string;
  type: 'plan';
  planId: string;
  round?: number;
  title?: string;
  steps: ChatPlanStep[];
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatPlanStepEvent {
  id: string;
  type: 'plan_step';
  planId: string;
  stepId: string;
  status: string;
  detail?: string;
  percent?: number;
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface ChatTraceEvent {
  id: string;
  type: 'trace';
  text: string;
  kind?: string;
  timestamp: string;
  runId?: string;
  agent?: AgentRef;
  call?: CallRef;
  workflowId?: string;
}

interface AgentPlanHistory {
  planId: string;
  round?: number;
  title?: string;
  steps: ChatPlanStep[];
  updatedAt: string;
  isActive: boolean;
}

type ApprovalDecisionState = 'idle' | 'submitting' | 'approved' | 'rejected' | 'error';

interface ApprovalRequestMeta {
  approvalKey: string;
  title?: string;
  description?: string;
  riskLevel?: string;
  changeValidationPlan?: ChangeValidationPlanArtifact;
}

interface ChangeValidationPlanTestcase {
  testcase_id: string;
  phase: string;
  source_family: string;
  target_device_ids: string[];
  policy?: string;
  rule?: string;
  policy_domain?: string;
  nat_rule?: string;
  acl?: string;
  ace?: string;
  attachment?: string;
  direction?: string;
  protocol?: string;
  destination_port?: number;
  interface?: string;
  vrf?: string;
  prefix?: string;
  subnet?: string;
  vlan?: string;
  required_pre_value?: unknown;
  required_post_value?: unknown;
  missing_evidence_verdict?: string;
  timeout_seconds: number;
  retries: number;
  maximum_evidence_age_seconds: number;
  blocking: boolean;
  severity: string;
  method_family_id?: string;
  observation_group_id?: string;
}

interface ChangeValidationPlanDocument {
  plan_id: string;
  version: number;
  status: string;
  profile_id: string;
  profile_version: string;
  automatic_testcases: ChangeValidationPlanTestcase[];
}

interface ChangeValidationPlanArtifact {
  document: ChangeValidationPlanDocument;
  rawJson: string;
  validationId?: string;
  contentHash?: string;
  deviceName?: string;
}

interface ThreadUiStateSnapshot {
  messageEvents: ChatMessageEventWithDelay[];
  messagesCursor: string | null;
  currentRunId: string | null;
  runEventHistory: Record<string, ChatSseEvent[]>;
  artifactsByRun: Record<string, ChatArtifact[]>;
}

interface ActiveRunStreamSession {
  threadId: string | null;
  lastEventId: string | null;
  lastEventSequence: number;
}

interface MultiplexedChatSseEnvelope {
  runId: string;
  eventId?: number;
  type: string;
  payload: unknown;
}

const serializeMultiplexedCursor = (runId: string, eventId: string): string =>
  eventId.startsWith(`${runId}:`) ? eventId : `${runId}:${eventId}`;

type ThreadAttentionState = 'action_required' | 'ready' | 'failed';

interface AgentCardState {
  key: string;
  agent: AgentRef;
  call?: CallRef;
  parentId?: string;
  runId?: string;
  displayName: string;
  stages: string[];
  currentStage?: string;
  latestLifecycleProgress?: ChatProgressEvent;
  narrationUpdates: ChatProgressEvent[];
  plans: AgentPlanHistory[];
  activePlanId?: string;
  trace?: ChatTraceEvent;
  recentActivity: AgentActivityItem[];
  artifacts: ChatArtifactEvent[];
  firstSeen: string;
  lastUpdated: string;
  indent: number;
}

interface AgentActivityItem {
  id: string;
  type: string;
  label: string;
  timestamp: string;
}

type ChatViewMode = 'user' | 'agent' | 'timeline';

interface ChatMessageRecord {
  id?: string;
  role?: string;
  createdAt?: string;
  runId?: string;
  content?: ChatMessageContentBlock[];
  attachments?: ChatMessageAttachmentRecord[];
}

interface ChatMessageAttachmentRecord {
  resourceType?: string;
  resourceId?: string;
  artifactId?: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
  inlinePreview?: boolean;
  evidence?: EvidencePointer[];
}

interface ChatInteractAttachmentPayload {
  resourceType: 'file' | 'artifact';
  resourceId: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number;
}

interface PendingComposerFile {
  localId: string;
  source: 'local' | 'promoted';
  file?: File;
  fileRecord?: UploadedFileRecord;
  status: 'pending' | 'uploading' | 'uploaded';
  uploadedAttachment?: ChatInteractAttachmentPayload;
  uploadedThreadId?: string;
  error?: string;
}

type ChatChoiceOptionRecord = ChatChoiceOptionschema;
type ChatFormFieldRecord = ChatFormFieldschema;
type ChatFormResponseFieldRecord = ChatFormResponseEntryschema;
type ChatMessageContentBlock = ApiChatMessageContentBlock;
type ChatInteractRequestContentBlock = ApiChatInteractRequestContentBlock;
type ChatChoicePromptBlock = Extract<ChatMessageContentBlock, { type: 'choicePrompt' }>;
type ChatChoiceResponseBlock = Extract<ChatMessageContentBlock, { type: 'choiceResponse' }>;
type ChatConfirmationPromptBlock = Extract<ChatMessageContentBlock, { type: 'confirmationPrompt' }>;
type ChatConfirmationResponseBlock = Extract<ChatMessageContentBlock, { type: 'confirmationResponse' }>;
type ChatInputPromptBlock = Extract<ChatMessageContentBlock, { type: 'inputPrompt' }>;
type ChatInputResponseBlock = Extract<ChatMessageContentBlock, { type: 'inputResponse' }>;
type ChatFormPromptBlock = Extract<ChatMessageContentBlock, { type: 'formPrompt' }>;
type ChatFormResponseBlock = Extract<ChatMessageContentBlock, { type: 'formResponse' }>;
type ChatResponseContentBlock =
  | ChatChoiceResponseBlock
  | ChatConfirmationResponseBlock
  | ChatInputResponseBlock
  | ChatFormResponseBlock;

const AGENT_CARD_NARRATION_LIMIT = 3;
const AGENT_CARD_ACTIVITY_LIMIT = 18;
const uuidTokenPattern = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

type ChatMessagesListResponse = CursorListResponse<ChatMessageRecord>;

interface ChatRagLookupChunkRecord {
  id: string;
  score: number;
  source: string;
  content: string;
  metadata?: Record<string, unknown>;
}

interface ChatRagLookupResultRecord {
  query: string;
  topK: number;
  latencyMs: number;
  chunks: ChatRagLookupChunkRecord[];
}

interface ChatRagCollectionsRecord {
  collections: string[];
}

const RAG_LOOKUP_VENDOR_OPTIONS = [
  { label: 'Cisco', value: 'cisco' },
  { label: 'Arista', value: 'arista' },
  { label: 'Aruba', value: 'aruba' },
  { label: 'Fortinet', value: 'fortinet' },
  { label: 'Silver Peak', value: 'silver_peak' },
  { label: 'Palo Alto Networks', value: 'Palo Alto Networks' },
  { label: 'None', value: 'none' }
] as const;

const RAG_LOOKUP_VENDOR_VALUES = new Set(RAG_LOOKUP_VENDOR_OPTIONS.map((option) => option.value.toLowerCase()));
const DEFAULT_RAG_LOOKUP_VENDOR = 'none';
const RAG_LOOKUP_CHUNK_CONFIG_DOCUMENT_FILE_OPTIONS = [
  'Security Configuration',
  'IP Multicast Routing Configuration',
  'IP Routing Configuration',
  'Multiprotocol Label Switching Configuration',
  'System Management Configuration',
  'Programmability Configuration',
  'Layer 2 Configuration',
  'IP Addressing Services Configuration',
  'Network Management Configuration',
  'BGP EVPN VXLAN Configuration',
  'Interface and Hardware Components Configuration',
  'TrustSec Configuration',
  'Quality of Service Configuration',
  'VLAN Configuration',
  'Stacking and High Availability Configuration',
  'DNA Service for Bonjour Configuration'
] as const;
const RAG_LOOKUP_CHUNK_TROUBLESHOOTING_DOCUMENT_FILE_OPTIONS = [
  'Troubleshoot Power over Ethernet',
  'Troubleshoot EVPN VXLANv6',
  'Verify Head-End Replication in SD-Access Fabric',
  'Troubleshooting Network Latency and Packet',
  'Understand Smart Licensing',
  'Troubleshoot Smart Licensing',
  'Troubleshoot Scenarios with Null0 and MSS Clamping',
  'Verify MPLS',
  'Troubleshoot Fiber Links',
  'Troubleshoot Unexpected Reloads',
  'Troubleshoot MACsec',
  'Troubleshoot Port Flaps',
  'Troubleshoot EtherChannels',
  'Understand Extended Fast Software Upgrade',
  'Troubleshoot Silent Reloads',
  'Troubleshoot Precision',
  'Troubleshoot Output',
  'Troubleshoot MTU',
  'Troubleshoot Mac Address Table Manager',
  'Troubleshoot ARP Resolution in SD-Access Fabric',
  'Troubleshoot Power Supplies',
  'Troubleshoot EVPN VxLAN TRM',
  'Understand Output Drops',
  'Troubleshoot Output Drops',
  'Troubleshoot GuestShell',
  'Troubleshoot DHCP Snooping Database Integrity Due to NTP',
  'Troubleshoot Unknown',
  'Verify SPAN and ERSPAN',
  'Troubleshoot Reloads Due to Stack Issues',
  'Troubleshoot Smart Licensing with Policy after Upgrade to 17.3.2 and Later',
  'Understand REP',
  'Troubleshoot SISF',
  'Verify Differences between PoE 802.3AT/802.3BT',
  'Troubleshoot HSEC License',
  'Troubleshoot DHCP Issues',
  'Verify and Troubleshoot Stackwise',
  'Troubleshoot Rommon',
  'Troubleshoot IGMP for NLB Deployments',
  'Verify Layer 2 LISP Connectivity in SDA',
  'Troubleshoot Control Plane Operations',
  'Troubleshoot DHCP',
  'Troubleshoot IGMP Snooping',
  'Understand Object Manager',
  'Troubleshoot Network Related Audio Issues',
  'Understand Mac Flapping Notifications',
  'Understand Unexpected MAC Learning',
  'Troubleshoot Layer-2 Loops'
] as const;

const copyTextToClipboard = async (value: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document !== 'undefined') {
    const helper = document.createElement('textarea');
    helper.value = value;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    document.execCommand('copy');
    document.body.removeChild(helper);
    return;
  }

  throw new Error('Clipboard API is not available');
};

const toProblemMessage = async (error: unknown, fallback: string): Promise<string> => {
  if (!(error instanceof ApiResponseError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  try {
    const payload = (await error.response.clone().json()) as { title?: string; detail?: string };
    const title = typeof payload?.title === 'string' ? payload.title.trim() : '';
    const detail = typeof payload?.detail === 'string' ? payload.detail.trim() : '';
    if (title && detail) {
      return `${title}: ${detail}`;
    }
    if (title) {
      return title;
    }
    if (detail) {
      return detail;
    }
  } catch {
    // Ignore parse failures and fall back to status-based messaging.
  }

  return `${fallback} (status ${error.response.status})`;
};

const mapMessageToEvent = (
  message: ChatMessageRecord,
  fallbackRole: 'user' | 'assistant' = 'user'
): ChatMessageEventWithDelay => {
  const artifacts = mapAttachmentsToArtifacts(message);
  return {
    id: message.id ?? `${fallbackRole}-${message.createdAt ?? Date.now()}`,
    type: 'message',
    role: message.role === 'assistant' ? 'assistant' : fallbackRole,
    text: resolveChatMessageText(message),
    contentBlocks: message.content ?? undefined,
    artifacts: artifacts.length > 0 ? artifacts : undefined,
    runId: message.runId ?? undefined,
    timestamp: message.createdAt ?? new Date().toISOString(),
    messageId: message.id
  };
};

const extractRunIdFromLocation = (location: string | null): string | null => {
  if (!location) {
    return null;
  }
  const match = location.match(/chat\/runs\/([^/?]+)/i);
  return match ? match[1] : null;
};

type ChatTimelineEvent =
  | ChatStatusEvent
  | ChatProgressEvent
  | ChatMessageEvent
  | ChatArtifactEvent
  | ChatArtifactUpdateEvent
  | ChatErrorEvent
  | ChatPlanEvent
  | ChatPlanStepEvent
  | ChatTraceEvent;
type ChatTimelineEventWithDelay = ChatTimelineEvent & { delayAfter?: number };
type ChatSseEvent = (
  | ChatStatusEvent
  | ChatProgressEvent
  | ChatArtifactEvent
  | ChatArtifactUpdateEvent
  | ChatErrorEvent
  | ChatPlanEvent
  | ChatPlanStepEvent
  | ChatTraceEvent
) & { delayAfter?: number; runId?: string };

type ChatMessageEventWithDelay = ChatMessageEvent & { delayAfter?: number; messageId?: string; runId?: string };
const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`;
  }
  const seconds = ms / 1000;
  return `${seconds.toFixed(1)}s`;
};

const isStatusEvent = (event: ChatTimelineEventWithDelay): event is ChatStatusEvent & { delayAfter?: number } => event.type === 'status';

const getTimelineEventRunId = (event: ChatTimelineEventWithDelay): string | undefined =>
  'runId' in event ? event.runId : undefined;

const textValue = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const defaultTopologyArtifactSaveName = (artifact: ChatArtifact, topology: TopologyArtifactContent): string =>
  textValue(topology.title) || textValue(artifact.summary) || 'Saved topology view';

const defaultArtifactTopologySaveState = (
  artifact: ChatArtifact,
  topology: TopologyArtifactContent
): ArtifactTopologySaveState => ({
  open: true,
  name: defaultTopologyArtifactSaveName(artifact, topology),
  description: '',
  saveMode: 'reusable',
  busy: false,
  error: null,
  result: null
});

const preferredTopologyViewScope = (
  view: TopologyV2ViewSaveResponse['view'],
  topology: TopologyArtifactContent
): { scopeType?: string | null; scopeId?: string | null } => {
  if (view.boundScope?.scopeType && view.boundScope?.scopeId) {
    return {
      scopeType: view.boundScope.scopeType,
      scopeId: view.boundScope.scopeId
    };
  }
  return {
    scopeType: textValue(topology.binding?.scope?.scopeType) || undefined,
    scopeId: textValue(topology.binding?.scope?.scopeId) || undefined
  };
};

const parseArtifactDocumentLoose = (raw: string): any | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const inferTableColumns = (rows: Record<string, unknown>[], maxScan = 50): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  rows.slice(0, Math.max(0, maxScan)).forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    });
  });
  return out;
};

const buildCsv = (rows: Record<string, unknown>[], columns: string[]): string => {
  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    const rawValue = String(value);
    if (/[",\n\r]/.test(rawValue)) {
      return `"${rawValue.replace(/"/g, '""')}"`;
    }
    return rawValue;
  };
  const header = columns.map(escape).join(',');
  const body = rows.map((row) => columns.map((col) => escape(row[col])).join(',')).join('\n');
  return `${header}\n${body}\n`;
};

const parseArtifactPayload = (raw: string): ArtifactPayload => {
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        command: typeof parsed.command === 'string' ? parsed.command : undefined,
        output: typeof parsed.output === 'string' ? parsed.output : undefined
      };
    }
  } catch {
    // ignore parse errors and return fallback
  }
  return { output: raw };
};

const computeResponseDuration = (events: ChatTimelineEventWithDelay[]): number | null => {
  const running = events.find((event): event is ChatStatusEvent & { delayAfter?: number } => isStatusEvent(event) && event.status === 'running');
  const completed = [...events]
    .reverse()
    .find((event): event is ChatStatusEvent & { delayAfter?: number } => isStatusEvent(event) && event.status !== 'running');
  if (running?.startedAt && completed?.finishedAt) {
    const start = Date.parse(running.startedAt);
    const end = Date.parse(completed.finishedAt);
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) {
      return end - start;
    }
  }
  return null;
};

const sortEventsByTimestamp = <T extends { timestamp: string }>(events: T[]): T[] => {
  return [...events].sort((a, b) => new Date(a.timestamp).valueOf() - new Date(b.timestamp).valueOf());
};

const mergeMessageEventLists = (
  existing: ChatMessageEventWithDelay[],
  incoming: ChatMessageEventWithDelay[]
): ChatMessageEventWithDelay[] => {
  const merged = new Map<string, ChatMessageEventWithDelay>();
  for (const event of [...existing, ...incoming]) {
    merged.set(event.id, event);
  }
  return sortEventsByTimestamp(Array.from(merged.values()));
};

const mergeRunEvents = (existing: ChatSseEvent[], incoming: ChatSseEvent[]): ChatSseEvent[] => {
  const merged = new Map<string, ChatSseEvent>();
  const addEvent = (event: ChatSseEvent) => {
    const key = event.id ?? `${event.type}-${event.timestamp}`;
    merged.set(key, event);
  };
  existing.forEach(addEvent);
  incoming.forEach(addEvent);
  return sortEventsByTimestamp(Array.from(merged.values()));
};

const appendAgentActivity = (current: AgentActivityItem[], next: AgentActivityItem): AgentActivityItem[] => {
  const updated = [...current.filter((item) => item.id !== next.id), next];
  if (updated.length <= AGENT_CARD_ACTIVITY_LIMIT) {
    return updated;
  }
  return updated.slice(updated.length - AGENT_CARD_ACTIVITY_LIMIT);
};

const buildAgentActivityItem = (event: ChatSseEvent): AgentActivityItem | null => {
  const timestamp = event.timestamp ?? new Date().toISOString();
  switch (event.type) {
    case 'progress': {
      const stage = event.stage?.trim() ?? '';
      const detail = event.detail?.trim() ?? '';
      const label = detail || stage || 'Progress updated';
      return { id: event.id, type: event.type, label: stage && detail ? `${stage}: ${detail}` : label, timestamp };
    }
    case 'trace':
      return { id: event.id, type: event.type, label: event.text, timestamp };
    case 'plan':
      return { id: event.id, type: event.type, label: event.title?.trim() || 'Plan updated', timestamp };
    case 'plan_step': {
      const detail = event.detail?.trim() ?? '';
      const label = detail || [event.stepId, event.status].filter(Boolean).join(' · ') || 'Plan step updated';
      return { id: event.id, type: event.type, label, timestamp };
    }
    case 'artifact':
      return { id: event.id, type: event.type, label: event.summary?.trim() || 'Artifact ready', timestamp };
    case 'artifact_update':
      return { id: event.id, type: event.type, label: event.summary?.trim() || 'Artifact updated', timestamp };
    case 'error':
      return { id: event.id, type: event.type, label: event.message?.trim() || 'Run failed', timestamp };
    case 'status':
      return { id: event.id, type: event.type, label: event.detail?.trim() || event.status, timestamp };
    default:
      return null;
  }
};

const collectApprovalTimelineEvents = (runEventHistory: Record<string, ChatSseEvent[]>): ChatProgressEvent[] => {
  const approvals: ChatProgressEvent[] = [];
  Object.values(runEventHistory).forEach((events) => {
    events.forEach((event) => {
      if (event.type !== 'progress') {
        return;
      }
      const approval = extractApprovalMeta(event);
      if (!approval) {
        return;
      }
      approvals.push({
        ...event,
        id: event.id,
        type: 'progress'
      });
    });
  });
  return approvals;
};

const collectVisibleRunTimelineEvents = (events: ChatSseEvent[]): ChatSseEvent[] =>
  events.filter((event) => {
    if (event.type === 'progress' && extractApprovalMeta(event)) {
      return false;
    }
    return true;
  });

interface UserProgressEntry {
  id: string;
  label: string;
  detail?: string;
  timestamp: string;
  terminal?: boolean;
}

const buildUserProgressEntries = (events: ChatSseEvent[]): UserProgressEntry[] => {
  const entries: UserProgressEntry[] = [];
  const pushEntry = (entry: UserProgressEntry) => {
    const last = entries[entries.length - 1];
    if (last && last.label === entry.label && (last.detail ?? '') === (entry.detail ?? '')) {
      entries[entries.length - 1] = entry;
      return;
    }
    entries.push(entry);
  };

  events.forEach((event) => {
    if (event.type === 'progress') {
      if (extractApprovalMeta(event)) {
        return;
      }
      const stage = event.stage?.trim();
      const detail = event.detail?.trim();
      const label =
        stage && stage.toLowerCase() !== 'assistant'
          ? stage
          : detail
            ? 'Update'
            : typeof event.percent === 'number'
              ? 'Progress'
              : '';
      if (!label) {
        return;
      }
      pushEntry({
        id: event.id,
        label,
        detail: detail || (typeof event.percent === 'number' ? `${Math.round(event.percent)}% complete` : undefined),
        timestamp: event.timestamp
      });
      return;
    }

    if (event.type !== 'status') {
      return;
    }

    const normalized = event.status?.trim().toLowerCase();
    let label = '';
    let terminal = false;
    switch (normalized) {
      case 'queued':
        label = 'Queued';
        break;
      case 'running':
        label = 'Working';
        break;
      case 'succeeded':
        label = 'Completed';
        terminal = true;
        break;
      case 'failed':
        label = 'Failed';
        terminal = true;
        break;
      case 'cancelled':
        label = 'Cancelled';
        terminal = true;
        break;
      case 'needs_input':
        label = 'Needs input';
        break;
      default:
        label = event.status?.trim() || 'Status update';
        terminal = terminalRunStatuses.has(normalized ?? '');
        break;
    }
    pushEntry({
      id: event.id,
      label,
      detail: event.detail?.trim() || undefined,
      timestamp: event.timestamp,
      terminal
    });
  });

  return entries;
};

type RunStatusSummary = {
  queued?: ChatStatusEvent;
  running?: ChatStatusEvent;
  paused?: ChatStatusEvent;
  terminal?: ChatStatusEvent;
  latest?: ChatStatusEvent;
};

const summarizeRunStatus = (events: ChatSseEvent[]): RunStatusSummary => {
  const summary: RunStatusSummary = {};
  events.forEach((event) => {
    if (event.type !== 'status') {
      return;
    }
    summary.latest = event;
    const normalized = event.status?.toLowerCase();
    if (normalized === 'queued') {
      summary.queued = event;
    } else if (normalized === 'running') {
      summary.running = event;
    } else if (normalized && pausedRunStatuses.has(normalized)) {
      summary.paused = event;
    } else if (normalized && terminalRunStatuses.has(normalized)) {
      summary.terminal = event;
    }
  });
  return summary;
};

const buildLiveAssistantPreviewText = (events: ChatSseEvent[], summary?: RunStatusSummary): string => {
  const latestTrace = [...events].reverse().find((event): event is ChatTraceEvent => event.type === 'trace' && Boolean(event.text?.trim()));
  const latestProgress = [...events].reverse().find((event): event is ChatProgressEvent => {
    if (event.type !== 'progress') {
      return false;
    }
    if (extractApprovalMeta(event)) {
      return false;
    }
    return Boolean(event.detail?.trim()) || Boolean(event.stage?.trim());
  });
  const latestPlanStep = [...events].reverse().find((event): event is ChatPlanStepEvent => event.type === 'plan_step');

  const lines: string[] = [];
  const progressStage = latestProgress?.stage?.trim();
  const progressDetail = latestProgress?.detail?.trim();
  const traceText = latestTrace?.text?.trim();
  const planStepDetail = latestPlanStep?.detail?.trim();

  if (progressStage && progressStage.toLowerCase() !== 'assistant') {
    lines.push(`**${progressStage}**`);
  }
  if (traceText) {
    lines.push(traceText);
  } else if (progressDetail) {
    lines.push(progressDetail);
  } else if (planStepDetail) {
    lines.push(planStepDetail);
  }

  if (lines.length === 0) {
    const statusDetail = summary?.running?.detail?.trim() ?? summary?.queued?.detail?.trim();
    if (statusDetail) {
      lines.push(statusDetail);
    }
  }

  if (lines.length === 0) {
    if (summary?.queued) {
      lines.push('Getting things ready...');
    } else {
      lines.push('Working on your request...');
    }
  }

  if (progressDetail && !traceText && progressDetail !== lines[lines.length - 1]) {
    lines.push(progressDetail);
  }

  return lines.join('\n\n').trim();
};

const coerceEventId = (input: unknown): string | null => {
  if (typeof input === 'string') {
    const trimmed = input.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof input === 'number' && Number.isFinite(input)) {
    return Math.trunc(input).toString();
  }
  return null;
};

const normalizeDateString = (input: unknown): string | undefined => {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof Date) {
    return input.toISOString();
  }
  return undefined;
};

const parseAgentContext = (payload: Record<string, unknown>): AgentContext => {
  const agent = payload.agent ? ChatRunEventAgentRefschemaFromJSON(payload.agent) : undefined;
  const call = payload.call ? ChatRunEventCallRefschemaFromJSON(payload.call) : undefined;
  const workflowId = typeof payload.workflowId === 'string' ? payload.workflowId : undefined;
  return { agent, call, workflowId };
};

const formatAgentName = (agent?: AgentRef): string => {
  if (!agent) {
    return 'Agent';
  }
  if (agent.displayName) {
    return agent.displayName;
  }
  if (agent.kind) {
    const label = agent.kind
      .split('_')
      .filter(Boolean)
      .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
      .join(' ');
    return label || agent.id || 'Agent';
  }
  return agent.id || 'Agent';
};

const extractDeviceHintFromTaskKey = (taskKey?: string | null): string | null => {
  const normalized = String(taskKey ?? '').trim();
  if (!normalized) {
    return null;
  }
  const match = normalized.match(/^task_([^_]+)_/i);
  if (match?.[1]) {
    return match[1];
  }
  return null;
};

const formatDeviceLabel = (labels?: Record<string, string> | null): string | null => {
  const deviceName = String(labels?.deviceName ?? labels?.deviceHostname ?? '').trim();
  const deviceAddr = String(labels?.deviceAddress ?? '').trim();
  if (deviceName && deviceAddr) {
    return `${deviceName} (${deviceAddr})`;
  }
  if (deviceName) {
    return deviceName;
  }
  if (deviceAddr) {
    return deviceAddr;
  }
  return null;
};

const formatAgentCardTitle = (agent?: AgentRef, call?: CallRef): string => {
  if (agent?.displayName) {
    return agent.displayName;
  }
  const base = formatAgentName(agent);
  const deviceHint = extractDeviceHintFromTaskKey(call?.taskKey);
  if (!deviceHint) {
    return base;
  }
  if (base.toLowerCase() === 'device agent') {
    return `Device Agent — ${deviceHint}`;
  }
  return `${base} — ${deviceHint}`;
};

const isActiveRunStatus = (status?: string | null): boolean => {
  if (!status) {
    return false;
  }
  const normalized = status.trim().toLowerCase();
  return normalized === 'running' || normalized === 'queued';
};

const isPausedRunStatus = (status?: string | null): boolean => {
  if (!status) {
    return false;
  }
  return pausedRunStatuses.has(status.trim().toLowerCase());
};

const shouldCacheThreadSession = (
  thread: Pick<ChatThreadSummary, 'id' | 'latestRunStatus'> | null | undefined,
  streamingThreadIds?: ReadonlySet<string>
): boolean => {
  if (!thread?.id) {
    return false;
  }
  if (streamingThreadIds?.has(thread.id)) {
    return true;
  }
  return isActiveRunStatus(thread.latestRunStatus);
};

const snapshotHasResolvedCurrentRun = (snapshot: ThreadUiStateSnapshot): boolean => {
  if (!snapshot.currentRunId) {
    return true;
  }
  return snapshot.messageEvents.some(
    (event) => event.role === 'assistant' && event.runId === snapshot.currentRunId
  );
};

const snapshotHasUnresolvedCurrentRun = (snapshot: ThreadUiStateSnapshot): boolean => {
  if (!snapshot.currentRunId) {
    return false;
  }
  const hasCurrentRunMessage = snapshot.messageEvents.some((event) => event.runId === snapshot.currentRunId);
  const hasCurrentRunHistory = (snapshot.runEventHistory[snapshot.currentRunId] ?? []).length > 0;
  return !snapshotHasResolvedCurrentRun(snapshot) && (hasCurrentRunMessage || hasCurrentRunHistory);
};

const shouldRetainThreadSnapshot = (
  thread: Pick<ChatThreadSummary, 'id' | 'latestRunStatus'> | null | undefined,
  snapshot: ThreadUiStateSnapshot | undefined,
  streamingThreadIds?: ReadonlySet<string>
): boolean => {
  if (shouldCacheThreadSession(thread, streamingThreadIds)) {
    return true;
  }
  return snapshot ? snapshotHasUnresolvedCurrentRun(snapshot) : false;
};

const shouldDisplayUnresolvedRun = ({
  currentRunId,
  latestRunId,
  streamingRunId,
  hasAssistantReply,
  hasRunHistory
}: {
  currentRunId?: string | null;
  latestRunId?: string | null;
  streamingRunId?: string | null;
  hasAssistantReply: boolean;
  hasRunHistory: boolean;
}): boolean => {
  if (!currentRunId || hasAssistantReply) {
    return false;
  }
  return streamingRunId === currentRunId || latestRunId === currentRunId || hasRunHistory;
};

const mergeThreadsById = (existing: ChatThreadSummary[], incoming: ChatThreadSummary[]): ChatThreadSummary[] => {
  if (incoming.length === 0) {
    return existing;
  }
  const byId = new Map<string, ChatThreadSummary>();
  const withoutIds: ChatThreadSummary[] = [];
  const add = (thread: ChatThreadSummary) => {
    if (thread.id) {
      byId.set(thread.id, thread);
    } else {
      withoutIds.push(thread);
    }
  };
  existing.forEach(add);
  incoming.forEach(add);
  return [...byId.values(), ...withoutIds];
};

const resolveArtifactIcon = (artifact: ChatArtifact): string => {
  const mime = artifact.mimeType?.toLowerCase() ?? '';
  if (artifact.kind === 'table') {
    return '📊';
  }
  if (artifact.kind === 'chart') {
    return '📈';
  }
  if (artifact.kind === 'topology') {
    return '◎';
  }
  if (artifact.kind === 'pcap') {
    return '📡';
  }
  if (mime.startsWith('image/')) {
    return '🖼️';
  }
  if (mime.startsWith('text/') || mime.includes('json') || artifact.kind === 'config') {
    return '📝';
  }
  return '📁';
};

const renderArtifactSummary = (artifact: ChatArtifact): ReactNode => {
  const summary = resolveArtifactSummaryParts(artifact);
  if (summary.type === 'command') {
    return (
      <span>
        <span className="chat-artifact-summary-prefix">Output for </span>
        <span className="chat-artifact-command">{summary.command}</span>
      </span>
    );
  }
  return summary.text;
};

const artifactLinkLabel = (artifact: ChatArtifact): string => {
  const summary = resolveArtifactSummaryParts(artifact);
  return summary.type === 'command' ? `Output for ${summary.command}` : summary.text;
};

const genericArtifactLinkLabels = new Set(['artifact', 'details', 'file', 'output', 'report', 'table']);

const describeArtifactContext = (artifact: ChatArtifact): string | null => {
  const labels = artifact.labels ?? {};
  const device = (labels.deviceName ?? labels.deviceHostname ?? labels.deviceAddress ?? '').trim();
  if (device) {
    return device;
  }
  const tagKeys = Object.keys(labels)
    .filter((k) => k.startsWith('tag.') && String(labels[k] ?? '').trim() !== '')
    .sort();
  if (tagKeys.length === 0) {
    return null;
  }
  const key = tagKeys.find((k) => ['tag.scope', 'tag.site', 'tag.siteName', 'tag.vlan', 'tag.ospf', 'tag.instance'].includes(k)) ?? tagKeys[0];
  const value = String(labels[key] ?? '').trim();
  if (!value) {
    return null;
  }
  return key === 'tag.scope' ? value : `${key.replace(/^tag\./, '')}=${value}`;
};

const renderArtifactDisplayTitle = (artifact: ChatArtifact): ReactNode => {
  const context = describeArtifactContext(artifact);
  if (!context) {
    return renderArtifactSummary(artifact);
  }
  return (
    <span>
      {renderArtifactSummary(artifact)}
      <span className="chat-artifact-context"> — {context}</span>
    </span>
  );
};

const describeArtifactPresentation = (artifact: ChatArtifact): string => {
  const mime = artifact.mimeType?.toLowerCase() ?? '';
  if (isFileArtifactKind(artifact.kind)) {
    return 'Original uploaded file – opens according to current file availability, with text preview, inline preview, or download behavior.';
  }
  if (mime.startsWith('image/')) {
    return 'Image artifact – opens as a preview or download.';
  }
  if (artifact.kind === 'chart') {
    return 'Chart – rendered from Vega-Lite with artifact or metric data.';
  }
  if (artifact.kind === 'topology') {
    return 'Topology – rendered from a topology-service view binding.';
  }
  if (mime.startsWith('text/') || mime.includes('json')) {
    return 'Text artifact – ideal for inline reading.';
  }
  if (artifact.kind === 'pcap') {
    return 'PCAP capture – download to inspect in Wireshark.';
  }
  if (artifact.kind === 'table') {
    return 'Structured table – export for spreadsheets.';
  }
  return 'Download to view.';
};

const artifactStateKey = (artifact: Pick<ChatArtifact, 'id' | 'runId'>): string => artifactIdentityKey(artifact.id, artifact.runId);

const isChartContent = (content: any): boolean => content?.type === 'chart';

const isChartArtifact = (artifact: ChatArtifact): boolean => {
  const kind = String(artifact.kind ?? '').toLowerCase();
  return kind === 'chart';
};

const isTopologyArtifact = (artifact: ChatArtifact): boolean => {
  const kind = String(artifact.kind ?? '').toLowerCase();
  return kind === 'topology';
};

const approvalEventKey = (runId: string | undefined, approvalKey: string): string => `${runId ?? 'run'}:${approvalKey}`;

const parseChangeValidationPlanArtifact = (meta: Record<string, unknown>): ChangeValidationPlanArtifact | undefined => {
  if (meta.change_validation_plan_schema !== 'change_validation_plan.v1' || typeof meta.change_validation_plan !== 'string') {
    return undefined;
  }
  try {
    const document = JSON.parse(meta.change_validation_plan) as Partial<ChangeValidationPlanDocument>;
    if (
      typeof document.plan_id !== 'string' ||
      typeof document.version !== 'number' ||
      typeof document.profile_id !== 'string' ||
      !Array.isArray(document.automatic_testcases)
    ) {
      return undefined;
    }
    return {
      document: document as ChangeValidationPlanDocument,
      rawJson: meta.change_validation_plan,
      validationId: typeof meta.validation_id === 'string' ? meta.validation_id : undefined,
      contentHash:
        typeof meta.change_validation_plan_content_hash === 'string' ? meta.change_validation_plan_content_hash : undefined,
      deviceName: typeof meta.change_validation_device_name === 'string' ? meta.change_validation_device_name : undefined
    };
  } catch {
    return undefined;
  }
};

const humanizePlanToken = (value: string): string =>
  value
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const formatPlanPhase = (phase: string): string => (phase === 'both' ? 'Before and after change' : humanizePlanToken(phase));

const formatPlanValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined || value === null) {
    return 'Not specified';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const planTestcaseScope = (testcase: ChangeValidationPlanTestcase): string => {
  const parts = [
    testcase.interface && `Interface ${testcase.interface}`,
    testcase.vlan && `VLAN ${testcase.vlan}`,
    testcase.vrf && `VRF ${testcase.vrf}`,
    testcase.prefix && `Prefix ${testcase.prefix}`,
    testcase.subnet && `Subnet ${testcase.subnet}`,
    testcase.policy_domain && `Policy domain ${testcase.policy_domain}`,
    testcase.policy && `Policy ${testcase.policy}`,
    testcase.rule && `Rule ${testcase.rule}`,
    testcase.nat_rule && `NAT rule ${testcase.nat_rule}`,
    testcase.acl && `ACL ${testcase.acl}`,
    testcase.ace && `ACE ${testcase.ace}`,
    testcase.attachment && `Attachment ${testcase.attachment}`,
    testcase.direction && `Direction ${testcase.direction}`,
    testcase.protocol && `Protocol ${testcase.protocol}`,
    testcase.destination_port ? `Port ${testcase.destination_port}` : undefined
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(' · ') : 'Device-level validation';
};

const ChangeValidationPlanCard = ({ artifact }: { artifact: ChangeValidationPlanArtifact }) => {
  const { document } = artifact;
  const downloadPlan = () => {
    downloadBlobAsFile(
      new Blob([artifact.rawJson], { type: 'application/json' }),
      `change-validation-plan-${document.plan_id}-v${document.version}.json`
    );
  };
  return (
    <section className="change-validation-plan" aria-label="Change validation plan">
      <div className="change-validation-plan__header">
        <div>
          <strong>Validation plan v{document.version}</strong>
          <span>{humanizePlanToken(document.profile_id)}</span>
        </div>
        <span className="change-validation-plan__count">
          {document.automatic_testcases.length} automatic {document.automatic_testcases.length === 1 ? 'check' : 'checks'}
        </span>
      </div>
      {artifact.deviceName && (
        <div className="change-validation-plan__target">
          <span>Device</span>
          <strong>{artifact.deviceName}</strong>
        </div>
      )}
      <div className="change-validation-plan__checks">
        {document.automatic_testcases.map((testcase) => (
          <article className="change-validation-plan__check" key={testcase.testcase_id}>
            <div className="change-validation-plan__check-title">
              <strong>{humanizePlanToken(testcase.testcase_id)}</strong>
              <span>{formatPlanPhase(testcase.phase)}</span>
            </div>
            <p>{planTestcaseScope(testcase)}</p>
            <dl>
              <div>
                <dt>Before</dt>
                <dd>{formatPlanValue(testcase.required_pre_value)}</dd>
              </div>
              <div>
                <dt>Expected after</dt>
                <dd>{formatPlanValue(testcase.required_post_value)}</dd>
              </div>
              <div>
                <dt>Failure impact</dt>
                <dd>{testcase.blocking ? `Blocking · ${testcase.severity}` : testcase.severity}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
      <div className="change-validation-plan__footer">
        <details>
          <summary>Technical details</summary>
          <dl>
            {artifact.validationId && <div><dt>Validation ID</dt><dd>{artifact.validationId}</dd></div>}
            <div><dt>Plan ID</dt><dd>{document.plan_id}</dd></div>
            {artifact.contentHash && <div><dt>Content hash</dt><dd>{artifact.contentHash}</dd></div>}
            <div><dt>Profile</dt><dd>{document.profile_id}@{document.profile_version}</dd></div>
            {document.automatic_testcases.map((testcase) => (
              <Fragment key={`technical-${testcase.testcase_id}`}>
                <div><dt>{humanizePlanToken(testcase.testcase_id)} source</dt><dd>{testcase.source_family}</dd></div>
                <div><dt>Target device ID</dt><dd>{testcase.target_device_ids.join(', ')}</dd></div>
                {testcase.method_family_id && <div><dt>Method</dt><dd>{testcase.method_family_id}</dd></div>}
                <div><dt>Execution limits</dt><dd>{testcase.timeout_seconds}s timeout · {testcase.retries} retry</dd></div>
                <div><dt>Maximum evidence age</dt><dd>{testcase.maximum_evidence_age_seconds}s</dd></div>
                {testcase.missing_evidence_verdict && <div><dt>Missing evidence result</dt><dd>{testcase.missing_evidence_verdict}</dd></div>}
              </Fragment>
            ))}
          </dl>
        </details>
        <button type="button" className="chat-timeline__artifact-button" onClick={downloadPlan}>
          Download plan JSON
        </button>
      </div>
    </section>
  );
};

const extractApprovalMeta = (event: ChatProgressEvent): ApprovalRequestMeta | null => {
  const meta = event.meta;
  if (!meta || typeof meta !== 'object') {
    return null;
  }
  if (meta.approval_required !== true && event.stage !== 'approval_required') {
    return null;
  }
  const approvalKey = typeof meta.approval_key === 'string' ? meta.approval_key.trim() : '';
  if (!approvalKey) {
    return null;
  }
  return {
    approvalKey,
    title: typeof meta.approval_title === 'string' ? meta.approval_title.trim() : undefined,
    description:
      typeof meta.approval_description === 'string' ? meta.approval_description.trim() : event.detail?.trim() || undefined,
    riskLevel: typeof meta.risk_level === 'string' ? meta.risk_level.trim() : undefined,
    changeValidationPlan: parseChangeValidationPlanArtifact(meta)
  };
};

const getChatThreadsLoadErrorMessage = (_error: unknown): string =>
  'We couldn’t load chat threads right now. Please refresh and try again.';

const getChatThreadLoadErrorMessage = (_error: unknown): string =>
  'We couldn’t load this chat right now. Please refresh and try again.';

const getApiErrorStatus = (error: unknown): number | null => {
  if (error instanceof ApiResponseError) {
    return error.response.status;
  }
  if (error instanceof Error) {
    const match = error.message.match(/status\s+(\d{3})/i);
    if (match) {
      const status = Number.parseInt(match[1] ?? '', 10);
      return Number.isNaN(status) ? null : status;
    }
  }
  return null;
};

const isTransientChatLoadError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status !== null && status >= 500 && status < 600;
};

const isTransientThreadListError = (error: unknown): boolean =>
  isTransientChatLoadError(error) ||
  (error instanceof Error && (error.name === 'TimeoutError' || /timed out/i.test(error.message)));

const isUnavailableThreadError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return status === 400 || status === 403 || status === 404 || status === 410;
};


const ChatAgentsPage = () => {
  const orgId = useActiveOrgId();
  const location = useLocation();
  const chatLocationState = (location.state as { sharedLinkFallbackMessage?: unknown } | null) ?? null;
  const navigate = useNavigate();
  const { apiBaseUrl, openAiLogsEnabled } = useConfig();
  const metricsApi = useMetricsApi();
  const visibilityApi = useVisibilityApi();
  const { user } = useAuthSession();
  const basePath = useMemo(() => resolveBasePath(apiBaseUrl), [apiBaseUrl]);
  const { fetchWithAuthRetry, fetchOrgCollection, fetchOrgResource } = useAuthFetch();
  const isChatRoute = useMemo(() => location.pathname.replace(/\/+$/, '') === '/app/chat-agents', [location.pathname]);
  const requestedThreadId = useMemo(() => {
    const value = new URLSearchParams(location.search).get('threadId')?.trim();
    return value ? value : null;
  }, [location.search]);
  const requestedComposerAttachment = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fileId = params.get('attachFileId')?.trim();
    if (!fileId) {
      return null;
    }
    const name = params.get('attachFileName')?.trim() || fileId;
    const mimeType = params.get('attachMimeType')?.trim() || 'application/vnd.tcpdump.pcap';
    const category = params.get('attachCategory')?.trim() || null;
    return { fileId, name, mimeType, category };
  }, [location.search]);
  const requestedOrgId = useMemo(() => {
    const value = new URLSearchParams(location.search).get('orgId')?.trim();
    return value ? value : null;
  }, [location.search]);
  const sharedLinkFallbackMessage =
    typeof chatLocationState?.sharedLinkFallbackMessage === 'string' ? chatLocationState.sharedLinkFallbackMessage : null;
  const isChatRouteRef = useRef(isChatRoute);
  useEffect(() => {
    isChatRouteRef.current = isChatRoute;
  }, [isChatRoute]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedThread, setSelectedThread] = useState<ChatThreadSummary | null>(null);
  const [messageEvents, setMessageEvents] = useState<ChatMessageEventWithDelay[]>([]);
  const messageEventsRef = useRef(messageEvents);
  useEffect(() => {
    messageEventsRef.current = messageEvents;
  }, [messageEvents]);
  const [runEventHistory, setRunEventHistory] = useState<Record<string, ChatSseEvent[]>>({});
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const currentRunIdRef = useRef(currentRunId);
  useEffect(() => {
    currentRunIdRef.current = currentRunId;
  }, [currentRunId]);
  const activeStreamSessionsRef = useRef<Map<string, ActiveRunStreamSession>>(new Map());
  const multiplexedStreamControllerRef = useRef<AbortController | null>(null);
  const multiplexedStreamGenerationRef = useRef(0);
  const multiplexedReconnectTimerRef = useRef<number | null>(null);
  const multiplexedReconnectAttemptRef = useRef(0);
  const [multiplexedReconnectEpoch, setMultiplexedReconnectEpoch] = useState(0);
  const [activeStreamRunIds, setActiveStreamRunIds] = useState<string[]>([]);
  const [threadAttentionById, setThreadAttentionById] = useState<Record<string, ThreadAttentionState>>({});
  const activeStreamingThreadIds = useMemo(() => {
    const threadIds = new Set<string>();
    activeStreamRunIds.forEach((runId) => {
      const threadId = activeStreamSessionsRef.current.get(runId)?.threadId;
      if (threadId) {
        threadIds.add(threadId);
      }
    });
    return threadIds;
  }, [activeStreamRunIds]);
  const streamingRunId = currentRunId && activeStreamRunIds.includes(currentRunId) ? currentRunId : null;
  const isStreaming = streamingRunId !== null;
  const runThreadMapRef = useRef<Map<string, string>>(new Map());
  const consumedComposerAttachmentRef = useRef<string | null>(null);
  const pendingThreadTitleRefreshRef = useRef<Set<string>>(new Set());
  const creatingThreadDraftRef = useRef<ChatThreadSummary | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const threadListRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef(true);
  const runEventHistoryRef = useRef(runEventHistory);
  useEffect(() => {
    runEventHistoryRef.current = runEventHistory;
  }, [runEventHistory]);
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null);
  const messagesCursorRef = useRef(messagesCursor);
  useEffect(() => {
    messagesCursorRef.current = messagesCursor;
  }, [messagesCursor]);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [composerText, setComposerText] = useState('');
  const [traceLevel, setTraceLevel] = useState<RunTraceLevel>('summary');
  const [pendingComposerFiles, setPendingComposerFiles] = useState<PendingComposerFile[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [threadsScopeKey, setThreadsScopeKey] = useState<string | null>(null);
  const [threadsMeta, setThreadsMeta] = useState<CursorListResponse<ChatThreadSummary>['meta'] | null>(null);
  const [threadsLoadingMore, setThreadsLoadingMore] = useState(false);
  const [threadsLoadMoreError, setThreadsLoadMoreError] = useState<Error | null>(null);
  const [pendingDeleteThread, setPendingDeleteThread] = useState<ChatThreadSummary | null>(null);
  const [deletingThreadId, setDeletingThreadId] = useState<string | null>(null);
  const [locallyDeletedThreadIds, setLocallyDeletedThreadIds] = useState<Set<string>>(() => new Set());
  const [threadDeleteToast, setThreadDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<Record<string, 'liked' | 'disliked' | undefined>>({});
  const [runRating, setRunRating] = useState<number | null>(null);
  const [approvalStateByKey, setApprovalStateByKey] = useState<Record<string, { state: ApprovalDecisionState; error?: string }>>({});
  const [creatingThread, setCreatingThread] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<'mine' | 'all'>('mine');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'org' | 'private'>('private');
  const [onlyPinned, setOnlyPinned] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [chatLoadErrorMessage, setChatLoadErrorMessage] = useState<string | null>(null);
  const [ragLookupModalOpen, setRagLookupModalOpen] = useState(false);
  const [ragLookupQuery, setRagLookupQuery] = useState('');
  const [ragLookupTopKInput, setRagLookupTopKInput] = useState('');
  const [ragLookupCollectionOptions, setRagLookupCollectionOptions] = useState<string[]>([]);
  const [ragLookupCollectionsLoading, setRagLookupCollectionsLoading] = useState(false);
  const [ragLookupCollections, setRagLookupCollections] = useState<string[]>([]);
  const [ragLookupVendor, setRagLookupVendor] = useState(DEFAULT_RAG_LOOKUP_VENDOR);
  const [ragLookupChunkConfigDocumentFile, setRagLookupChunkConfigDocumentFile] = useState('');
  const [ragLookupChunkTroubleshootingDocumentFile, setRagLookupChunkTroubleshootingDocumentFile] = useState('');
  const [ragLookupLoading, setRagLookupLoading] = useState(false);
  const [ragLookupError, setRagLookupError] = useState<string | null>(null);
  const [ragLookupResult, setRagLookupResult] = useState<ChatRagLookupResultRecord | null>(null);
  const [ragLookupCopiedChunkId, setRagLookupCopiedChunkId] = useState<string | null>(null);

  useEffect(() => {
    setPendingDeleteThread(null);
    setDeletingThreadId(null);
    setLocallyDeletedThreadIds(new Set());
    setThreadDeleteToast(null);
  }, [orgId]);

  const promptAnswerState = useMemo(() => getPromptAnswerState(messageEvents), [messageEvents]);
  const currentUserId = useMemo(() => {
    const profile = user?.user as { id?: string | null } | undefined;
    const id = profile?.id?.trim();
    return id && id.length > 0 ? id : null;
  }, [user]);
  const [localPinned, setLocalPinned] = useState<Record<string, boolean>>({});
  const [artifactsByRun, setArtifactsByRun] = useState<Record<string, ChatArtifact[]>>({});
  const artifactsByRunRef = useRef(artifactsByRun);
  useEffect(() => {
    artifactsByRunRef.current = artifactsByRun;
  }, [artifactsByRun]);
  const [threadListCollapsed, setThreadListCollapsed] = useState(false);
  const [copiedThreadLink, setCopiedThreadLink] = useState(false);
  const [siteNameById, setSiteNameById] = useState<Record<string, string>>({});
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<'preview' | 'artifacts'>('preview');
  const [inspectorPreviewSource, setInspectorPreviewSource] = useState<'timeline' | 'artifacts' | null>(null);
  const [inspectorArtifactId, setInspectorArtifactId] = useState<string | null>(null);
  const [inspectorArtifact, setInspectorArtifact] = useState<ChatArtifact | null>(null);
  const [inspectorMaximized, setInspectorMaximized] = useState(false);
  const [inspectorWrap, setInspectorWrap] = useState<Record<string, boolean>>({});
  const [artifactsLoadingCount, setArtifactsLoadingCount] = useState(0);
  const inspectorRef = useRef<HTMLDivElement | null>(null);
  const [artifactPreview, setArtifactPreview] = useState<Record<string, ArtifactPreviewRecord>>({});
  const [artifactPreviewReloadRequests, setArtifactPreviewReloadRequests] = useState<Record<string, number>>({});
  const [artifactExportFormats, setArtifactExportFormats] = useState<Record<string, OutputExportFormat>>({});
  const [artifactExpanded, setArtifactExpanded] = useState<Record<string, boolean>>({});
  const [messageOutputsExpanded, setMessageOutputsExpanded] = useState<Record<string, boolean>>({});
  const [artifactHighlights, setArtifactHighlights] = useState<Record<string, { startLine: number; endLine: number }>>({});
  const [artifactTableHighlights, setArtifactTableHighlights] = useState<Record<string, string[]>>({});
  const [artifactViewReps, setArtifactViewReps] = useState<Record<string, string>>({});
  const [artifactTopologySaveState, setArtifactTopologySaveState] = useState<Record<string, ArtifactTopologySaveState>>({});
  const [activeTopologyArtifact, setActiveTopologyArtifact] = useState<ActiveTopologyArtifactState | null>(null);
  const [topologySelectionsByArtifactId, setTopologySelectionsByArtifactId] = useState<Record<string, TopologySelectionState>>({});
  const [topologyOverlaySnapshotsByArtifactId, setTopologyOverlaySnapshotsByArtifactId] = useState<
    Record<string, TopologyArtifactOverlaySnapshot>
  >({});
  const artifactsLoading = artifactsLoadingCount > 0;
  const {
    promptChoiceDrafts,
    promptInputDrafts,
    promptFormDrafts,
    promptSubmissionState,
    setPromptChoiceDrafts,
    setPromptInputDrafts,
    setPromptFormDrafts,
    submitSingleChoicePrompt,
    submitChoicePromptDraft,
    submitConfirmationPrompt,
    submitInputPrompt,
    submitFormPrompt
  } = useChatPromptInteractions({
    submitBlocks: async (_messageId, _questionId, _blockType, content) => {
      const threadId = selectedThread?.id;
      if (!threadId) {
        throw new Error('No chat thread selected.');
      }
      await interactWithThread(threadId, content);
    }
  });
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);
  const [planOpenOverrides, setPlanOpenOverrides] = useState<Record<string, boolean>>({});
  const [agentPanelOpenOverrides, setAgentPanelOpenOverrides] = useState<Record<string, boolean>>({});
  const [agentCardCollapsedOverrides, setAgentCardCollapsedOverrides] = useState<Record<string, boolean>>({});
  const requestedRunArtifactsRef = useRef<Set<string>>(new Set());
  const planCollapseTimers = useRef<Record<string, number>>({});
  const copiedRunIdResetTimeoutRef = useRef<number | null>(null);
  const copiedThreadLinkResetTimeoutRef = useRef<number | null>(null);
  const pendingInternalThreadIdsRef = useRef<Set<string>>(new Set());
  const [chatViewMode, setChatViewMode] = useState<ChatViewMode>('user');
  const completedRunsRef = useRef<Set<string>>(new Set());
  const prevThreadIdRef = useRef<string | null>(null);
  const skipThreadReloadRef = useRef(false);
  const changefeedRefreshTimeoutRef = useRef<number | null>(null);
  const threadUiStateCacheRef = useRef<Map<string, ThreadUiStateSnapshot>>(new Map());
  const [requestedThreadLoadState, setRequestedThreadLoadState] = useState<{
    threadId: string;
    status: 'loading' | 'resolved' | 'failed' | 'retrying';
  } | null>(null);
  const [suppressAutoThreadSelection, setSuppressAutoThreadSelection] = useState(false);
  const [chatRefreshNotice, setChatRefreshNotice] = useState<string | null>(null);
  const [requestedThreadRetryKey, setRequestedThreadRetryKey] = useState(0);
  const reloadThreads = useCallback(() => setRefreshKey((prev) => prev + 1), []);
  const chatRefreshNoticeTimeoutRef = useRef<number | null>(null);
  const requestedThreadRetryTimeoutRef = useRef<number | null>(null);
  const requestedThreadRetryCountRef = useRef<{ threadId: string; count: number } | null>(null);
  const selectedThreadRef = useRef<ChatThreadSummary | null>(selectedThread);
  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);
  const showTransientChatRefreshNotice = useCallback((message: string) => {
    if (chatRefreshNoticeTimeoutRef.current !== null) {
      window.clearTimeout(chatRefreshNoticeTimeoutRef.current);
    }
    setChatRefreshNotice(message);
    chatRefreshNoticeTimeoutRef.current = window.setTimeout(() => {
      chatRefreshNoticeTimeoutRef.current = null;
      setChatRefreshNotice(null);
    }, CHAT_TRANSIENT_NOTICE_DURATION_MS);
  }, []);
  const clearTransientChatRefreshNotice = useCallback(() => {
    if (chatRefreshNoticeTimeoutRef.current !== null) {
      window.clearTimeout(chatRefreshNoticeTimeoutRef.current);
      chatRefreshNoticeTimeoutRef.current = null;
    }
    setChatRefreshNotice(null);
  }, []);
  const scheduleRequestedThreadRetry = useCallback(() => {
    if (requestedThreadRetryTimeoutRef.current !== null) {
      return;
    }
    requestedThreadRetryTimeoutRef.current = window.setTimeout(() => {
      requestedThreadRetryTimeoutRef.current = null;
      setRequestedThreadRetryKey((prev) => prev + 1);
    }, CHAT_TRANSIENT_RETRY_DELAY_MS);
  }, []);
  useEffect(() => {
    if (!requestedThreadId) {
      requestedThreadRetryCountRef.current = null;
      if (requestedThreadRetryTimeoutRef.current !== null) {
        window.clearTimeout(requestedThreadRetryTimeoutRef.current);
        requestedThreadRetryTimeoutRef.current = null;
      }
      return;
    }
    if (requestedThreadRetryCountRef.current?.threadId !== requestedThreadId) {
      requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: 0 };
      if (requestedThreadRetryTimeoutRef.current !== null) {
        window.clearTimeout(requestedThreadRetryTimeoutRef.current);
        requestedThreadRetryTimeoutRef.current = null;
      }
    }
  }, [requestedThreadId]);
  const updateThreadIdInLocation = useCallback(
    (threadId: string | null, options?: { replace?: boolean; markAsInternal?: boolean }) => {
      if (!isChatRoute) {
        return;
      }
      if (options?.markAsInternal && threadId) {
        pendingInternalThreadIdsRef.current.add(threadId);
      }
      const params = new URLSearchParams(location.search);
      if (threadId) {
        params.set('threadId', threadId);
      } else {
        params.delete('threadId');
      }
      const nextSearch = params.toString();
      const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
      if (nextSearch === currentSearch) {
        return;
      }
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : ''
        },
        { replace: options?.replace ?? true }
      );
    },
    [isChatRoute, location.pathname, location.search, navigate]
  );

  const persistThreadUiState = useCallback(
    (threadOverride?: Pick<ChatThreadSummary, 'id' | 'latestRunStatus'> | null) => {
      const thread = threadOverride ?? selectedThread;
      const threadId = thread?.id;
      if (!threadId || creatingThread) {
        return;
      }
      const snapshot: ThreadUiStateSnapshot = {
        messageEvents: messageEventsRef.current,
        messagesCursor: messagesCursorRef.current,
        currentRunId: currentRunIdRef.current,
        runEventHistory: runEventHistoryRef.current,
        artifactsByRun: artifactsByRunRef.current
      };
      const cache = threadUiStateCacheRef.current;
      if (!shouldRetainThreadSnapshot(thread, snapshot, activeStreamingThreadIds)) {
        cache.delete(threadId);
        return;
      }
      cache.set(threadId, snapshot);
      while (cache.size > MAX_ACTIVE_THREAD_SESSION_CACHE) {
        const oldestKey = cache.keys().next().value;
        if (!oldestKey) {
          break;
        }
        cache.delete(oldestKey);
      }
    },
    [activeStreamingThreadIds, creatingThread, selectedThread]
  );
  const selectedThreadShareUrl = useMemo(() => {
    const threadId = selectedThread?.id;
    if (!threadId) {
      return null;
    }
    const params = new URLSearchParams();
    if (orgId) {
      params.set('orgId', orgId);
    }
    params.set('threadId', threadId);
    const search = params.toString();
    const relativeUrl = `${location.pathname}${search ? `?${search}` : ''}`;
    if (typeof window === 'undefined') {
      return relativeUrl;
    }
    return `${window.location.origin}${relativeUrl}`;
  }, [location.pathname, orgId, selectedThread?.id]);

  const updateCachedThreadSnapshot = useCallback(
    (threadId: string, updater: (snapshot: ThreadUiStateSnapshot) => ThreadUiStateSnapshot) => {
      const cache = threadUiStateCacheRef.current;
      const isSelectedThread = selectedThreadRef.current?.id === threadId;
      const current =
        cache.get(threadId) ??
        (isSelectedThread
          ? {
              messageEvents: messageEventsRef.current,
              messagesCursor: messagesCursorRef.current,
              currentRunId: currentRunIdRef.current,
              runEventHistory: runEventHistoryRef.current,
              artifactsByRun: artifactsByRunRef.current
            }
          : {
              messageEvents: [],
              messagesCursor: null,
              currentRunId: null,
              runEventHistory: {},
              artifactsByRun: {}
            });
      cache.set(threadId, updater(current));
    },
    []
  );

  const setThreadAttentionForRun = useCallback((runId: string, attention: ThreadAttentionState | null) => {
    const threadId = runThreadMapRef.current.get(runId);
    if (!threadId) {
      return;
    }
    setThreadAttentionById((prev) => {
      if (attention && prev[threadId] === attention) {
        return prev;
      }
      if (!attention && !prev[threadId]) {
        return prev;
      }
      const next = { ...prev };
      if (attention) {
        next[threadId] = attention;
      } else {
        delete next[threadId];
      }
      return next;
    });
  }, []);

  const appendRunEvent = useCallback(
    (runId: string | null, event: ChatSseEvent) => {
      if (!runId) {
        return;
      }
      const enrichedEvent: ChatSseEvent = { ...event, runId };
      const threadId = runThreadMapRef.current.get(runId) ?? null;
      if (threadId) {
        updateCachedThreadSnapshot(threadId, (snapshot) => ({
          ...snapshot,
          currentRunId: snapshot.currentRunId ?? runId,
          runEventHistory: {
            ...snapshot.runEventHistory,
            [runId]: mergeRunEvents(snapshot.runEventHistory[runId] ?? [], [enrichedEvent])
          }
        }));
      }
      if (threadId && selectedThreadRef.current?.id !== threadId) {
        return;
      }
      const next = {
        ...runEventHistoryRef.current,
        [runId]: mergeRunEvents(runEventHistoryRef.current[runId] ?? [], [enrichedEvent])
      };
      runEventHistoryRef.current = next;
      setRunEventHistory(next);
    },
    [updateCachedThreadSnapshot]
  );

  const mergeMessageEventForRun = useCallback(
    (runId: string, event: ChatMessageEventWithDelay) => {
      const threadId = runThreadMapRef.current.get(runId) ?? null;
      if (threadId) {
        updateCachedThreadSnapshot(threadId, (snapshot) => ({
          ...snapshot,
          currentRunId: snapshot.currentRunId ?? runId,
          messageEvents: mergeMessageEventLists(snapshot.messageEvents, [event])
        }));
      }
      if (threadId && selectedThreadRef.current?.id !== threadId) {
        return;
      }
      setMessageEvents((prev) => {
        const next = mergeMessageEventLists(prev, [event]);
        messageEventsRef.current = next;
        return next;
      });
    },
    [updateCachedThreadSnapshot]
  );

  const updateArtifactsForRun = useCallback(
    (runId: string, updater: (artifacts: ChatArtifact[]) => ChatArtifact[]) => {
      const threadId = runThreadMapRef.current.get(runId) ?? null;
      if (threadId) {
        updateCachedThreadSnapshot(threadId, (snapshot) => ({
          ...snapshot,
          artifactsByRun: {
            ...snapshot.artifactsByRun,
            [runId]: updater(snapshot.artifactsByRun[runId] ?? [])
          }
        }));
      }
      if (threadId && selectedThreadRef.current?.id !== threadId) {
        return;
      }
      setArtifactsByRun((prev) => {
        const next = {
          ...prev,
          [runId]: updater(prev[runId] ?? [])
        };
        artifactsByRunRef.current = next;
        return next;
      });
    },
    [updateCachedThreadSnapshot]
  );
  const authorizedRequest = useCallback(async (url: string, init: RequestInit = {}) => {
    const headers = await cloneHeadersWithAuth(init.headers);
    const isMultipartBody = typeof FormData !== 'undefined' && init.body instanceof FormData;
    if (init.body && !headers['Content-Type'] && !isMultipartBody) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetchWithAuthRetry(url, { ...init, headers });
    if (!response.ok) {
      throw new ApiResponseError(response, `Request failed with status ${response.status}`);
    }
    return response;
  }, [fetchWithAuthRetry]);

  const clearActiveThreadUiState = useCallback(() => {
    messageEventsRef.current = [];
    messagesCursorRef.current = null;
    currentRunIdRef.current = null;
    runEventHistoryRef.current = {};
    artifactsByRunRef.current = {};
    setMessageEvents([]);
    setMessagesCursor(null);
    setCurrentRunId(null);
    setRunEventHistory({});
    setArtifactsByRun({});
  }, []);

  const restoreCachedThreadUiState = useCallback((threadId: string): boolean => {
    const cached = threadUiStateCacheRef.current.get(threadId);
    if (!cached) {
      return false;
    }
    messageEventsRef.current = cached.messageEvents;
    messagesCursorRef.current = cached.messagesCursor;
    currentRunIdRef.current = cached.currentRunId;
    runEventHistoryRef.current = cached.runEventHistory;
    artifactsByRunRef.current = cached.artifactsByRun;
    setMessageEvents(cached.messageEvents);
    setMessagesCursor(cached.messagesCursor);
    setCurrentRunId(cached.currentRunId);
    setRunEventHistory(cached.runEventHistory);
    setArtifactsByRun(cached.artifactsByRun);
    return true;
  }, []);

  const requestOrgApi = useCallback(
    async (resource: string, init: RequestInit = {}) => {
      if (!orgId) {
        throw new Error('No organization selected.');
      }
      const url = buildOrgUrl(basePath, orgId, resource);
      return authorizedRequest(url, init);
    },
    [authorizedRequest, basePath, orgId]
  );

  const buildThreadsQueryString = useCallback(
    (after?: string | null) => {
      const params = new URLSearchParams();
      params.set('limit', String(THREAD_PAGE_SIZE));
      if (after) {
        params.set('after', after);
      }
      if (ownerFilter === 'mine' && currentUserId) {
        params.set('createdByUserId', currentUserId);
      }
      return params.toString();
    },
    [currentUserId, ownerFilter]
  );

  const queryMetricRange = useCallback(
    async (request: MetricsIntentQueryRangeRequest): Promise<MetricsSeriesResponse> => {
      if (!orgId) {
        throw new Error('No organization selected.');
      }
      return metricsApi.orgsOrgIdMetricsQueryRangePost({
        orgId,
        metricsIntentQueryRangeRequest: request
      });
    },
    [metricsApi, orgId]
  );

  const resolveTopologyArtifactView = useCallback(
    async (viewId: string, request: TopologyResolveRequest): Promise<TopologyV2ResolveResponse> => {
      const response = await requestOrgApi(`topology/views/${encodeURIComponent(viewId)}/resolve`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(TopologyV2ResolveRequestToJSON(request))
      });
      return TopologyV2ResolveResponseFromJSON(await response.json());
    },
    [requestOrgApi]
  );

  const streamTopologyArtifactView = useCallback(
    async (
      viewId: string,
      request: TopologyResolveRequest,
      onEvent: (type: string, data: string) => void,
      signal: AbortSignal,
      fromSeq?: number
    ): Promise<void> => {
      if (!orgId) {
        throw new Error('No organization selected.');
      }
      const baseUrl = buildOrgUrl(basePath, orgId, `topology/views/${encodeURIComponent(viewId)}/stream`);
      await streamSse(buildTopologyStreamUrl(baseUrl, request, fromSeq), { onEvent }, signal);
    },
    [basePath, orgId]
  );

  const streamTopologyArtifact = useCallback(
    async (
      artifactId: string,
      overlayRevision: number,
      onEvent: (type: string, data: string) => void,
      signal: AbortSignal,
      fromSeq?: number
    ): Promise<void> => {
      if (!orgId) {
        throw new Error('No organization selected.');
      }
      const url = new URL(buildOrgUrl(basePath, orgId, `chat/artifacts/${encodeURIComponent(artifactId)}/stream`));
      url.searchParams.set('overlayRevision', String(Math.max(0, Math.floor(overlayRevision))));
      if (typeof fromSeq === 'number' && Number.isFinite(fromSeq) && fromSeq > 0) {
        url.searchParams.set('fromSeq', String(Math.floor(fromSeq)));
      }
      await streamSse(url.toString(), { onEvent }, signal);
    },
    [basePath, orgId]
  );

  const topologyArtifactUpdatesByArtifactId = useMemo(() => {
    const grouped: Record<string, ChatArtifactUpdateEvent[]> = {};
    const seen = new Set<string>();
    Object.values(runEventHistory).forEach((events) => {
      events.forEach((event) => {
        if (event.type !== 'artifact_update') {
          return;
        }
        if (event.artifactKind !== ChatRunEventArtifactUpdateschemaArtifactKindEnum.Topology) {
          return;
        }
        if (event.updateKind !== ChatRunEventArtifactUpdateschemaUpdateKindEnum.TopologyOverlayPatch) {
          return;
        }
        const dedupeKey = event.id || `${event.artifactId}:${event.revision}`;
        if (seen.has(dedupeKey)) {
          return;
        }
        seen.add(dedupeKey);
        const list = grouped[event.artifactId] ?? [];
        list.push(event);
        grouped[event.artifactId] = list;
      });
    });
    Object.values(grouped).forEach((events) => {
      events.sort((left, right) => {
        const byRevision = left.revision - right.revision;
        if (byRevision !== 0) {
          return byRevision;
        }
        return new Date(left.timestamp).valueOf() - new Date(right.timestamp).valueOf();
      });
    });
    return grouped;
  }, [runEventHistory]);

  const rememberTopologyOverlaySnapshot = useCallback(
    (artifactId: string, snapshot: TopologyArtifactOverlaySnapshot) => {
      setTopologyOverlaySnapshotsByArtifactId((prev) => {
        const current = prev[artifactId];
        const currentKey = current
          ? JSON.stringify({
              revision: current.revision,
              layerIds: current.layerIds,
              selectionNodeIds: current.selectionNodeIds,
              selectionEdgeIds: current.selectionEdgeIds,
              needsReload: current.needsReload ?? false
            })
          : '';
        const nextKey = JSON.stringify({
          revision: snapshot.revision,
          layerIds: snapshot.layerIds,
          selectionNodeIds: snapshot.selectionNodeIds,
          selectionEdgeIds: snapshot.selectionEdgeIds,
          needsReload: snapshot.needsReload ?? false
        });
        if (currentKey === nextKey) {
          return prev;
        }
        return { ...prev, [artifactId]: snapshot };
      });
    },
    []
  );

  const activateTopologyArtifact = useCallback(
    (
      artifactId: string,
      topology: TopologyArtifactContent,
      surface: ChatArtifactSurface,
      overlay?: TopologyArtifactOverlaySnapshot
    ) => {
      if (overlay) {
        rememberTopologyOverlaySnapshot(artifactId, overlay);
      }
      const key = topologyArtifactKey(topology);
      setActiveTopologyArtifact((prev) => {
        if (prev?.artifactId === artifactId && prev.surface === surface && prev.key === key) {
          return prev;
        }
        return { artifactId, topology, surface, key };
      });
    },
    [rememberTopologyOverlaySnapshot]
  );

  const updateTopologySelection = useCallback(
    (artifactId: string, event: MouseEvent, kind: 'node' | 'edge', id: string) => {
      const additive = event.metaKey || event.ctrlKey || event.shiftKey;
      setTopologySelectionsByArtifactId((prev) => {
        const current = prev[artifactId] ?? { nodeIds: [], edgeIds: [] };
        const nodeIds = new Set(additive ? current.nodeIds : []);
        const edgeIds = new Set(additive ? current.edgeIds : []);
        const targetSet = kind === 'node' ? nodeIds : edgeIds;
        if (additive && targetSet.has(id)) {
          targetSet.delete(id);
        } else {
          targetSet.add(id);
        }
        const nextSelection = {
          nodeIds: Array.from(nodeIds).sort(),
          edgeIds: Array.from(edgeIds).sort()
        };
        const next = { ...prev };
        if (nextSelection.nodeIds.length === 0 && nextSelection.edgeIds.length === 0) {
          delete next[artifactId];
        } else {
          next[artifactId] = nextSelection;
        }
        return next;
      });
    },
    []
  );

  const clearTopologySelection = useCallback((artifactId: string) => {
    setTopologySelectionsByArtifactId((prev) => {
      if (!prev[artifactId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[artifactId];
      return next;
    });
  }, []);

  const patchArtifactTopologySaveState = useCallback(
    (artifactId: string, next: Partial<ArtifactTopologySaveState> | ((current: ArtifactTopologySaveState) => ArtifactTopologySaveState)) => {
      setArtifactTopologySaveState((prev) => {
        const current =
          prev[artifactId] ??
          ({
            open: false,
            name: '',
            description: '',
            saveMode: 'reusable',
            busy: false,
            error: null,
            result: null
          } satisfies ArtifactTopologySaveState);
        const resolved = typeof next === 'function' ? next(current) : { ...current, ...next };
        return { ...prev, [artifactId]: resolved };
      });
    },
    []
  );

  const openArtifactTopologySave = useCallback(
    (artifact: ChatArtifact, topology: TopologyArtifactContent) => {
      const artifactKey = artifactStateKey(artifact);
      patchArtifactTopologySaveState(artifactKey, (current) => {
        const base = current.name.trim() ? current : defaultArtifactTopologySaveState(artifact, topology);
        return {
          ...base,
          open: true,
          error: null
        };
      });
    },
    [patchArtifactTopologySaveState]
  );

  const closeArtifactTopologySave = useCallback(
    (artifactId: string) => {
      patchArtifactTopologySaveState(artifactId, { open: false, busy: false, error: null });
    },
    [patchArtifactTopologySaveState]
  );

  const handleOpenSavedTopologyView = useCallback(
    (response: TopologyV2ViewSaveResponse, topology: TopologyArtifactContent) => {
      const viewId = textValue(response.view.viewId);
      if (!viewId) {
        return;
      }
      navigate(buildTopologyViewHref(viewId, preferredTopologyViewScope(response.view, topology)));
    },
    [navigate]
  );

  const handleCreateTopologyArtifactView = useCallback(
    async (artifact: ChatArtifact, topology: TopologyArtifactContent, artifactRep?: string) => {
      if (!orgId) {
        return;
      }
      const artifactKey = artifactStateKey(artifact);
      patchArtifactTopologySaveState(artifactKey, (current) => ({ ...current, busy: true, error: null }));
      try {
        const snapshot = artifactTopologySaveState[artifactKey] ?? defaultArtifactTopologySaveState(artifact, topology);
        const request: TopologyV2ViewCreateRequest = {
          name: snapshot.name.trim(),
          description: snapshot.description.trim() || undefined,
          saveMode: snapshot.saveMode,
          source: {
            kind: 'topology_artifact_snapshot',
            artifactId: artifact.id,
            artifactRep: artifactRep?.trim() || undefined
          }
        };
        const response = await visibilityApi.orgsOrgIdTopologyViewsPost({
          orgId,
          topologyV2ViewCreateRequest: request
        });
        patchArtifactTopologySaveState(artifactKey, (current) => ({
          ...current,
          open: true,
          busy: false,
          error: null,
          result: response,
          name: response.view.name ?? current.name,
          description: response.view.description ?? current.description,
          saveMode: response.view.scopeMode ?? current.saveMode
        }));
      } catch (error) {
        const message = await toProblemMessage(error, 'Failed to save custom topology view.');
        patchArtifactTopologySaveState(artifactKey, (current) => ({
          ...current,
          busy: false,
          error: message
        }));
      }
    },
    [artifactTopologySaveState, orgId, patchArtifactTopologySaveState, visibilityApi]
  );

  const buildCurrentChatInteractContext = useCallback((): ChatInteractContextschema | undefined => {
    if (!activeTopologyArtifact) {
      return undefined;
    }
    const artifactId = activeTopologyArtifact.artifactId;
    const uiSelection = topologySelectionsByArtifactId[artifactId];
    const overlaySnapshot = topologyOverlaySnapshotsByArtifactId[artifactId];
    const overlayUpdates = topologyArtifactUpdatesByArtifactId[artifactId] ?? [];
    const persistedRevision = activeTopologyArtifact.topology.presentation?.overlayState?.revision;
    const revision = Math.max(
      typeof persistedRevision === 'number' && Number.isFinite(persistedRevision) ? persistedRevision : 0,
      overlaySnapshot?.revision ?? 0,
      ...overlayUpdates.map((event) => event.revision)
    );
    const selectedNodeIds =
      uiSelection?.nodeIds && uiSelection.nodeIds.length > 0 ? uiSelection.nodeIds : overlaySnapshot?.selectionNodeIds;
    const selectedEdgeIds =
      uiSelection?.edgeIds && uiSelection.edgeIds.length > 0 ? uiSelection.edgeIds : overlaySnapshot?.selectionEdgeIds;

    return {
      clientRequestId: createClientRequestId(),
      ...(browserTimeZone() ? { timeZone: browserTimeZone() } : {}),
      activeArtifact: buildTopologyActiveArtifactContext({
        artifactId,
        topology: activeTopologyArtifact.topology,
        surface: activeTopologyArtifact.surface,
        selectedNodeIds,
        selectedEdgeIds,
        revision
      })
    };
  }, [
    activeTopologyArtifact,
    topologyArtifactUpdatesByArtifactId,
    topologyOverlaySnapshotsByArtifactId,
    topologySelectionsByArtifactId
  ]);

  const submitApprovalDecision = useCallback(
    async (runId: string, approvalKey: string, approved: boolean) => {
      const decisionKey = approvalEventKey(runId, approvalKey);
      setApprovalStateByKey((prev) => ({ ...prev, [decisionKey]: { state: 'submitting' } }));
      try {
        let reason = '';
        if (!approved) {
          reason = window.prompt('Reason for rejecting this action (optional):', '')?.trim() ?? '';
        }
        await requestOrgApi(`chat/runs/${runId}/approval-decisions`, {
          method: 'POST',
          body: JSON.stringify({
            approvalKey,
            approved,
            ...(reason ? { reason } : {})
          })
        });
        setApprovalStateByKey((prev) => ({
          ...prev,
          [decisionKey]: { state: approved ? 'approved' : 'rejected' }
        }));
        setThreadAttentionForRun(runId, null);
      } catch (error) {
        const message = await toProblemMessage(error, approved ? 'Failed to approve action' : 'Failed to reject action');
        setApprovalStateByKey((prev) => ({
          ...prev,
          [decisionKey]: { state: 'error', error: message }
        }));
      }
    },
    [requestOrgApi, setThreadAttentionForRun]
  );

  const resetRagLookupModalState = useCallback(() => {
    setRagLookupQuery('');
    setRagLookupTopKInput('');
    setRagLookupCollections([]);
    setRagLookupVendor(DEFAULT_RAG_LOOKUP_VENDOR);
    setRagLookupChunkConfigDocumentFile('');
    setRagLookupChunkTroubleshootingDocumentFile('');
    setRagLookupLoading(false);
    setRagLookupCollectionsLoading(false);
    setRagLookupError(null);
    setRagLookupResult(null);
    setRagLookupCopiedChunkId(null);
  }, []);

  const loadRagLookupCollections = useCallback(async () => {
    setRagLookupCollectionsLoading(true);
    try {
      const response = await requestOrgApi('chat/rag-collections');
      const payload = (await response.json()) as ChatRagCollectionsRecord;
      const next = Array.isArray(payload.collections)
        ? Array.from(new Set(payload.collections.map((value) => value.trim()).filter(Boolean))).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
          )
        : [];
      setRagLookupCollectionOptions(next);
      setRagLookupCollections((previous) => previous.filter((collection) => next.includes(collection)));
      if (next.length === 0) {
        setRagLookupError('No collections available from rag-service.');
      }
    } catch (error) {
      const message = await toProblemMessage(error, 'Failed to load RAG collections');
      setRagLookupError(message);
      setRagLookupCollectionOptions([]);
    } finally {
      setRagLookupCollectionsLoading(false);
    }
  }, [requestOrgApi]);

  const openRagLookupModal = useCallback(() => {
    console.debug('[chat-rag-lookup] modal_open');
    resetRagLookupModalState();
    setRagLookupModalOpen(true);
    void loadRagLookupCollections();
  }, [loadRagLookupCollections, resetRagLookupModalState]);

  const closeRagLookupModal = useCallback(() => {
    console.debug('[chat-rag-lookup] modal_close');
    setRagLookupModalOpen(false);
    resetRagLookupModalState();
  }, [resetRagLookupModalState]);

  const runRagLookupValidation = useCallback(async () => {
    const query = ragLookupQuery.trim();
    if (!query) {
      setRagLookupError('Query is required.');
      return;
    }
    const collections = Array.from(new Set(ragLookupCollections.map((value) => value.trim()).filter(Boolean)));
    if (collections.length === 0) {
      setRagLookupError('Select at least one collection.');
      return;
    }
    const vendor = ragLookupVendor.trim();
    if (!RAG_LOOKUP_VENDOR_VALUES.has(vendor.toLowerCase())) {
      setRagLookupError('Select a vendor option.');
      return;
    }
    const configCollections = collections.filter((collection) => collection.toLowerCase().includes('config'));
    const troubleshootingCollections = collections.filter((collection) =>
      collection.toLowerCase().includes('troubleshooting')
    );
    const chunkConfigDocumentFile = ragLookupChunkConfigDocumentFile.trim();
    const chunkTroubleshootingDocumentFile = ragLookupChunkTroubleshootingDocumentFile.trim();

    const topKRaw = ragLookupTopKInput.trim();
    let topK: number | undefined;
    if (topKRaw) {
      const parsed = Number(topKRaw);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        setRagLookupError('topK must be a positive integer.');
        return;
      }
      topK = parsed;
    }

    const requestId = `ui-rag-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setRagLookupLoading(true);
    setRagLookupError(null);
    setRagLookupCopiedChunkId(null);
    const collectionFiltersEntries = [
      ...(chunkConfigDocumentFile
        ? configCollections.map((collection) => [collection, { document_file: chunkConfigDocumentFile }] as const)
        : []),
      ...(chunkTroubleshootingDocumentFile
        ? troubleshootingCollections.map((collection) => [collection, { document_file: chunkTroubleshootingDocumentFile }] as const)
        : [])
    ];
    const collectionFilters =
      collectionFiltersEntries.length > 0 ? Object.fromEntries(collectionFiltersEntries) : undefined;

    console.debug('[chat-rag-lookup] request_start', {
      requestId,
      queryLength: query.length,
      topK: topK ?? null,
      collections,
      vendor,
      collectionFilters
    });

    try {
      const response = await requestOrgApi('chat/rag-lookup', {
        method: 'POST',
        headers: { 'X-Request-Id': requestId },
        body: JSON.stringify({
          query,
          collections,
          vendor,
          ...(typeof topK === 'number' ? { topK } : {}),
          ...(collectionFilters ? { collectionFilters } : {})
        })
      });

      const payload = (await response.json()) as ChatRagLookupResultRecord;
      const normalizedChunks = Array.isArray(payload.chunks)
        ? payload.chunks.map((chunk) => ({
            id: typeof chunk.id === 'string' ? chunk.id : '',
            score: typeof chunk.score === 'number' ? chunk.score : 0,
            source: typeof chunk.source === 'string' ? chunk.source : '',
            content: typeof chunk.content === 'string' ? chunk.content : '',
            metadata: chunk.metadata && typeof chunk.metadata === 'object' ? chunk.metadata : undefined
          }))
        : [];
      const normalized: ChatRagLookupResultRecord = {
        query: typeof payload.query === 'string' && payload.query.trim() ? payload.query : query,
        topK: typeof payload.topK === 'number' ? payload.topK : topK ?? normalizedChunks.length,
        latencyMs: typeof payload.latencyMs === 'number' ? payload.latencyMs : 0,
        chunks: normalizedChunks
      };
      setRagLookupResult(normalized);
      console.debug('[chat-rag-lookup] request_success', {
        requestId,
        topK: normalized.topK,
        chunkCount: normalized.chunks.length,
        latencyMs: normalized.latencyMs
      });
    } catch (error) {
      const message = await toProblemMessage(error, 'Failed to validate RAG lookup');
      setRagLookupError(message);
      console.error('[chat-rag-lookup] request_failed', {
        requestId,
        status: error instanceof ApiResponseError ? error.response.status : undefined,
        message,
        error
      });
    } finally {
      setRagLookupLoading(false);
    }
  }, [
    ragLookupChunkConfigDocumentFile,
    ragLookupChunkTroubleshootingDocumentFile,
    ragLookupCollections,
    ragLookupQuery,
    ragLookupTopKInput,
    ragLookupVendor,
    requestOrgApi
  ]);

  const copyRagLookupChunk = useCallback(async (chunk: ChatRagLookupChunkRecord, index: number) => {
    const key = chunk.id?.trim() || `chunk-${index + 1}`;
    try {
      await copyTextToClipboard(JSON.stringify(chunk, null, 2));
      setRagLookupCopiedChunkId(key);
      console.debug('[chat-rag-lookup] chunk_json_copied', { chunkId: key, index });
      window.setTimeout(() => {
        setRagLookupCopiedChunkId((prev) => (prev === key ? null : prev));
      }, 1800);
    } catch (error) {
      setRagLookupError('Failed to copy chunk JSON.');
      console.error('[chat-rag-lookup] chunk_json_copy_failed', { chunkId: key, index, error });
    }
  }, []);

  const createThreadMutation = useCallback(
    async (title: string) => {
      const response = await requestOrgApi('chat/threads', {
        method: 'POST',
        body: JSON.stringify({
          title: normalizeThreadTitle(title),
          visibility: 'private'
        })
      });
      return (await response.json()) as ChatThreadSummary;
    },
    [requestOrgApi]
  );

  const scrollTimelineToBottom = useCallback(
    (force = false) => {
      const container = timelineRef.current;
      if (!container) {
        return;
      }
      if (!force && !autoScrollRef.current) {
        return;
      }
      container.scrollTop = container.scrollHeight;
    },
    []
  );

  const handleTimelineScroll = useCallback(() => {
    const container = timelineRef.current;
    if (!container) {
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    autoScrollRef.current = distanceFromBottom < 80;
  }, []);

  useEffect(() => {
    autoScrollRef.current = true;
    scrollTimelineToBottom(true);
  }, [scrollTimelineToBottom, selectedThread?.id]);

  useEffect(() => {
    setActiveTopologyArtifact(null);
    setTopologySelectionsByArtifactId({});
    setTopologyOverlaySnapshotsByArtifactId({});
  }, [selectedThread?.id]);

  const closeEventStream = useCallback((runId?: string) => {
    if (runId) {
      activeStreamSessionsRef.current.delete(runId);
    } else {
      activeStreamSessionsRef.current.clear();
    }
    setActiveStreamRunIds(Array.from(activeStreamSessionsRef.current.keys()));
  }, []);

  useEffect(() => {
    closeEventStream();
  }, [closeEventStream, orgId]);

  useEffect(() => {
    if (isChatRoute) {
      return;
    }
    closeEventStream();
    if (changefeedRefreshTimeoutRef.current !== null) {
      window.clearTimeout(changefeedRefreshTimeoutRef.current);
      changefeedRefreshTimeoutRef.current = null;
    }
  }, [closeEventStream, isChatRoute]);

  const startStreamingRun = useCallback(
    (runId: string, threadId: string | null) => {
      console.debug('[chat] startStreamingRun', { runId, threadId });
      if (threadId) {
        runThreadMapRef.current.set(runId, threadId);
      }
      const sessions = activeStreamSessionsRef.current;
      const existing = sessions.get(runId);
      if (existing) {
        if (threadId && existing.threadId !== threadId) {
          existing.threadId = threadId;
          setActiveStreamRunIds(Array.from(sessions.keys()));
        }
        return;
      }
      while (sessions.size >= MAX_ACTIVE_THREAD_SESSION_CACHE) {
        const selectedThreadId = selectedThreadRef.current?.id ?? null;
        const oldestRunId = Array.from(sessions.entries()).find(([, session]) => session.threadId !== selectedThreadId)?.[0] ??
          sessions.keys().next().value;
        if (!oldestRunId) {
          break;
        }
        sessions.delete(oldestRunId);
      }
      sessions.set(runId, { threadId, lastEventId: null, lastEventSequence: 0 });
      setActiveStreamRunIds(Array.from(sessions.keys()));
    },
    []
  );

  const stopStreamingRun = useCallback(
    (runId?: string | null) => {
      console.debug('[chat] stopStreamingRun', { runId });
      if (runId) {
        activeStreamSessionsRef.current.delete(runId);
      } else {
        activeStreamSessionsRef.current.clear();
      }
      setActiveStreamRunIds(Array.from(activeStreamSessionsRef.current.keys()));
    },
    []
  );

  const interactWithThread = useCallback(
    async (
      threadId: string,
      content: ChatInteractRequestContentBlock[],
      options: { includeContext?: boolean; attachments?: ChatInteractAttachmentPayload[] } = {}
    ) => {
      const context = options.includeContext === false
        ? (browserTimeZone() ? { timeZone: browserTimeZone() } : undefined)
        : buildCurrentChatInteractContext() ?? (browserTimeZone() ? { timeZone: browserTimeZone() } : undefined);
      const response = await requestOrgApi(`chat/threads/${threadId}/interact`, {
        method: 'POST',
        body: JSON.stringify({
          ...(content.length > 0 ? { content } : {}),
          ...(options.attachments && options.attachments.length > 0 ? { attachments: options.attachments } : {}),
          ...(context ? { context: ChatInteractContextschemaToJSON(context) } : {}),
          trace: { level: traceLevel }
        })
      });
      const payload = (await response.json()) as ChatMessageRecord;
      setMessageEvents((prev) => mergeMessageEventLists(prev, [mapMessageToEvent(payload, 'user')]));
      const runId = extractRunIdFromLocation(response.headers.get('Location'));
      console.debug('[chat] interact response', { runId, threadId });
      if (runId) {
        console.debug('[chat] interact response', { runId, threadId });
        if (threadId) {
          runThreadMapRef.current.set(runId, threadId);
        }
        setCurrentRunId(runId);
        completedRunsRef.current.delete(runId);
        setThreadAttentionById((prev) => {
          if (!prev[threadId]) {
            return prev;
          }
          const next = { ...prev };
          delete next[threadId];
          return next;
        });
        setRunEventHistory((prev) => {
          const next = {
            ...prev,
            [runId]: []
          };
          runEventHistoryRef.current = next;
          return next;
        });
        setArtifactsByRun((prev) => ({
          ...prev,
          [runId]: []
        }));
        startStreamingRun(runId, threadId);
        skipThreadReloadRef.current = true;
        setSelectedThread((prev) =>
          prev && prev.id === threadId
            ? {
                ...prev,
                latestRunId: runId,
                latestRunStatus: 'running',
                lastMessageAt: payload.createdAt ?? new Date().toISOString(),
                updatedAt: payload.createdAt ?? new Date().toISOString()
              }
            : prev
        );
        autoScrollRef.current = true;
        scrollTimelineToBottom(true);
      }
      return runId;
    },
    [buildCurrentChatInteractContext, requestOrgApi, scrollTimelineToBottom, startStreamingRun, traceLevel]
  );

  const loadThreadMessages = useCallback(
    async (threadId: string, options?: { cursor?: string | null; append?: boolean }) => {
      if (!orgId) {
        return;
      }
      if (!options?.append) {
        setChatLoadErrorMessage(null);
      }
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('order', 'desc');
      if (options?.cursor) {
        params.set('after', options.cursor);
      }
      try {
        const response = await fetchOrgResource<ChatMessagesListResponse>(
          basePath,
          orgId,
          `chat/threads/${threadId}/messages?${params.toString()}`
        );
        const records = response.items ?? [];
        const events = sortEventsByTimestamp(records.map((message) => mapMessageToEvent(message, message.role === 'assistant' ? 'assistant' : 'user')));
        const runIds = Array.from(
          new Set(records.map((message) => message.runId).filter((id): id is string => typeof id === 'string' && id.length > 0))
        );
        runIds.forEach((runId) => runThreadMapRef.current.set(runId, threadId));
        if (selectedThreadRef.current?.id !== threadId) {
          updateCachedThreadSnapshot(threadId, (snapshot) => ({
            ...snapshot,
            messageEvents: options?.append ? mergeMessageEventLists(snapshot.messageEvents, events) : events,
            messagesCursor: response.meta?.next ?? null,
            currentRunId: snapshot.currentRunId ?? runIds[0] ?? null
          }));
          return;
        }
        if (options?.append) {
          setMessageEvents((prev) => {
            const next = mergeMessageEventLists(prev, events);
            messageEventsRef.current = next;
            return next;
          });
        } else {
          messageEventsRef.current = events;
          setMessageEvents(events);
        }
        clearTransientChatRefreshNotice();
        setChatLoadErrorMessage(null);
        setMessagesCursor(response.meta?.next ?? null);
      } catch (error) {
        console.error('Failed to load thread messages', error);
        const hasExistingContent =
          selectedThreadRef.current?.id === threadId &&
          messageEventsRef.current.length > 0 &&
          isTransientChatLoadError(error);
        if (!options?.append && hasExistingContent) {
          setChatLoadErrorMessage(null);
          showTransientChatRefreshNotice('Chat refresh failed temporarily. Retrying...');
        } else if (!options?.append) {
          setMessageEvents([]);
          setMessagesCursor(null);
          setChatLoadErrorMessage(getChatThreadLoadErrorMessage(error));
        }
      }
    },
    [basePath, clearTransientChatRefreshNotice, orgId, showTransientChatRefreshNotice, updateCachedThreadSnapshot]
  );

  const refreshThreadsAndHistory = useCallback(() => {
    reloadThreads();
    if (selectedThread?.id) {
      void loadThreadMessages(selectedThread.id);
    }
  }, [loadThreadMessages, reloadThreads, selectedThread?.id]);

  const cancelScheduledChangefeedRefresh = useCallback(() => {
    if (changefeedRefreshTimeoutRef.current !== null) {
      window.clearTimeout(changefeedRefreshTimeoutRef.current);
      changefeedRefreshTimeoutRef.current = null;
    }
  }, []);

  const syncRunStateFromBackend = useCallback(
    (runId: string, threadId?: string | null) => {
      const resolvedThreadId = threadId ?? runThreadMapRef.current.get(runId) ?? null;
      reloadThreads();
      if (resolvedThreadId) {
        void loadThreadMessages(resolvedThreadId);
      }
    },
    [loadThreadMessages, reloadThreads]
  );

  const reconcileRunStateFromBackend = useCallback(
    async (runId: string, threadId?: string | null): Promise<'active' | 'paused' | 'finished' | 'unknown'> => {
      const resolvedThreadId = threadId ?? runThreadMapRef.current.get(runId) ?? null;
      reloadThreads();
      if (!resolvedThreadId) {
        return 'unknown';
      }
      try {
        const response = await requestOrgApi(`chat/threads/${encodeURIComponent(resolvedThreadId)}`);
        const thread = (await response.json()) as ChatThreadSummary;
        setThreads((prev) => mergeThreadsById(prev, [thread]));
        if (selectedThreadRef.current?.id === thread.id) {
          setSelectedThread((current) => (current?.id === thread.id ? thread : current));
        }
        await loadThreadMessages(resolvedThreadId);
        if (thread.latestRunId && thread.latestRunId !== runId) {
          completedRunsRef.current.add(runId);
          stopStreamingRun(runId);
          return 'finished';
        }
        const status = thread.latestRunStatus;
        if (isActiveRunStatus(status)) {
          completedRunsRef.current.delete(runId);
          return 'active';
        }
        if (isPausedRunStatus(status)) {
          setThreadAttentionForRun(runId, 'action_required');
          return 'paused';
        }
        if (terminalRunStatuses.has(status?.trim().toLowerCase() ?? '')) {
          completedRunsRef.current.add(runId);
          stopStreamingRun(runId);
          return 'finished';
        }
        return 'unknown';
      } catch (error) {
        console.warn('[chat] failed to reconcile run state after stream failure', { runId, threadId: resolvedThreadId, error });
        try {
          await loadThreadMessages(resolvedThreadId);
        } catch (messageError) {
          console.warn('[chat] failed to refresh run messages after reconcile failure', { runId, threadId: resolvedThreadId, error: messageError });
        }
        return 'unknown';
      }
    },
    [loadThreadMessages, reloadThreads, requestOrgApi, setThreadAttentionForRun, stopStreamingRun]
  );

  const refreshFromChangefeed = useCallback(() => {
    reloadThreads();
    const selectedThreadId = selectedThread?.id;
    const selectedThreadIsStreaming =
      Boolean(selectedThreadId) &&
      Array.from(activeStreamSessionsRef.current.values()).some((session) => session.threadId === selectedThreadId);
    if (!selectedThreadIsStreaming && selectedThreadId) {
      void loadThreadMessages(selectedThreadId);
    }
  }, [loadThreadMessages, reloadThreads, selectedThread?.id]);

  const scheduleChangefeedRefresh = useCallback(() => {
    if (!orgId || !isChatRouteRef.current) {
      return;
    }
    cancelScheduledChangefeedRefresh();
    changefeedRefreshTimeoutRef.current = window.setTimeout(() => {
      changefeedRefreshTimeoutRef.current = null;
      refreshFromChangefeed();
    }, CHAT_CHANGEFEED_REFRESH_DEBOUNCE_MS);
  }, [cancelScheduledChangefeedRefresh, orgId, refreshFromChangefeed]);

  useOrgChangefeedEffect(
    ['chat_threads', 'chat_messages', 'chat_runs'],
    () => {
      scheduleChangefeedRefresh();
    },
    [scheduleChangefeedRefresh]
  );

  const handleLoadOlderMessages = useCallback(async () => {
    if (!selectedThread?.id || !messagesCursor) {
      return;
    }
    setLoadingOlderMessages(true);
    try {
      await loadThreadMessages(selectedThread.id, { cursor: messagesCursor, append: true });
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [loadThreadMessages, messagesCursor, selectedThread?.id]);

  const fetchRunArtifacts = useCallback(
    async (runId: string) => {
      if (!orgId) {
        return false;
      }
      setArtifactsLoadingCount((count) => count + 1);
      try {
        const response = await fetchOrgCollection<ChatArtifact>(basePath, orgId, `chat/runs/${runId}/artifacts?limit=100`);
        const artifacts = (response.items ?? []).map((item) => ({
          id: item.id ?? '',
          kind: item.kind ?? 'artifact',
          summary: item.summary,
          size: typeof item.size === 'number' ? item.size : undefined,
          mimeType: item.mimeType,
          createdAt: item.createdAt,
          runId: runId,
          labels: item.labels ?? undefined,
          evidence: item.evidence ?? undefined
        }));
        updateArtifactsForRun(runId, () => artifacts.filter((artifact) => artifact.id));
        return true;
      } catch (error) {
        console.error('Failed to load artifacts', error);
        return false;
      } finally {
        setArtifactsLoadingCount((count) => Math.max(0, count - 1));
      }
    },
    [basePath, orgId, updateArtifactsForRun]
  );

  const ensureRunArtifacts = useCallback(
    async (runId?: string | null) => {
      if (!runId || hasFetchedRunArtifacts(artifactsByRun, runId) || requestedRunArtifactsRef.current.has(runId)) {
        return hasFetchedRunArtifacts(artifactsByRun, runId);
      }
      requestedRunArtifactsRef.current.add(runId);
      const loaded = await fetchRunArtifacts(runId);
      if (!loaded) {
        requestedRunArtifactsRef.current.delete(runId);
      }
      return loaded;
    },
    [artifactsByRun, fetchRunArtifacts]
  );

  const handleSseEvent = useCallback(
    (runId: string, eventType: string, payload: string) => {
      let data: Record<string, any>;
      try {
        data = JSON.parse(payload);
      } catch {
        return;
      }
      const context = parseAgentContext(data);
      const timestamp =
        typeof data.timestamp === 'string'
          ? data.timestamp
          : typeof data.finishedAt === 'string'
            ? data.finishedAt
            : new Date().toISOString();
      switch (eventType) {
        case 'status': {
          const parsed = ChatRunEventStatusschemaFromJSON(data) as ChatRunEventStatusschema;
          const detail =
            typeof (data as Record<string, unknown>).detail === 'string'
              ? ((data as Record<string, unknown>).detail as string)
              : undefined;
          const eventId = coerceEventId(data.eventId) ?? `status-${runId}-${Date.now()}`;
          const normalizedStatus = typeof parsed.status === 'string' ? parsed.status.toLowerCase() : 'running';
          const event: ChatStatusEvent = {
            id: eventId,
            type: 'status',
            status: normalizedStatus as ChatStatusEvent['status'],
            detail,
            startedAt: normalizeDateString(parsed.startedAt),
            finishedAt: normalizeDateString(parsed.finishedAt),
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          if (normalizedStatus === 'running') {
            const threadId = runThreadMapRef.current.get(runId);
            if (threadId && pendingThreadTitleRefreshRef.current.has(threadId)) {
              pendingThreadTitleRefreshRef.current.delete(threadId);
              reloadThreads();
            }
          }
          if (isPausedRunStatus(normalizedStatus)) {
            setThreadAttentionForRun(runId, 'action_required');
            syncRunStateFromBackend(runId);
          } else if (terminalRunStatuses.has(normalizedStatus)) {
            const threadId = runThreadMapRef.current.get(runId) ?? null;
            if (normalizedStatus === 'succeeded') {
              if (threadId) {
                setThreadAttentionById((prev) => {
                  if (prev[threadId] === 'action_required') {
                    return prev;
                  }
                  const next = { ...prev };
                  if (selectedThreadRef.current?.id === threadId) {
                    delete next[threadId];
                  } else {
                    next[threadId] = 'ready';
                  }
                  return next;
                });
              }
            } else {
              setThreadAttentionForRun(runId, 'failed');
            }
            stopStreamingRun(runId);
            completedRunsRef.current.add(runId);
            fetchRunArtifacts(runId);
            syncRunStateFromBackend(runId);
          }
          break;
        }
        case 'progress': {
          const parsed = ChatRunEventProgressschemaFromJSON(data) as ChatRunEventProgressschema;
          const eventId = coerceEventId(data.eventId) ?? `progress-${runId}-${Date.now()}`;
          const event: ChatProgressEvent = {
            id: eventId,
            type: 'progress',
            stage: parsed.stage ?? 'In progress',
            detail: parsed.detail,
            percent: typeof parsed.percent === 'number' ? parsed.percent : undefined,
            event: typeof parsed.event === 'string' ? parsed.event : undefined,
            meta: typeof parsed.meta === 'object' && parsed.meta ? (parsed.meta as Record<string, unknown>) : undefined,
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          if (extractApprovalMeta(event)) {
            setThreadAttentionForRun(runId, 'action_required');
          }
          break;
        }
        case 'artifact': {
          const parsed = ChatRunEventArtifactFromJSON(data) as ChatRunEventArtifact;
          const eventId = coerceEventId(data.eventId) ?? data.artifactId ?? `artifact-${Date.now()}`;
          const artifactEvent: ChatArtifactEvent = {
            id: eventId,
            type: 'artifact',
            artifactId: parsed.artifactId,
            kind: parsed.kind ?? 'artifact',
            summary: parsed.summary ?? 'Artifact ready',
            mimeType: parsed.mimeType,
            size: typeof parsed.size === 'number' ? parsed.size : undefined,
            timestamp,
            runId,
            ...context
          };
          appendRunEvent(runId, artifactEvent);
          updateArtifactsForRun(runId, (existing) => {
            const artifactId = artifactEvent.artifactId ?? artifactEvent.id;
            return existing.some((item) => item.id === artifactId)
              ? existing.map((item) =>
                  item.id === artifactId
                    ? { ...item, summary: artifactEvent.summary, size: artifactEvent.size, mimeType: artifactEvent.mimeType }
                    : item
                )
              : [
                  ...existing,
                  {
                    id: artifactId,
                    kind: artifactEvent.kind,
                    summary: artifactEvent.summary,
                    size: artifactEvent.size,
                    mimeType: artifactEvent.mimeType,
                    runId,
                    createdAt: artifactEvent.timestamp
                  }
                ];
          });
          break;
        }
        case 'artifact_update': {
          const parsed = ChatRunEventArtifactUpdateschemaFromJSON(data) as ChatRunEventArtifactUpdateschema;
          if (!parsed.artifactId || !parsed.patch) {
            break;
          }
          const artifactUpdateEvent: ChatArtifactUpdateEvent = {
            id: coerceEventId(data.eventId) ?? `artifact-update-${parsed.artifactId}-${parsed.revision}`,
            type: 'artifact_update',
            eventId: parsed.eventId ?? coerceEventId(data.eventId) ?? undefined,
            artifactId: parsed.artifactId,
            artifactKind: parsed.artifactKind,
            updateKind: parsed.updateKind,
            summary: typeof parsed.summary === 'string' ? parsed.summary : undefined,
            revision: parsed.revision,
            patch: parsed.patch,
            timestamp,
            runId,
            ...context
          };
          appendRunEvent(runId, artifactUpdateEvent);
          updateArtifactsForRun(runId, (existing) => {
            if (existing.some((item) => item.id === artifactUpdateEvent.artifactId)) {
              return existing;
            }
            return [
              ...existing,
              {
                id: artifactUpdateEvent.artifactId,
                kind:
                  artifactUpdateEvent.artifactKind === ChatRunEventArtifactUpdateschemaArtifactKindEnum.Topology
                    ? 'topology'
                    : 'artifact',
                summary: artifactUpdateEvent.summary ?? 'Artifact updated',
                mimeType: 'application/vnd.reap.artifact+json',
                runId,
                createdAt: artifactUpdateEvent.timestamp
              }
            ];
          });
          setArtifactPreview((prev) => {
            const artifactKey = artifactIdentityKey(artifactUpdateEvent.artifactId, artifactUpdateEvent.runId ?? runId);
            if (!prev[artifactKey]) {
              return prev;
            }
            const next = { ...prev };
            delete next[artifactKey];
            return next;
          });
          setArtifactPreviewReloadRequests((prev) => ({
            ...prev,
            [artifactIdentityKey(artifactUpdateEvent.artifactId, artifactUpdateEvent.runId ?? runId)]: Date.now()
          }));
          break;
        }
        case 'error': {
          if (typeof data.error === 'string' && typeof data.message !== 'string' && typeof data.code !== 'string') {
            console.warn('[chat] SSE transport error; keeping run active for reconnect', { runId, error: data.error });
            syncRunStateFromBackend(runId);
            break;
          }
          const parsed = ChatRunEventErrorFromJSON(data) as ChatRunEventError;
          const eventId = coerceEventId(data.eventId) ?? `error-${runId}-${Date.now()}`;
          const event: ChatErrorEvent = {
            id: eventId,
            type: 'error',
            title: data.title ?? 'Error',
            message: parsed.message ?? 'Run reported an error.',
            code: parsed.code,
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          setThreadAttentionForRun(runId, 'failed');
          stopStreamingRun(runId);
          completedRunsRef.current.add(runId);
          syncRunStateFromBackend(runId);
          break;
        }
        case 'plan': {
          const parsed = ChatRunEventPlanFromJSON(data) as ChatRunEventPlan;
          const planId = typeof parsed.planId === 'string' ? parsed.planId : '';
          if (!planId) {
            break;
          }
          const steps = Array.isArray(parsed.steps) ? parsed.steps : [];
          const event: ChatPlanEvent = {
            id: coerceEventId(data.eventId) ?? `plan-${runId}-${Date.now()}`,
            type: 'plan',
            planId,
            round: typeof parsed.round === 'number' ? parsed.round : undefined,
            title: typeof parsed.title === 'string' ? parsed.title : undefined,
            steps: steps as ChatPlanStep[],
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          break;
        }
        case 'plan_step': {
          const parsed = ChatRunEventPlanStepFromJSON(data) as ChatRunEventPlanStep;
          const planId = typeof parsed.planId === 'string' ? parsed.planId : '';
          const stepId = typeof parsed.stepId === 'string' ? parsed.stepId : '';
          const status = typeof parsed.status === 'string' ? parsed.status : '';
          if (!planId || !stepId || !status) {
            break;
          }
          const event: ChatPlanStepEvent = {
            id: coerceEventId(data.eventId) ?? `plan-step-${runId}-${Date.now()}`,
            type: 'plan_step',
            planId,
            stepId,
            status: status as ChatRunPlanStepschemaStatusEnum,
            detail: typeof parsed.detail === 'string' ? parsed.detail : undefined,
            percent: typeof parsed.percent === 'number' ? parsed.percent : undefined,
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          break;
        }
        case 'trace': {
          const parsed = ChatRunEventTraceFromJSON(data) as ChatRunEventTrace;
          const text = typeof parsed.text === 'string' ? parsed.text : '';
          if (!text) {
            break;
          }
          const event: ChatTraceEvent = {
            id: coerceEventId(data.eventId) ?? `trace-${runId}-${Date.now()}`,
            type: 'trace',
            text,
            kind: typeof parsed.kind === 'string' ? parsed.kind : undefined,
            timestamp,
            ...context
          };
          appendRunEvent(runId, event);
          break;
        }
        case 'message': {
          const messageId = typeof data.messageId === 'string' ? data.messageId : null;
          if (!messageId) {
            break;
          }
          (async () => {
            try {
              const response = await requestOrgApi(`chat/messages/${messageId}`);
              const payload = (await response.json()) as ChatMessageRecord;
              const event = mapMessageToEvent(payload, payload.role === 'assistant' ? 'assistant' : 'user');
              mergeMessageEventForRun(runId, event);
              const requiresInput = event.contentBlocks?.some((block) =>
                ['choicePrompt', 'confirmationPrompt', 'inputPrompt', 'formPrompt'].includes(block.type)
              );
              if (requiresInput) {
                setThreadAttentionForRun(runId, 'action_required');
              }
            } catch (messageError) {
              console.error('Failed to load streamed message', messageError);
            }
          })();
          break;
        }
        default:
          break;
      }
    },
    [appendRunEvent, fetchRunArtifacts, mergeMessageEventForRun, reloadThreads, requestOrgApi, setThreadAttentionForRun, stopStreamingRun, syncRunStateFromBackend, updateArtifactsForRun]
  );

  const resolveArtifactPath = useCallback(
    (artifact: ChatArtifactLike): string | null => {
      if (!orgId || !artifact.id) {
        return null;
      }
      if (String(artifact.kind ?? '').toLowerCase() === 'file') {
        return `files/${artifact.id}`;
      }
      const labeledUrl = artifact.labels?.artifactUrl;
      if (labeledUrl) {
        if (labeledUrl.startsWith('/orgs/')) {
          const parts = labeledUrl.split('/').slice(3).join('/');
          return parts || null;
        }
        if (labeledUrl.startsWith('http')) {
          return labeledUrl;
        }
        return labeledUrl.replace(/^\//, '');
      }
      if (artifact.runId) {
        return `chat/runs/${encodeURIComponent(artifact.runId)}/artifacts/${encodeURIComponent(artifact.id)}`;
      }
      return `chat/artifacts/${artifact.id}`;
    },
    [orgId]
  );

  const downloadFileBytes = useCallback(
    async (fileId: string, accept: string) => {
      const response = await requestOrgApi(buildFileDownloadPath(fileId), {
        headers: { Accept: accept }
      });
      return response.blob();
    },
    [requestOrgApi]
  );

  const loadFileArtifactState = useCallback(
    async (artifactId: string, existingPreview: ArtifactPreviewRecord | undefined, full = false) =>
      loadFileArtifactPreview({
        fileId: artifactId,
        full,
        existing: {
          fileRecord: existingPreview?.fileRecord,
          fileText: full ? existingPreview?.fileText : undefined
        },
        loadMetadata: async (fileId) => {
          const metadataResponse = await requestOrgApi(buildFileMetadataPath(fileId), {
            headers: { Accept: 'application/json' }
          });
          return parseUploadedFileRecord(await metadataResponse.json());
        },
        loadTextSlice: async (fileId, startLine, maxLines) => {
          const response = await requestOrgApi(buildFileTextContentPath(fileId, startLine, maxLines), {
            headers: { Accept: 'application/json' }
          });
          return parseFileTextSlice(await response.json());
        },
        loadDownloadBlob: downloadFileBytes
      }),
    [downloadFileBytes, requestOrgApi]
  );

  const fetchArtifactDocumentById = useCallback(
    async (artifactId: string, full: boolean) => {
      const resource = `chat/artifacts/${artifactId}${full ? '?content=full' : ''}`;
      const response = await requestOrgApi(resource, {
        headers: { Accept: 'application/vnd.reap.artifact+json' }
      });
      const raw = await response.text();
      const parsed = parseArtifactDocument(raw);
      if (!parsed) {
        throw new Error('Failed to parse artifact document');
      }
      return parsed as any;
    },
    [requestOrgApi]
  );

  const handleLoadArtifactPreview = useCallback(
    async (artifact: ChatArtifact) => {
      if (!artifact.id) {
        return;
      }
      const artifactKey = artifactStateKey(artifact);
      const existing = artifactPreview[artifactKey];
      if (existing?.loading || (existing && !existing.error)) {
        return;
      }
      const isFileArtifact = isFileArtifactKind(artifact.kind);
      const path = isFileArtifact ? null : resolveArtifactPathWithContent(resolveArtifactPath, artifact, false);
      if (!path) {
        if (!isFileArtifact) {
          return;
        }
      }
      setArtifactPreview((prev) => ({
        ...prev,
        [artifactKey]: { ...(prev[artifactKey] ?? { content: '' }), loading: true, error: undefined }
      }));
      try {
        if (isFileArtifact) {
          const loadedPreview = await loadFileArtifactState(artifact.id, artifactPreview[artifactKey], false);
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: {
              content: loadedPreview.content,
              loading: false,
              objectUrl: loadedPreview.objectUrl,
              fileText: loadedPreview.fileText,
              fileRecord: loadedPreview.fileRecord,
              isFull: loadedPreview.isFull
            }
          }));
          return;
        }
        const artifactPath = path;
        if (!artifactPath) {
          return;
        }
        const mimeHint = (artifact.mimeType ?? '').toLowerCase();
        if (mimeHint.startsWith('image/') || mimeHint === 'application/pdf') {
          const response = await requestOrgApi(artifactPath, {
            headers: { Accept: mimeHint || '*/*' }
          });
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: { content: objectUrl, loading: false, isFull: true }
          }));
          return;
        }

        const response = await requestOrgApi(artifactPath, {
          headers: { Accept: 'application/vnd.reap.artifact+json' }
        });
        const contentType = (response.headers.get('content-type') ?? '').toLowerCase();
        const content = await response.text();

        let doc: ArtifactDocumentschema | null = null;
        if (contentType.includes('json') || contentType.includes('vnd.reap.artifact+json')) {
          doc = parseArtifactDocument(content);
        }
        const loose = doc ? null : parseArtifactDocumentLoose(content);

        if (doc) {
          const docContent: any = doc.content;
          if ((docContent as any)?.type === 'commandOutput') {
            const previewText = typeof (docContent as any).previewText === 'string' ? (docContent as any).previewText : '';
            setArtifactPreview((prev) => ({
              ...prev,
              [artifactKey]: { content: previewText, loading: false, doc, isFull: false }
            }));
            return;
          }
          if ((docContent as any)?.type === 'table') {
            const rows = Array.isArray(docContent.rows) ? (docContent.rows as unknown[]) : null;
            const rowCount =
              typeof docContent.rowCount === 'number'
                ? docContent.rowCount
                : rows
                  ? rows.length
                  : undefined;
            setArtifactPreview((prev) => ({
              ...prev,
              [artifactKey]: { content: rowCount != null ? `Table (${rowCount} rows)` : 'Table', loading: false, doc, isFull: false }
            }));
            return;
          }
          if (isChartContent(docContent)) {
            setArtifactPreview((prev) => ({
              ...prev,
              [artifactKey]: { content: 'Chart', loading: false, doc, isFull: false }
            }));
            return;
          }
          if (isTopologyArtifactContent(docContent)) {
            setArtifactPreview((prev) => ({
              ...prev,
              [artifactKey]: { content: 'Topology', loading: false, doc, isFull: false }
            }));
            return;
          }
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: { content: JSON.stringify(doc.content, null, 2), loading: false, doc, isFull: false }
          }));
          return;
        }

        if (loose?.content?.type === 'commandOutput') {
          const previewText = typeof loose.content.previewText === 'string' ? loose.content.previewText : '';
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: { content: previewText, loading: false, isFull: false }
          }));
          return;
        }
        if (isTopologyArtifactContent(loose?.content)) {
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: { content: 'Topology', loading: false, doc: loose as ArtifactDocumentschema, isFull: false }
          }));
          return;
        }

        const parsed = contentType.includes('json') ? parseArtifactPayload(content) : { output: content };
        setArtifactPreview((prev) => ({
          ...prev,
          [artifactKey]: {
            content: parsed.output ?? content,
            loading: false,
            isFull: false
          }
        }));
      } catch (error) {
        console.error('Failed to load artifact preview', error);
        setArtifactPreview((prev) => ({
          ...prev,
          [artifactKey]: {
            ...(prev[artifactKey] ?? { content: '' }),
            loading: false,
            error: isFileArtifact ? formatFilePreviewErrorMessage(error) : 'Failed to load artifact'
          }
        }));
      }
    },
    [artifactPreview, loadFileArtifactState, requestOrgApi, resolveArtifactPath, resolveArtifactPathWithContent]
  );

  const handleOpenArtifact = useCallback((artifact: ChatArtifact) => {
    if (!artifact.id) {
      return;
    }
    const artifactKey = artifactStateKey(artifact);
    setArtifactExpanded((prev) => {
      const next = !(prev[artifactKey] ?? false);
      if (next && !artifactPreview[artifactKey]) {
        void handleLoadArtifactPreview(artifact);
      }
      return {
        ...prev,
        [artifactKey]: next
      };
    });
  }, [artifactPreview, handleLoadArtifactPreview]);

  const handleToggleMessageOutputs = useCallback((messageId: string) => {
    setMessageOutputsExpanded((prev) => ({
      ...prev,
      [messageId]: !(prev[messageId] ?? false)
    }));
  }, []);

  const handleLoadFullArtifact = useCallback(
    async (artifact: ChatArtifact) => {
      if (!artifact.id) {
        return;
      }
      const artifactKey = artifactStateKey(artifact);
      const isFileArtifact = isFileArtifactKind(artifact.kind);
      const existingPreview = artifactPreview[artifactKey];
      const existingFileText = existingPreview?.fileText;
      const path = isFileArtifact ? null : resolveArtifactPathWithContent(resolveArtifactPath, artifact, true);
      if (!path && !isFileArtifact) {
        return;
      }
      setArtifactPreview((prev) => ({
        ...prev,
        [artifactKey]: { ...(prev[artifactKey] ?? { content: '' }), loading: true }
      }));
      try {
        if (isFileArtifact) {
          if (existingFileText && !hasMoreFileText(existingFileText)) {
            setArtifactPreview((prev) => ({
              ...prev,
              [artifactKey]: { ...(prev[artifactKey] ?? { content: '' }), loading: false, isFull: true }
            }));
            return;
          }
          const loadedPreview = await loadFileArtifactState(artifact.id, existingPreview, true);
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: {
              ...(prev[artifactKey] ?? { content: '' }),
              content: loadedPreview.content,
              loading: false,
              objectUrl: loadedPreview.objectUrl,
              fileText: loadedPreview.fileText,
              fileRecord: loadedPreview.fileRecord,
              isFull: loadedPreview.isFull
            }
          }));
          return;
        }
        const artifactPath = path;
        if (!artifactPath) {
          return;
        }
        const response = await requestOrgApi(artifactPath, {
          headers: { Accept: 'application/vnd.reap.artifact+json' }
        });
        const contentType = (response.headers.get('content-type') ?? '').toLowerCase();
        const raw = await response.text();
        const doc =
          contentType.includes('json') || contentType.includes('vnd.reap.artifact+json') ? parseArtifactDocument(raw) : null;
        const docAny: any = doc;
        const rawView =
          Array.isArray(docAny?.views) && docAny.views.length > 0
            ? docAny.views.find((v: any) => v && v.rep === 'raw_lines_exact') ?? docAny.views[0]
            : undefined;
        const docContent: any = rawView?.content ?? docAny?.content;
        if ((docContent as any)?.type === 'commandOutput') {
          const fullText = typeof (docContent as any).fullText === 'string' ? (docContent as any).fullText : '';
          const previewText = typeof (docContent as any).previewText === 'string' ? (docContent as any).previewText : '';
          const output = fullText || previewText || '';
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: { content: output, loading: false, doc: doc ?? undefined, isFull: Boolean(fullText) }
          }));
          return;
        }
        setArtifactPreview((prev) => ({
          ...prev,
          [artifactKey]: { ...(prev[artifactKey] ?? { content: '' }), loading: false, doc: doc ?? prev[artifactKey]?.doc, isFull: true }
        }));
      } catch (error) {
        console.error('Failed to load full artifact', error);
        setArtifactPreview((prev) => ({
          ...prev,
          [artifactKey]: {
            ...(prev[artifactKey] ?? { content: '' }),
            loading: false,
            error: isFileArtifact ? formatFilePreviewErrorMessage(error) : 'Failed to load full artifact'
          }
        }));
      }
    },
    [artifactPreview, loadFileArtifactState, requestOrgApi, resolveArtifactPath, resolveArtifactPathWithContent]
  );

  const handleDownloadArtifact = useCallback(
    async (artifact: ChatArtifact, explicitFormat?: OutputExportFormat) => {
      try {
        const artifactKey = artifactStateKey(artifact);
        if (isFileArtifactKind(artifact.kind)) {
          if (!artifact.id) {
            return;
          }
          const blob = await downloadFileBytes(artifact.id, artifact.mimeType ?? 'application/octet-stream');
          downloadBlobAsFile(
            blob,
            deriveFileDownloadName(artifact.id, artifactPreview[artifactKey]?.fileRecord, artifact.summary)
          );
          return;
        }
        if (!artifact.id) {
          return;
        }
        const format = explicitFormat ?? artifactExportFormats[artifactKey] ?? resolveDefaultArtifactExportFormat(artifact);
        const fallbackStem =
          (typeof artifact.summary === 'string' && artifact.summary.trim().length > 0 ? artifact.summary : `${artifact.kind ?? 'artifact'}-${artifact.id}`);
        const downloaded = await createAndDownloadOutputExport(
          requestOrgApi,
          {
            resourceRefs: [`artifact:${artifact.id}`],
            purpose: 'ui_download',
            bundleMode: 'single',
            ...(format ? { format } : {}),
          },
          fallbackStem,
        );
        downloadBlobAsFile(downloaded.blob, downloaded.fileName);
      } catch (error) {
        console.error('Failed to download artifact', error);
        if (artifact.id) {
          const artifactKey = artifactStateKey(artifact);
          setArtifactPreview((prev) => ({
            ...prev,
            [artifactKey]: {
              ...(prev[artifactKey] ?? { content: '' }),
              loading: false,
              error: isFileArtifactKind(artifact.kind)
                ? formatFilePreviewErrorMessage(error)
                : error instanceof Error && error.message
                  ? error.message
                  : 'Failed to download artifact',
            }
          }));
        }
      }
    },
    [artifactExportFormats, artifactPreview, downloadFileBytes, requestOrgApi]
  );

  const renderArtifactDownloadAction = useCallback(
    (artifact: ChatArtifact, buttonClassName: string, selectClassName?: string) => {
      if (isFileArtifactKind(artifact.kind)) {
        return null;
      }
      const artifactKey = artifactStateKey(artifact);
      const formats = resolveArtifactExportFormats(artifact);
      const selectedFormat = artifactExportFormats[artifactKey] ?? resolveDefaultArtifactExportFormat(artifact);
      return (
        <>
          {formats.length > 1 && (
            <label className="chat-export-format">
              <span className="chat-export-format__label">Format</span>
              <select
                className={selectClassName ?? 'chat-export-format__select'}
                value={selectedFormat ?? formats[0]}
                onChange={(event) =>
                  setArtifactExportFormats((previous) => ({
                    ...previous,
                    [artifactKey]: event.target.value as OutputExportFormat,
                  }))
                }
              >
                {formats.map((format) => (
                  <option key={format} value={format}>
                    {OUTPUT_EXPORT_FORMAT_LABELS[format]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className={buttonClassName}
            onClick={() => void handleDownloadArtifact(artifact, selectedFormat)}
          >
            Download
          </button>
        </>
      );
    },
    [artifactExportFormats, handleDownloadArtifact]
  );

  const openInspectorForArtifact = useCallback(
    async (artifact: ChatArtifact, evidence?: EvidencePointer, source: 'timeline' | 'artifacts' = 'timeline') => {
      if (!artifact.id) {
        return;
      }
      const artifactKey = artifactStateKey(artifact);
      if (artifact.runId) {
        setCurrentRunId(artifact.runId);
      }
      setInspectorOpen(true);
      setInspectorTab('preview');
      setInspectorPreviewSource(source);
      setInspectorArtifactId(artifactKey);
      setInspectorArtifact(artifact);
      setInspectorMaximized(false);

      const runId = artifact.runId ?? currentRunId;
      if (runId && !hasFetchedRunArtifacts(artifactsByRun, runId)) {
        await ensureRunArtifacts(runId);
      }

      const existingPreview = artifactPreview[artifactKey];
      if (!existingPreview || existingPreview.error) {
        await handleLoadArtifactPreview(artifact);
      }

      if (typeof evidence?.rep === 'string' && evidence.rep.trim()) {
        const rep = evidence.rep.trim();
        setArtifactViewReps((prev) => ({ ...prev, [artifactKey]: rep }));
      }

      if (evidence?.kind === 'raw' && typeof evidence.startLine === 'number' && typeof evidence.endLine === 'number') {
        const startLine = Math.max(1, evidence.startLine);
        const endLine = Math.max(startLine, evidence.endLine);
        setArtifactHighlights((prev) => ({ ...prev, [artifactKey]: { startLine, endLine } }));
        setArtifactTableHighlights((prev) => {
          if (!prev[artifactKey]) {
            return prev;
          }
          const next = { ...prev };
          delete next[artifactKey];
          return next;
        });
      } else if (evidence?.kind === 'table' && Array.isArray(evidence.rowKeys) && evidence.rowKeys.length > 0) {
        const keys = evidence.rowKeys.filter((v) => typeof v === 'string' && v.trim()).map((v) => v.trim());
        if (keys.length > 0) {
          setArtifactTableHighlights((prev) => ({ ...prev, [artifactKey]: keys }));
          setArtifactHighlights((prev) => {
            if (!prev[artifactKey]) {
              return prev;
            }
            const next = { ...prev };
            delete next[artifactKey];
            return next;
          });
        }
      }
    },
    [artifactsByRun, artifactPreview, currentRunId, ensureRunArtifacts, handleLoadArtifactPreview]
  );

  const closeInspector = useCallback(() => {
    if (inspectorTab === 'preview' && inspectorPreviewSource === 'artifacts') {
      setInspectorTab('artifacts');
      setInspectorPreviewSource(null);
      setInspectorMaximized(false);
      return;
    }
    setInspectorOpen(false);
    setInspectorMaximized(false);
    setInspectorPreviewSource(null);
    setInspectorArtifactId(null);
    setInspectorArtifact(null);
    setInspectorTab('preview');
  }, [inspectorPreviewSource, inspectorTab]);

  useEffect(() => {
    if (!inspectorOpen || !inspectorArtifactId || inspectorMaximized) {
      return;
    }
    if (typeof window === 'undefined' || window.innerWidth > 1080) {
      return;
    }
    const frameId = window.requestAnimationFrame(() => {
      const inspectorNode = inspectorRef.current;
      if (inspectorNode && typeof inspectorNode.scrollIntoView === 'function') {
        inspectorNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [inspectorArtifactId, inspectorMaximized, inspectorOpen]);

  const openInspectorArtifactsTab = useCallback(async () => {
    setInspectorOpen(true);
    setInspectorTab('artifacts');
    setInspectorPreviewSource(null);
    setInspectorArtifactId(null);
    setInspectorArtifact(null);
    setInspectorMaximized(false);
    if (currentRunId && !hasFetchedRunArtifacts(artifactsByRun, currentRunId)) {
      await ensureRunArtifacts(currentRunId);
    }
  }, [artifactsByRun, currentRunId, ensureRunArtifacts]);

  const buildSiteHref = useCallback((siteId: string) => `/app/posture?scope=site&siteId=${encodeURIComponent(siteId)}`, []);

  const renderTextWithSiteLinks = useCallback(
    (text: string): ReactNode => {
      if (!text) {
        return text;
      }
      const parts: ReactNode[] = [];
      let cursor = 0;
      let matchedSite = false;
      text.replace(uuidTokenPattern, (match, offset: number) => {
        const siteId = match.toLowerCase();
        const siteName = siteNameById[siteId];
        if (!siteName) {
          return match;
        }
        matchedSite = true;
        if (offset > cursor) {
          parts.push(text.slice(cursor, offset));
        }
        parts.push(
          <a key={`${siteId}-${offset}`} href={buildSiteHref(siteId)} className="chat-site-link">
            {siteName}
          </a>
        );
        cursor = offset + match.length;
        return match;
      });
      if (!matchedSite) {
        return text;
      }
      if (cursor < text.length) {
        parts.push(text.slice(cursor));
      }
      return parts;
    },
    [buildSiteHref, siteNameById]
  );

  const renderArtifactTitleWithSiteLinks = useCallback(
    (artifact: ChatArtifact): ReactNode => {
      const context = describeArtifactContext(artifact);
      if (!context) {
        return renderTextWithSiteLinks(artifactLinkLabel(artifact));
      }
      return (
        <span>
          {renderTextWithSiteLinks(artifactLinkLabel(artifact))}
          <span className="chat-artifact-context"> — {renderTextWithSiteLinks(context)}</span>
        </span>
      );
    },
    [renderTextWithSiteLinks]
  );

  const renderHighlightedPreview = useCallback(
    (artifactId: string, content: string, options?: { showLineNumbers?: boolean }) => {
      const highlight = artifactHighlights[artifactId];
      const showLineNumbers = options?.showLineNumbers ?? false;
      if (!highlight && !showLineNumbers) {
        return renderTextWithSiteLinks(content);
      }
      if (!highlight && showLineNumbers && content.length === 0) {
        return content;
      }
      const lines = content.split('\n');
      const startIdx = highlight ? Math.max(0, highlight.startLine - 1) : 0;
      const endIdx = highlight ? Math.min(lines.length - 1, highlight.endLine - 1) : -1;
      if (highlight && startIdx > endIdx) {
        return content;
      }
      return (
        <>
          {lines.map((line, idx) => {
            const isHL = Boolean(highlight) && idx >= startIdx && idx <= endIdx;
            const lineNo = idx + 1;
            const className = [
              'chat-artifact-line',
              showLineNumbers ? 'chat-artifact-line--numbered' : '',
              isHL ? 'chat-artifact-line--highlight' : ''
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <span
                key={`${artifactId}-line-${idx}`}
                data-line={lineNo}
                className={className}>
                {renderTextWithSiteLinks(line)}
                {'\n'}
              </span>
            );
          })}
        </>
      );
    },
    [artifactHighlights, renderTextWithSiteLinks]
  );

  const startNewThread = () => {
    persistThreadUiState(selectedThread);
    creatingThreadDraftRef.current = null;
    clearPendingComposerFiles();
    setCreatingThread(true);
    setSuppressAutoThreadSelection(false);
    setChatLoadErrorMessage(null);
    setSelectedThread(null);
    updateThreadIdInLocation(null, { markAsInternal: true });
    setMessageEvents([]);
    setMessagesCursor(null);
    setCurrentRunId(null);
    setComposerText('');
    setMessageFeedback({});
    setRunRating(null);
    setNewThreadTitle('');
    setComposerError(null);
    setArtifactsByRun({});
    requestedRunArtifactsRef.current.clear();
    closeInspector();
    setThreadListCollapsed(false);
    setRunEventHistory({});
  };
  const toggleMessageFeedback = useCallback(
    async (eventMeta: ChatMessageEventWithDelay, type: 'liked' | 'disliked') => {
      setMessageFeedback((prev) => ({
        ...prev,
        [eventMeta.id]: type
      }));

      if (eventMeta.role !== 'assistant' || !eventMeta.messageId) {
        return;
      }
      try {
        await requestOrgApi(`chat/messages/${eventMeta.messageId}/reactions`, {
          method: 'POST',
          body: JSON.stringify({
            reaction: type === 'liked' ? 'thumbsUp' : 'thumbsDown'
          })
        });
      } catch (error) {
        console.error('Failed to record reaction', error);
      }
    },
    [requestOrgApi]
  );
  const handleCopyRunId = useCallback(async (runId: string) => {
    try {
      await copyTextToClipboard(runId);
      setCopiedRunId(runId);
      if (copiedRunIdResetTimeoutRef.current != null && typeof window !== 'undefined') {
        window.clearTimeout(copiedRunIdResetTimeoutRef.current);
      }
      if (typeof window !== 'undefined') {
        copiedRunIdResetTimeoutRef.current = window.setTimeout(() => {
          setCopiedRunId((current) => (current === runId ? null : current));
          copiedRunIdResetTimeoutRef.current = null;
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to copy run ID', error);
    }
  }, []);
  const handleCopyThreadLink = useCallback(async () => {
    if (!selectedThreadShareUrl) {
      return;
    }
    try {
      await copyTextToClipboard(selectedThreadShareUrl);
      setCopiedThreadLink(true);
      if (copiedThreadLinkResetTimeoutRef.current != null && typeof window !== 'undefined') {
        window.clearTimeout(copiedThreadLinkResetTimeoutRef.current);
      }
      if (typeof window !== 'undefined') {
        copiedThreadLinkResetTimeoutRef.current = window.setTimeout(() => {
          setCopiedThreadLink(false);
          copiedThreadLinkResetTimeoutRef.current = null;
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to copy chat thread link', error);
    }
  }, [selectedThreadShareUrl]);
  const resolvePinned = (thread: ChatThreadSummary): boolean => {
    const id = thread.id ?? '';
    if (!id) {
      return Boolean(thread.pinned);
    }
    return localPinned[id] ?? Boolean(thread.pinned);
  };
  const togglePinned = useCallback(
    async (thread: ChatThreadSummary, event?: MouseEvent) => {
      event?.stopPropagation();
      const id = thread.id;
      if (!id) {
        return;
      }
      const nextPinned = !resolvePinned(thread);
      setLocalPinned((prev) => ({
        ...prev,
        [id]: nextPinned
      }));
      try {
        const threadResponse = await requestOrgApi(`chat/threads/${id}`);
        const etag = threadResponse.headers.get('ETag');
        if (!etag) {
          throw new Error('Missing thread ETag; reload and try again.');
        }
        await requestOrgApi(`chat/threads/${id}`, {
          method: 'PATCH',
          headers: {
            'If-Match': etag
          },
          body: JSON.stringify({ pinned: nextPinned })
        });
        reloadThreads();
      } catch (error) {
        setLocalPinned((prev) => {
          const rollback = { ...prev };
          delete rollback[id];
          return rollback;
        });
        reloadThreads();
        console.error('Failed to toggle pin', error);
      }
    },
    [reloadThreads, requestOrgApi, resolvePinned]
  );
  const deriveThreadStatus = useCallback(
    (thread: ChatThreadSummary): { label: string; tone: 'neutral' | 'success' | 'warning' | 'danger' } | null => {
      if (thread.archivedAt) {
        return { label: 'Archived', tone: 'danger' };
      }
      const attention = thread.id ? threadAttentionById[thread.id] : undefined;
      if (attention === 'action_required') {
        return { label: 'Action required', tone: 'warning' };
      }
      if (attention === 'ready') {
        return { label: 'Response ready', tone: 'success' };
      }
      if (attention === 'failed') {
        return { label: 'Run failed', tone: 'danger' };
      }
      if (thread.id && activeStreamingThreadIds.has(thread.id)) {
        return { label: 'Streaming', tone: 'neutral' };
      }
      return null;
    },
    [activeStreamingThreadIds, threadAttentionById]
  );
  const threadListQuery = buildThreadsQueryString();
  const threadListScopeKey = orgId ? `${orgId}?${threadListQuery}` : null;
  const threadsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    if (ownerFilter === 'mine' && !currentUserId) {
      return null;
    }
    return () => fetchOrgCollection<ChatThreadSummary>(basePath, orgId, `chat/threads?${threadListQuery}`);
  }, [basePath, orgId, refreshKey, fetchOrgCollection, currentUserId, ownerFilter, threadListQuery]);

  const { data, dataScope, loading, error, backendMissing } = useApiQuery<CursorListResponse<ChatThreadSummary>>(
    threadsFetcher,
    [threadsFetcher, refreshKey],
    { dataScope: threadListScopeKey ?? undefined, preservePreviousDataOnError: isTransientThreadListError }
  );
  const isCurrentThreadListScope = dataScope === undefined || dataScope === threadListScopeKey;
  const currentThreadList = data && isCurrentThreadListScope ? data : null;
  const threadListError = isCurrentThreadListScope ? error : null;
  const scopedThreads = useMemo(
    () => (threadsScopeKey === threadListScopeKey ? threads : []),
    [threads, threadsScopeKey, threadListScopeKey]
  );
  const threadListLoading = loading || threadsScopeKey !== threadListScopeKey;
  const paginationRequestIdRef = useRef(0);
  useLayoutEffect(() => {
    paginationRequestIdRef.current += 1;
    setThreadsLoadingMore(false);
    setThreadsLoadMoreError(null);
    return () => {
      paginationRequestIdRef.current += 1;
    };
  }, [threadListScopeKey]);
  const retainPreviousThreads = Boolean(threadListError && currentThreadList && isTransientThreadListError(threadListError));
  const threadListErrorMessage = threadListError && !retainPreviousThreads ? getChatThreadsLoadErrorMessage(threadListError) : null;
  const activeChatBannerMessage = threadListErrorMessage ?? chatLoadErrorMessage ?? sharedLinkFallbackMessage;

  useEffect(() => {
    if (!retainPreviousThreads) {
      return;
    }
    console.warn('Failed to refresh chat threads; keeping the previous list.', threadListError);
    const retryTimer = window.setTimeout(reloadThreads, 30_000);
    return () => window.clearTimeout(retryTimer);
  }, [reloadThreads, retainPreviousThreads, threadListError]);

  useEffect(() => {
    if (!orgId) {
      setSiteNameById({});
      return;
    }
    let cancelled = false;
    const sitesResource = `sites?limit=${SITE_LOOKUP_PAGE_LIMIT}`;
    void (async () => {
      let nextResource = sitesResource;
      let nextCursor = '';
      const next: Record<string, string> = {};

      for (let page = 0; page < SITE_LOOKUP_MAX_PAGES; page += 1) {
        const response = await fetchOrgCollection<SiteSummary>(basePath, orgId, nextResource, { cache: 'no-store' });
        (response.items ?? []).forEach((site) => {
          const siteId = site.id?.trim();
          if (siteId) {
            next[siteId.toLowerCase()] = site.name?.trim() || `Site ${siteId.slice(0, 8)}`;
          }
        });
        nextCursor = typeof response.meta?.next === 'string' ? response.meta.next.trim() : '';
        if (!nextCursor) {
          break;
        }
        nextResource = resourceWithCursor(sitesResource, nextCursor);
      }

      if (!cancelled) {
        setSiteNameById(next);
      }
    })()
      .catch((sitesError) => {
        if (!cancelled) {
          console.error('Failed to load sites', sitesError);
          setSiteNameById({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [basePath, fetchOrgCollection, orgId]);

  const normalizeThreadsMeta = useCallback(
    (
      meta: CursorListResponse<ChatThreadSummary>['meta'] | null | undefined,
      fallbackExactTotal: number | null = null
    ): CursorListResponse<ChatThreadSummary>['meta'] | null => {
      if (!meta) {
        return null;
      }
      const normalizedExactTotal = typeof meta.exactTotal === 'number' && !Number.isNaN(meta.exactTotal) ? meta.exactTotal : fallbackExactTotal;
      return {
        ...meta,
        next: meta.next ?? null,
        prev: meta.prev ?? null,
        exactTotal: normalizedExactTotal ?? null
      };
    },
    []
  );

  useEffect(() => {
    if (ownerFilter === 'mine' && !currentUserId) {
      setThreads([]);
      setThreadsMeta(null);
      setThreadsScopeKey(threadListScopeKey);
      return;
    }
    if (retainPreviousThreads || (loading && currentThreadList)) {
      return;
    }
    if (!currentThreadList) {
      setThreads([]);
      setThreadsMeta(null);
      setThreadsScopeKey(threadListScopeKey);
      return;
    }
    const incoming = currentThreadList.items ?? [];
    const visibleThreads = incoming.filter((thread) => !thread.id || !locallyDeletedThreadIds.has(thread.id));
    const removedCount = incoming.length - visibleThreads.length;
    setThreads(visibleThreads);
    setThreadsScopeKey(threadListScopeKey);
    const normalizedMeta = normalizeThreadsMeta(currentThreadList.meta ?? null, null);
    setThreadsMeta(
      normalizedMeta && removedCount > 0 && typeof normalizedMeta.exactTotal === 'number'
        ? { ...normalizedMeta, exactTotal: Math.max(0, normalizedMeta.exactTotal - removedCount) }
        : normalizedMeta
    );
    setThreadsLoadMoreError(null);
  }, [currentUserId, currentThreadList, loading, locallyDeletedThreadIds, normalizeThreadsMeta, ownerFilter, retainPreviousThreads, threadListScopeKey]);

  const hasMoreThreads = useMemo(() => {
    if (threadsScopeKey !== threadListScopeKey) {
      return false;
    }
    if (threadsMeta?.next) {
      return true;
    }
    const exactTotal = threadsMeta?.exactTotal;
    if (typeof exactTotal === 'number' && !Number.isNaN(exactTotal)) {
      return scopedThreads.length < exactTotal;
    }
    return false;
  }, [scopedThreads.length, threadsMeta?.exactTotal, threadsMeta?.next, threadsScopeKey, threadListScopeKey]);

  const loadMoreThreads = useCallback(async () => {
    if (!orgId || loading || threadsLoadingMore) {
      return;
    }
    if (!hasMoreThreads) {
      return;
    }
    const cursor = threadsMeta?.next;
    if (!cursor) {
      return;
    }
    const requestId = ++paginationRequestIdRef.current;
    setThreadsLoadingMore(true);
    setThreadsLoadMoreError(null);
    try {
      const response = await fetchOrgCollection<ChatThreadSummary>(basePath, orgId, `chat/threads?${buildThreadsQueryString(cursor)}`);
      if (paginationRequestIdRef.current !== requestId) {
        return;
      }
      const incomingThreads = response.items ?? [];
      const nextThreads = incomingThreads.filter((thread) => !thread.id || !locallyDeletedThreadIds.has(thread.id));
      const removedCount = incomingThreads.length - nextThreads.length;
      setThreads((prev) => mergeThreadsById(prev, nextThreads));
      setThreadsMeta((prev) => {
        const normalized = normalizeThreadsMeta(response.meta ?? null, prev?.exactTotal ?? null);
        const adjusted =
          normalized && removedCount > 0 && typeof normalized.exactTotal === 'number'
            ? { ...normalized, exactTotal: Math.max(0, normalized.exactTotal - removedCount) }
            : normalized;
        return adjusted;
      });
    } catch (caught) {
      if (paginationRequestIdRef.current !== requestId) {
        return;
      }
      const error = caught instanceof Error ? caught : new Error('Failed to load more chat threads.');
      setThreadsLoadMoreError(error);
      console.error('Failed to load more chat threads', error);
    } finally {
      if (paginationRequestIdRef.current === requestId) {
        setThreadsLoadingMore(false);
      }
    }
  }, [basePath, fetchOrgCollection, hasMoreThreads, loading, orgId, threadsLoadingMore, threadsMeta?.next, normalizeThreadsMeta, buildThreadsQueryString, locallyDeletedThreadIds]);

  const handleThreadListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (loading || threadsLoadingMore || !hasMoreThreads) {
        return;
      }
      const target = event.currentTarget;
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - THREAD_SCROLL_THRESHOLD_PX;
      if (nearBottom) {
        void loadMoreThreads();
      }
    },
    [hasMoreThreads, loadMoreThreads, loading, threadsLoadingMore]
  );

  useEffect(() => {
    if (loading || threadsLoadingMore) {
      return;
    }
    if (!hasMoreThreads || !threadsMeta?.next) {
      return;
    }
    const target = threadListRef.current;
    if (!target) {
      return;
    }
    if (target.scrollHeight <= target.clientHeight + THREAD_SCROLL_THRESHOLD_PX) {
      void loadMoreThreads();
    }
  }, [hasMoreThreads, loading, loadMoreThreads, threads.length, threadsLoadingMore, threadsMeta?.next]);

  const visibleThreads = useMemo(
    () => scopedThreads.filter((thread) => !hasChatThreadTag(thread, TOPOLOGY_WORKBENCH_THREAD_TAG)),
    [scopedThreads]
  );

  const totalThreadsCount = useMemo(() => visibleThreads.length, [visibleThreads.length]);

  const sortedThreads = useMemo(() => {
    return [...visibleThreads].sort((a, b) => {
      if (a.pinned && !b.pinned) {
        return -1;
      }
      if (!a.pinned && b.pinned) {
        return 1;
      }
      const aTime = Date.parse(a.lastMessageAt ?? a.updatedAt ?? '') || 0;
      const bTime = Date.parse(b.lastMessageAt ?? b.updatedAt ?? '') || 0;
      return bTime - aTime;
    });
  }, [visibleThreads]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const threadFilterOptions = useMemo(
    () => ({
      currentUserId,
      ownerFilter,
      visibilityFilter,
      onlyPinned,
      normalizedSearch,
      isPinned: resolvePinned
    }),
    [currentUserId, normalizedSearch, ownerFilter, onlyPinned, resolvePinned, visibilityFilter]
  );

  const filteredThreads = useMemo(
    () => sortedThreads.filter((thread) => chatThreadMatchesFilters(thread, threadFilterOptions)),
    [sortedThreads, threadFilterOptions]
  );
  const hasRequestedThreadInThreads = useMemo(
    () => (requestedThreadId ? scopedThreads.some((thread) => thread.id === requestedThreadId) : false),
    [requestedThreadId, scopedThreads]
  );

  const selectedThreadMatchesActiveFilters = useMemo(
    () => (selectedThread ? chatThreadMatchesFilters(selectedThread, threadFilterOptions) : false),
    [selectedThread, threadFilterOptions]
  );
  const threadRunIds = useMemo(() => collectThreadRunIds(messageEvents), [messageEvents]);
  const fallbackArtifactRunByMessageId = useMemo(() => buildFallbackArtifactRunByMessageId(messageEvents), [messageEvents]);

  useEffect(() => {
    if (!requestedThreadId) {
      pendingInternalThreadIdsRef.current.clear();
      setRequestedThreadLoadState(null);
      return;
    }
    if (
      pendingInternalThreadIdsRef.current.delete(requestedThreadId) ||
      selectedThreadRef.current?.id === requestedThreadId
    ) {
      return;
    }
    pendingInternalThreadIdsRef.current.clear();
    setOwnerFilter('all');
    setVisibilityFilter('all');
    setOnlyPinned(false);
    setSearchTerm('');
  }, [requestedThreadId]);

  useEffect(() => {
    if (!requestedThreadId || !orgId || (requestedOrgId && requestedOrgId !== orgId)) {
      setRequestedThreadLoadState(null);
      return;
    }
    if (hasRequestedThreadInThreads) {
      setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'resolved' });
      setSuppressAutoThreadSelection(false);
      return;
    }

    let cancelled = false;
    setRequestedThreadLoadState((current) => {
      if (current?.threadId === requestedThreadId && current.status === 'loading') {
        return current;
      }
      return { threadId: requestedThreadId, status: 'loading' };
    });
    void (async () => {
      try {
        const response = await requestOrgApi(`chat/threads/${encodeURIComponent(requestedThreadId)}`);
        const thread = (await response.json()) as ChatThreadSummary;
        if (cancelled) {
          return;
        }
        clearTransientChatRefreshNotice();
        requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: 0 };
        setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'resolved' });
        setSuppressAutoThreadSelection(false);
        setThreads((prev) => mergeThreadsById(prev, [thread]));
        setSelectedThread((current) => (current?.id === thread.id ? current : thread));
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load requested chat thread', error);
          if (isUnavailableThreadError(error)) {
            requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: 0 };
            setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'failed' });
            setSuppressAutoThreadSelection(true);
            setSelectedThread(null);
            setChatLoadErrorMessage('The requested chat thread was not found or is not available to you.');
            return;
          }
          if (isTransientChatLoadError(error)) {
            const retryState =
              requestedThreadRetryCountRef.current?.threadId === requestedThreadId
                ? requestedThreadRetryCountRef.current
                : { threadId: requestedThreadId, count: 0 };
            if (retryState.count < REQUESTED_THREAD_MAX_RETRIES) {
              requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: retryState.count + 1 };
              setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'retrying' });
              setChatLoadErrorMessage(null);
              showTransientChatRefreshNotice('Chat refresh failed temporarily. Retrying...');
              scheduleRequestedThreadRetry();
              return;
            }
            clearTransientChatRefreshNotice();
            requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: 0 };
            setChatLoadErrorMessage(getChatThreadLoadErrorMessage(error));
            setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'failed' });
            return;
          }
          requestedThreadRetryCountRef.current = { threadId: requestedThreadId, count: 0 };
          setRequestedThreadLoadState({ threadId: requestedThreadId, status: 'failed' });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasRequestedThreadInThreads,
    orgId,
    clearTransientChatRefreshNotice,
    requestOrgApi,
    requestedOrgId,
    requestedThreadId,
    requestedThreadRetryKey,
    scheduleRequestedThreadRetry,
    showTransientChatRefreshNotice
  ]);

  useEffect(() => {
    if (loading) {
      return;
    }
    const requestedThreadStillPending =
      requestedThreadId &&
      requestedThreadLoadState?.threadId === requestedThreadId &&
      !hasRequestedThreadInThreads;
    if (requestedThreadStillPending) {
      const requestedThread = filteredThreads.find((thread) => thread.id === requestedThreadId) ?? null;
      if (requestedThread && requestedThread !== selectedThread) {
        setSelectedThread(requestedThread);
      } else if (selectedThread?.id !== requestedThreadId) {
        setSelectedThread(null);
      }
      return;
    }
    if (requestedThreadId) {
      const requestedThread = filteredThreads.find((thread) => thread.id === requestedThreadId) ?? null;
      if (requestedThread && requestedThread !== selectedThread) {
        setSelectedThread(requestedThread);
        return;
      }
      if (selectedThread?.id !== requestedThreadId) {
        return;
      }
    }
    if (filteredThreads.length === 0) {
      if (selectedThreadMatchesActiveFilters) {
        return;
      }
      setSelectedThread(null);
      return;
    }
    if (!selectedThread && !creatingThread) {
      if (suppressAutoThreadSelection) {
        return;
      }
      setSelectedThread(filteredThreads[0]);
      return;
    }
    if (selectedThread) {
      const refreshed = filteredThreads.find((thread) => thread.id === selectedThread.id);
      if (refreshed && refreshed !== selectedThread) {
        setSelectedThread(refreshed);
        return;
      }
      if (!refreshed && !creatingThread && !selectedThreadMatchesActiveFilters) {
        setSelectedThread(filteredThreads[0] ?? null);
      }
    }
  }, [
    creatingThread,
    filteredThreads,
    hasRequestedThreadInThreads,
    loading,
    requestedThreadId,
    requestedThreadLoadState,
    selectedThread,
    selectedThreadMatchesActiveFilters,
    suppressAutoThreadSelection
  ]);

  useEffect(() => {
    if (loading || creatingThread) {
      return;
    }
    const selectedThreadId = selectedThread?.id ?? null;
    if (requestedThreadId && selectedThreadId !== requestedThreadId) {
      return;
    }
    const requestedThreadStillLoading =
      requestedThreadId &&
      requestedThreadLoadState?.threadId === requestedThreadId &&
      requestedThreadLoadState.status === 'loading' &&
      !threads.some((thread) => thread.id === requestedThreadId);
    if (requestedThreadStillLoading && selectedThreadId !== requestedThreadId) {
      return;
    }
    updateThreadIdInLocation(selectedThreadId, { replace: true, markAsInternal: true });
  }, [creatingThread, loading, requestedThreadId, requestedThreadLoadState, selectedThread?.id, threads, updateThreadIdInLocation]);

  useEffect(() => {
    const threadId = selectedThread?.id;
    if (!threadId || creatingThread) {
      return;
    }
    if (prevThreadIdRef.current !== threadId) {
      return;
    }
    persistThreadUiState(selectedThread);
  }, [artifactsByRun, creatingThread, currentRunId, messageEvents, messagesCursor, persistThreadUiState, runEventHistory, selectedThread]);

  useEffect(() => {
    const cache = threadUiStateCacheRef.current;
    if (cache.size === 0) {
      return;
    }
    Array.from(cache.keys()).forEach((threadId) => {
      if (selectedThread?.id === threadId) {
        if (!shouldRetainThreadSnapshot(selectedThread, cache.get(threadId), activeStreamingThreadIds)) {
          cache.delete(threadId);
        }
        return;
      }
      const thread = threads.find((entry) => entry.id === threadId);
      if (thread && !shouldRetainThreadSnapshot(thread, cache.get(threadId), activeStreamingThreadIds)) {
        cache.delete(threadId);
      }
    });
    while (cache.size > MAX_ACTIVE_THREAD_SESSION_CACHE) {
      const oldestKey = cache.keys().next().value;
      if (!oldestKey) {
        break;
      }
      cache.delete(oldestKey);
    }
  }, [activeStreamingThreadIds, selectedThread, threads]);

  useEffect(() => {
    const threadId = selectedThread?.id ?? null;
    const prevThreadId = prevThreadIdRef.current;
    const threadChanged = threadId !== prevThreadId;
    const skipReload = skipThreadReloadRef.current && !threadChanged && Boolean(threadId);
    skipThreadReloadRef.current = false;

    if (creatingThread || !threadId || !selectedThread) {
      clearActiveThreadUiState();
      requestedRunArtifactsRef.current.clear();
      prevThreadIdRef.current = threadId;
      return;
    }

    if (threadChanged) {
      if (!restoreCachedThreadUiState(threadId)) {
        clearActiveThreadUiState();
      }
      requestedRunArtifactsRef.current.clear();
      autoScrollRef.current = true;
    }

    if (threadChanged || !skipReload) {
      setMessageFeedback({});
      setComposerError(null);
      setRunRating(null);
      setComposerText('');
      if (threadChanged) {
        closeInspector();
      }
      loadThreadMessages(threadId);
    }

    prevThreadIdRef.current = threadId;
  }, [
    clearActiveThreadUiState,
    closeInspector,
    selectedThread,
    creatingThread,
    loadThreadMessages,
    restoreCachedThreadUiState
  ]);

  useEffect(() => {
    if (creatingThread || !selectedThread) {
      setCurrentRunId(null);
      return;
    }
    const latestRunId = selectedThread.latestRunId ?? null;
    if (latestRunId && selectedThread.id) {
      runThreadMapRef.current.set(latestRunId, selectedThread.id);
    }
    const preferredRunId = resolvePreferredThreadRunId({
      currentRunId,
      latestRunId,
      threadRunIds
    });
    if (preferredRunId !== currentRunId) {
      setCurrentRunId(preferredRunId);
    }
    const shouldStream =
      typeof latestRunId === 'string' &&
      (isActiveRunStatus(selectedThread.latestRunStatus) || isPausedRunStatus(selectedThread.latestRunStatus)) &&
      !completedRunsRef.current.has(latestRunId);
    if (shouldStream && !activeStreamSessionsRef.current.has(latestRunId)) {
      startStreamingRun(latestRunId, selectedThread.id ?? null);
    }
  }, [activeStreamRunIds, creatingThread, currentRunId, selectedThread, startStreamingRun, threadRunIds]);

  useEffect(() => {
    return () => {
      multiplexedStreamControllerRef.current?.abort();
      multiplexedStreamControllerRef.current = null;
      if (multiplexedReconnectTimerRef.current !== null) {
        window.clearTimeout(multiplexedReconnectTimerRef.current);
        multiplexedReconnectTimerRef.current = null;
      }
      activeStreamSessionsRef.current.clear();
      cancelScheduledChangefeedRefresh();
      if (chatRefreshNoticeTimeoutRef.current !== null) {
        window.clearTimeout(chatRefreshNoticeTimeoutRef.current);
      }
      if (requestedThreadRetryTimeoutRef.current !== null) {
        window.clearTimeout(requestedThreadRetryTimeoutRef.current);
      }
    };
  }, [cancelScheduledChangefeedRefresh]);

  useEffect(() => {
    if (multiplexedReconnectTimerRef.current !== null) {
      window.clearTimeout(multiplexedReconnectTimerRef.current);
      multiplexedReconnectTimerRef.current = null;
    }
    multiplexedStreamControllerRef.current?.abort();
    multiplexedStreamControllerRef.current = null;
    const generation = ++multiplexedStreamGenerationRef.current;
    if (!orgId || activeStreamRunIds.length === 0) {
      return;
    }
    const controller = new AbortController();
    multiplexedStreamControllerRef.current = controller;
    const buildStreamUrl = () => {
      const url = new URL(buildOrgUrl(basePath, orgId, 'chat/events'));
      activeStreamRunIds.forEach((runId) => {
        const session = activeStreamSessionsRef.current.get(runId);
        if (!session) {
          return;
        }
        url.searchParams.append('runId', runId);
        if (session.lastEventId) {
          url.searchParams.append('cursor', serializeMultiplexedCursor(runId, session.lastEventId));
        }
      });
      return url.toString();
    };

    console.debug('[chat] open multiplexed SSE stream', { runIds: activeStreamRunIds });
    void streamSse(
      buildStreamUrl(),
      {
        getUrl: buildStreamUrl,
        sendLastEventId: false,
        onOpen: () => {
          if (!controller.signal.aborted && multiplexedStreamGenerationRef.current === generation) {
            multiplexedReconnectAttemptRef.current = 0;
            console.debug('[chat] multiplexed SSE stream opened', { runIds: activeStreamRunIds });
          }
        },
        onEvent: (fallbackType, rawPayload, frameEventId) => {
          if (controller.signal.aborted || multiplexedStreamGenerationRef.current !== generation) {
            return;
          }
          let envelope: MultiplexedChatSseEnvelope;
          try {
            envelope = JSON.parse(rawPayload) as MultiplexedChatSseEnvelope;
          } catch {
            console.warn('[chat] invalid multiplexed SSE payload', { fallbackType });
            return;
          }
          const session = activeStreamSessionsRef.current.get(envelope.runId);
          if (!session) {
            return;
          }
          if (typeof envelope.eventId === 'number' && envelope.eventId > 0) {
            if (envelope.eventId <= session.lastEventSequence) {
              return;
            }
            session.lastEventSequence = envelope.eventId;
            session.lastEventId = String(envelope.eventId);
          } else if (typeof frameEventId === 'string' && frameEventId.trim()) {
            session.lastEventId = frameEventId;
          }
          const isMultiplexedEnvelope = typeof envelope.type === 'string' && Object.prototype.hasOwnProperty.call(envelope, 'payload');
          handleSseEvent(
            envelope.runId,
            isMultiplexedEnvelope ? envelope.type : fallbackType,
            isMultiplexedEnvelope ? JSON.stringify(envelope.payload ?? {}) : rawPayload
          );
        },
        reconnect: {
          enabled: true,
          maxAttempts: 2,
          retryDelayMs: 1000
        }
      },
      controller.signal
    ).catch(async (error) => {
      if (controller.signal.aborted || multiplexedStreamGenerationRef.current !== generation) {
        return;
      }
      console.error('Failed to stream multiplexed chat events', error);
      const failedSessions = activeStreamRunIds.map((runId) => ({
        runId,
        threadId: activeStreamSessionsRef.current.get(runId)?.threadId ?? null
      }));
      const reconciled = await Promise.all(
        failedSessions.map(async ({ runId, threadId }) => ({
          runId,
          status: await reconcileRunStateFromBackend(runId, threadId)
        }))
      );
      if (controller.signal.aborted || multiplexedStreamGenerationRef.current !== generation) {
        return;
      }
      reconciled.forEach(({ runId, status }) => {
        if (status === 'finished') {
          activeStreamSessionsRef.current.delete(runId);
        }
      });
      const nextActiveRunIds = Array.from(activeStreamSessionsRef.current.keys());
      if (nextActiveRunIds.length !== activeStreamRunIds.length || nextActiveRunIds.some((runId, index) => runId !== activeStreamRunIds[index])) {
        setActiveStreamRunIds(nextActiveRunIds);
      }
      if (activeStreamSessionsRef.current.size === 0) {
        return;
      }
      const retryAttempt = multiplexedReconnectAttemptRef.current;
      multiplexedReconnectAttemptRef.current += 1;
      const retryDelayMs = Math.min(1000 * 2 ** retryAttempt, 30_000);
      multiplexedReconnectTimerRef.current = window.setTimeout(() => {
        multiplexedReconnectTimerRef.current = null;
        if (
          !controller.signal.aborted &&
          multiplexedStreamGenerationRef.current === generation &&
          activeStreamSessionsRef.current.size > 0
        ) {
          setMultiplexedReconnectEpoch((value) => value + 1);
        }
      }, retryDelayMs);
    });

    const reconnectWhenOnline = () => {
      if (controller.signal.aborted || multiplexedStreamGenerationRef.current !== generation) {
        return;
      }
      if (multiplexedReconnectTimerRef.current !== null) {
        window.clearTimeout(multiplexedReconnectTimerRef.current);
        multiplexedReconnectTimerRef.current = null;
      }
      multiplexedReconnectAttemptRef.current = 0;
      controller.abort();
      setMultiplexedReconnectEpoch((value) => value + 1);
    };
    window.addEventListener('online', reconnectWhenOnline);

    return () => {
      window.removeEventListener('online', reconnectWhenOnline);
      if (multiplexedReconnectTimerRef.current !== null) {
        window.clearTimeout(multiplexedReconnectTimerRef.current);
        multiplexedReconnectTimerRef.current = null;
      }
      controller.abort();
      if (multiplexedStreamControllerRef.current === controller) {
        multiplexedStreamControllerRef.current = null;
      }
    };
  }, [activeStreamRunIds, basePath, handleSseEvent, multiplexedReconnectEpoch, orgId, reconcileRunStateFromBackend]);

  useEffect(() => {
    if (!inspectorOpen || inspectorTab !== 'artifacts' || !currentRunId || hasFetchedRunArtifacts(artifactsByRun, currentRunId)) {
      return;
    }
    void ensureRunArtifacts(currentRunId);
  }, [artifactsByRun, currentRunId, ensureRunArtifacts, inspectorOpen, inspectorTab]);

  useEffect(() => {
    threadRunIds.forEach((runId) => {
      void ensureRunArtifacts(runId);
    });
  }, [ensureRunArtifacts, threadRunIds]);

  const runStatusById = useMemo(() => {
    const summaries: Record<string, RunStatusSummary> = {};
    Object.entries(runEventHistory).forEach(([runId, events]) => {
      summaries[runId] = summarizeRunStatus(events);
    });
    return summaries;
  }, [runEventHistory]);

  const runMessagePresence = useMemo(() => {
    const presence: Record<string, { hasAssistant: boolean }> = {};
    messageEvents.forEach((event) => {
      if (!event.runId) {
        return;
      }
      if (!presence[event.runId]) {
        presence[event.runId] = { hasAssistant: false };
      }
      if (event.role === 'assistant') {
        presence[event.runId].hasAssistant = true;
      }
    });
    return presence;
  }, [messageEvents]);

  const liveAssistantPreviewEvent = useMemo<ChatMessageEventWithDelay | null>(() => {
    if (!selectedThread || !currentRunId) {
      return null;
    }
    const hasAssistant = runMessagePresence[currentRunId]?.hasAssistant ?? false;
    const hasRestoredRunActivity = (runEventHistory[currentRunId] ?? []).length > 0;
    if (
      !shouldDisplayUnresolvedRun({
        currentRunId,
        latestRunId: selectedThread.latestRunId ?? null,
        streamingRunId,
        hasAssistantReply: hasAssistant,
        hasRunHistory: hasRestoredRunActivity
      })
    ) {
      return null;
    }
    const events = runEventHistory[currentRunId] ?? [];
    const summary = runStatusById[currentRunId];
    if (!summary?.queued && !summary?.running && streamingRunId !== currentRunId && events.length === 0) {
      return null;
    }
    const latestTimestamp =
      [...events].reverse().find((event) => typeof event.timestamp === 'string' && event.timestamp.trim().length > 0)?.timestamp ??
      selectedThread.lastMessageAt ??
      selectedThread.updatedAt ??
      new Date().toISOString();
    const previewText = buildLiveAssistantPreviewText(events, summary);
    return {
      id: `live-assistant-${currentRunId}`,
      type: 'message',
      role: 'assistant',
      text: previewText,
      contentBlocks: [
        {
          type: 'text',
          text: previewText
        }
      ],
      artifacts: artifactsByRun[currentRunId] ?? [],
      timestamp: latestTimestamp,
      runId: currentRunId,
      isLivePreview: true
    };
  }, [artifactsByRun, currentRunId, runEventHistory, runMessagePresence, runStatusById, selectedThread, streamingRunId]);

  const visibleActiveRunEvents = useMemo<ChatSseEvent[]>(() => {
    if (!selectedThread || !currentRunId) {
      return [];
    }
    const hasAssistant = runMessagePresence[currentRunId]?.hasAssistant ?? false;
    const hasRestoredRunActivity = (runEventHistory[currentRunId] ?? []).length > 0;
    if (
      !shouldDisplayUnresolvedRun({
        currentRunId,
        latestRunId: selectedThread.latestRunId ?? null,
        streamingRunId,
        hasAssistantReply: hasAssistant,
        hasRunHistory: hasRestoredRunActivity
      })
    ) {
      return [];
    }
    return collectVisibleRunTimelineEvents(runEventHistory[currentRunId] ?? []);
  }, [currentRunId, runEventHistory, runMessagePresence, selectedThread, streamingRunId]);

  const timelineEvents = useMemo(() => {
    if (chatViewMode !== 'timeline') {
      const combined = [...messageEvents, ...visibleActiveRunEvents, ...collectApprovalTimelineEvents(runEventHistory)];
      if (liveAssistantPreviewEvent) {
        combined.push(liveAssistantPreviewEvent);
      }
      return sortEventsByTimestamp(combined);
    }
    const relatedRunIds = new Set<string>();
    messageEvents.forEach((event) => {
      if (event.runId) {
        relatedRunIds.add(event.runId);
      }
    });
    if (currentRunId) {
      relatedRunIds.add(currentRunId);
    }
    const combined: ChatTimelineEventWithDelay[] = [...messageEvents];
    if (liveAssistantPreviewEvent) {
      combined.push(liveAssistantPreviewEvent);
    }
    relatedRunIds.forEach((runId) => {
      const history = runEventHistory[runId];
      if (history?.length) {
        combined.push(...history);
      }
    });
    return sortEventsByTimestamp(combined);
  }, [chatViewMode, currentRunId, liveAssistantPreviewEvent, messageEvents, runEventHistory, visibleActiveRunEvents]);

  const agentCardsByRun = useMemo(() => {
    const output: Record<string, AgentCardState[]> = {};
    Object.entries(runEventHistory).forEach(([runId, events]) => {
      if (!events?.length) {
        return;
      }
      const sorted = sortEventsByTimestamp(events);
      const cards = new Map<string, AgentCardState>();
      const callKeyMap = new Map<string, string>();

      const ensureCard = (event: ChatSseEvent): AgentCardState | null => {
        if (!event.agent) {
          return null;
        }
        const key = event.call?.id ?? `${event.agent.kind}:${event.agent.id}`;
        let card = cards.get(key);
        if (!card) {
          card = {
            key,
            agent: event.agent,
            call: event.call,
            parentId: event.call?.parentId,
            runId,
            displayName: formatAgentCardTitle(event.agent, event.call),
            stages: [],
            narrationUpdates: [],
            plans: [],
            recentActivity: [],
            artifacts: [],
            firstSeen: event.timestamp,
            lastUpdated: event.timestamp,
            indent: 0
          };
          cards.set(key, card);
        }
        card.agent = event.agent;
        card.call = event.call ?? card.call;
        card.displayName = formatAgentCardTitle(card.agent, card.call);
        if (event.call?.parentId) {
          card.parentId = event.call.parentId;
        }
        if (event.call?.id) {
          callKeyMap.set(event.call.id, key);
        }
        card.lastUpdated = event.timestamp;
        return card;
      };

      sorted.forEach((event) => {
        const card = ensureCard(event);
        if (!card) {
          return;
        }
        if (event.type === 'progress') {
          const stage = event.stage?.trim();
          const progressEvent = (event.event ?? '').trim().toLowerCase();
          // `assistant` is the reserved progress stage for agent narration. Be
          // tolerant of missing/empty progress.event so history/live paths do
          // not silently downgrade narration into lifecycle progress.
          const isNarration = stage === 'assistant';
          if (isNarration) {
            const existingIdx = card.narrationUpdates.findIndex((entry) => entry.id === event.id);
            if (existingIdx >= 0) {
              card.narrationUpdates[existingIdx] = event;
            } else {
              const lastNarration = card.narrationUpdates[card.narrationUpdates.length - 1];
              if (!lastNarration || lastNarration.detail !== event.detail) {
                card.narrationUpdates.push(event);
                if (card.narrationUpdates.length > AGENT_CARD_NARRATION_LIMIT) {
                  card.narrationUpdates = card.narrationUpdates.slice(-AGENT_CARD_NARRATION_LIMIT);
                }
              }
            }
          } else if (stage && (progressEvent === 'stage_enter' || progressEvent === '')) {
            card.currentStage = stage;
            if (!card.stages.includes(stage)) {
              card.stages.push(stage);
            }
          }
          if (!isNarration) {
            card.latestLifecycleProgress = event;
          }
        }
        if (event.type === 'plan') {
          const planId = event.planId;
          let plan = card.plans.find((entry) => entry.planId === planId);
          if (!plan) {
            plan = {
              planId,
              round: event.round,
              title: event.title,
              steps: event.steps ?? [],
              updatedAt: event.timestamp,
              isActive: true
            };
            card.plans.unshift(plan);
          } else {
            plan.round = event.round ?? plan.round;
            plan.title = event.title ?? plan.title;
            plan.steps = event.steps ?? plan.steps;
            plan.updatedAt = event.timestamp;
            plan.isActive = true;
          }
          card.plans.forEach((entry) => {
            if (entry.planId !== planId) {
              entry.isActive = false;
            }
          });
          card.activePlanId = planId;
        }
        if (event.type === 'plan_step') {
          let plan = card.plans.find((entry) => entry.planId === event.planId);
          if (!plan) {
            plan = {
              planId: event.planId,
              steps: [],
              updatedAt: event.timestamp,
              isActive: false
            };
            card.plans.unshift(plan);
          }
          let step = plan.steps.find((entry) => entry.id === event.stepId);
          if (!step) {
            step = { id: event.stepId, title: event.stepId };
            plan.steps.push(step);
          }
          step.status = event.status as ChatRunPlanStepschemaStatusEnum;
          if (event.detail) {
            step.detail = event.detail;
          }
          if (typeof event.percent === 'number') {
            step.percent = event.percent;
          }
          plan.updatedAt = event.timestamp;
        }
        if (event.type === 'trace') {
          card.trace = event;
        }
        if (event.type === 'artifact' && event.artifactId) {
          const exists = card.artifacts.some((item) => item.artifactId === event.artifactId);
          if (!exists) {
            card.artifacts.push(event);
          }
        }
        const activity = buildAgentActivityItem(event);
        if (activity) {
          card.recentActivity = appendAgentActivity(card.recentActivity, activity);
        }
      });

      const computeIndent = (card: AgentCardState, seen: Set<string>): number => {
        if (!card.parentId) {
          return 0;
        }
        if (seen.has(card.key)) {
          return 0;
        }
        const parentKey = callKeyMap.get(card.parentId);
        if (!parentKey) {
          return 0;
        }
        const parentCard = cards.get(parentKey);
        if (!parentCard) {
          return 0;
        }
        seen.add(card.key);
        return computeIndent(parentCard, seen) + 1;
      };

      const list = Array.from(cards.values()).map((card) => ({
        ...card,
        indent: computeIndent(card, new Set())
      }));
      list.sort((a, b) => new Date(a.firstSeen).valueOf() - new Date(b.firstSeen).valueOf());
      output[runId] = list;
    });
    return output;
  }, [runEventHistory]);

  useEffect(() => {
    const updates: Record<string, boolean> = {};
    Object.values(agentCardsByRun).forEach((cards) => {
      cards.forEach((card) => {
        card.plans.forEach((plan) => {
          const planKey = `${card.key}:${plan.planId}`;
        if (!plan.isActive) {
          updates[planKey] = false;
          return;
        }
        updates[planKey] = true;
        const existing = planCollapseTimers.current[planKey];
        if (existing) {
          window.clearTimeout(existing);
        }
        planCollapseTimers.current[planKey] = window.setTimeout(() => {
          setPlanOpenOverrides((prev) => ({ ...prev, [planKey]: false }));
        }, 12000);
      });
      });
    });
    if (Object.keys(updates).length > 0) {
      setPlanOpenOverrides((prev) => ({ ...prev, ...updates }));
    }
    return () => {
      Object.values(planCollapseTimers.current).forEach((id) => window.clearTimeout(id));
      planCollapseTimers.current = {};
    };
  }, [agentCardsByRun]);

  useEffect(() => {
    scrollTimelineToBottom();
  }, [scrollTimelineToBottom, timelineEvents]);

  useEffect(() => {
    return () => {
      if (copiedRunIdResetTimeoutRef.current != null && typeof window !== 'undefined') {
        window.clearTimeout(copiedRunIdResetTimeoutRef.current);
      }
      if (copiedThreadLinkResetTimeoutRef.current != null && typeof window !== 'undefined') {
        window.clearTimeout(copiedThreadLinkResetTimeoutRef.current);
      }
    };
  }, []);

  const responseDuration = useMemo(() => {
    if (!currentRunId) {
      return null;
    }
    return computeResponseDuration(runEventHistory[currentRunId] ?? []);
  }, [currentRunId, runEventHistory]);
  const currentRunArtifacts = currentRunId ? artifactsByRun[currentRunId] ?? [] : [];

  const { allArtifacts, finalArtifacts, intermediateArtifacts } = useMemo(
    () =>
      buildArtifactCollections<ChatArtifact>({
        artifactsByRun,
        currentRunId,
        currentRunArtifacts,
        runEventHistory: runEventHistory as Record<string, Array<{ id: string; type?: string; artifactId?: string; kind?: string; summary?: string; size?: number; mimeType?: string; timestamp?: string; runId?: string }>>,
        messageEvents: messageEvents as Array<{ id: string; role: 'assistant' | 'user'; runId?: string; timestamp?: string; artifacts?: ChatArtifact[] }>,
        fallbackArtifactRunByMessageId
      }),
    [artifactsByRun, currentRunArtifacts, currentRunId, fallbackArtifactRunByMessageId, messageEvents, runEventHistory]
  );

  const finalFileAttachments = useMemo(
    () => finalArtifacts.filter((artifact) => isFileArtifactKind(artifact.kind)),
    [finalArtifacts]
  );

  const finalGeneratedArtifacts = useMemo(
    () => finalArtifacts.filter((artifact) => !isFileArtifactKind(artifact.kind)),
    [finalArtifacts]
  );

  const finalMessageArtifacts = useMemo(() => {
    const seen = new Set<string>();
    const artifacts: ChatArtifact[] = [];
    messageEvents.forEach((event) => {
      if (event.role !== 'assistant' || !Array.isArray(event.artifacts) || event.artifacts.length === 0) {
        return;
      }
      event.artifacts.forEach((artifact) => {
        const key = artifactIdentityKey(artifact.id, artifact.runId ?? event.runId);
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        artifacts.push(artifact.runId ? artifact : { ...artifact, runId: event.runId ?? artifact.runId });
      });
    });
    return artifacts;
  }, [messageEvents]);

  const canToggleArtifacts =
    isStreaming || finalFileAttachments.length > 0 || finalGeneratedArtifacts.length > 0 || intermediateArtifacts.length > 0;

  const sanitizeArtifactMarkdownLabel = useCallback(
    (artifact: ChatArtifact): string =>
      artifactLinkLabel(artifact)
        .replace(uuidTokenPattern, (match) => siteNameById[match.toLowerCase()] ?? '')
        .replace(/\(\s*\)/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim(),
    [siteNameById]
  );

  const artifactIdByLinkLabel = useMemo(() => {
    const map = new Map<string, string>();
    allArtifacts.forEach((artifact) => {
      map.set(artifactLinkLabel(artifact), artifact.id);
      map.set(sanitizeArtifactMarkdownLabel(artifact), artifact.id);
    });
    return map;
  }, [allArtifacts, sanitizeArtifactMarkdownLabel]);

  const getScopedArtifactsForEvent = useCallback(
    (event?: { runId?: string; artifacts?: Array<{ id: string; runId?: string; summary?: string; kind?: string; mimeType?: string }> }) => {
      const explicitArtifacts = (event?.artifacts ?? [])
        .map((artifact) => {
          const runId = artifact.runId ?? event?.runId ?? null;
          return findArtifactRecord(allArtifacts, artifact.id, runId) ?? artifact;
        })
        .filter((artifact): artifact is ChatArtifact => Boolean(artifact?.id));
      if (explicitArtifacts.length > 0) {
        return explicitArtifacts;
      }
      const runId = event?.runId?.trim();
      if (!runId) {
        return [];
      }
      return allArtifacts.filter((artifact) => artifact.runId === runId);
    },
    [allArtifacts]
  );

  const resolveScopedArtifactIdByLabel = useCallback(
    (
      scopedArtifacts: ChatArtifact[],
      candidateLabel?: string | null
    ): string | null => {
      const normalized = candidateLabel?.trim();
      if (!normalized) {
        return null;
      }
      for (const artifact of scopedArtifacts) {
        if (artifactLinkLabel(artifact) === normalized || sanitizeArtifactMarkdownLabel(artifact) === normalized) {
          return artifact.id;
        }
      }
      return artifactIdByLinkLabel.get(normalized) ?? null;
    },
    [artifactIdByLinkLabel, sanitizeArtifactMarkdownLabel]
  );

  const isCurrentThreadChatLink = useCallback(
    (href: string): boolean => {
      const currentThreadId = selectedThread?.id ?? null;
      const currentOrgId = orgId ?? null;
      try {
        const parsed = new URL(href, window.location.origin);
        if (parsed.pathname.replace(/\/+$/, '') !== '/app/chat-agents') {
          return false;
        }
        const hrefThreadId = parsed.searchParams.get('threadId');
        const hrefOrgId = parsed.searchParams.get('orgId');
        if (currentThreadId && hrefThreadId !== currentThreadId) {
          return false;
        }
        if (currentOrgId && hrefOrgId && hrefOrgId !== currentOrgId) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    },
    [orgId, selectedThread?.id]
  );

  const resolveDirectChatLinkAssignments = useCallback(
    (
      text: string,
      event?: { runId?: string; artifacts?: Array<{ id: string; runId?: string; summary?: string; kind?: string; mimeType?: string }> }
    ): Map<number, string> => {
      const assignments = new Map<number, string>();
      const scopedArtifacts = getScopedArtifactsForEvent(event);
      if (scopedArtifacts.length === 0) {
        return assignments;
      }
      const consumedArtifactIds = new Set<string>();
      for (const match of text.matchAll(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi)) {
        const label = (match[1] ?? '').trim();
        const href = match[2] ?? '';
        const index = match.index ?? 0;
        if (!isCurrentThreadChatLink(href)) {
          continue;
        }
        const exactArtifactId = resolveScopedArtifactIdByLabel(scopedArtifacts, label);
        if (exactArtifactId) {
          assignments.set(index, exactArtifactId);
          consumedArtifactIds.add(exactArtifactId);
          continue;
        }
        if (!genericArtifactLinkLabels.has(label.toLowerCase())) {
          continue;
        }
        const nextArtifact = scopedArtifacts.find((artifact) => !consumedArtifactIds.has(artifact.id));
        if (!nextArtifact) {
          continue;
        }
        assignments.set(index, nextArtifact.id);
        consumedArtifactIds.add(nextArtifact.id);
      }
      return assignments;
    },
    [getScopedArtifactsForEvent, isCurrentThreadChatLink, resolveScopedArtifactIdByLabel]
  );

  const resolveReferencedArtifactIds = useCallback(
    (
      text: string,
      event?: { runId?: string; artifacts?: Array<{ id: string; runId?: string; summary?: string; kind?: string; mimeType?: string }> }
    ): string[] => {
      if (!text.trim()) {
        return [];
      }
      const scopedArtifacts = getScopedArtifactsForEvent(event);
      const directLinkAssignments = resolveDirectChatLinkAssignments(text, event);
      const orderedIds: string[] = [];
      const seen = new Set<string>();
      const pushId = (artifactId: string | null) => {
        if (!artifactId || seen.has(artifactId)) {
          return;
        }
        seen.add(artifactId);
        orderedIds.push(artifactId);
      };

      for (const match of text.matchAll(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi)) {
        pushId(directLinkAssignments.get(match.index ?? 0) ?? null);
      }
      for (const match of text.matchAll(
        /(\b[A-Za-z][\w-]*)\s*\(artifact\s+(?:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|\[([^\]]+)\]\(([^)]+)\))\)/gi
      )) {
        const rawArtifactId = match[2];
        const linkedLabel = match[3];
        if (rawArtifactId && allArtifacts.some((artifact) => artifact.id === rawArtifactId)) {
          pushId(rawArtifactId);
          continue;
        }
        pushId(linkedLabel ? resolveScopedArtifactIdByLabel(scopedArtifacts, linkedLabel) : null);
      }
      for (const match of text.matchAll(uuidTokenPattern)) {
        const artifact = allArtifacts.find((item) => item.id === match[0]);
        pushId(artifact?.id ?? null);
      }
      return orderedIds;
    },
    [allArtifacts, getScopedArtifactsForEvent, resolveDirectChatLinkAssignments, resolveScopedArtifactIdByLabel]
  );

  const transformMarkdownText = useCallback(
    (text: string, event?: { runId?: string; artifacts?: Array<{ id: string; runId?: string; summary?: string; kind?: string; mimeType?: string }> }): string => {
      const scopedArtifacts = getScopedArtifactsForEvent(event);
      const directLinkAssignments = resolveDirectChatLinkAssignments(text, event);
      const textWithDirectArtifactLinks = text.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi, (match, label: string, _href: string, offset: number) => {
        const artifactId = directLinkAssignments.get(offset) ?? null;
        if (!artifactId) {
          return match;
        }
        return `[${label}](artifact://${encodeURIComponent(artifactId)})`;
      });

      const textWithInlineArtifactLinks = textWithDirectArtifactLinks.replace(
        /(\b[A-Za-z][\w-]*)\s*\(artifact\s+(?:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|\[([^\]]+)\]\(([^)]+)\))\)/gi,
        (match, anchorWord: string, rawArtifactId?: string, linkedLabel?: string) => {
          const artifactId =
            rawArtifactId && allArtifacts.some((artifact) => artifact.id === rawArtifactId)
              ? rawArtifactId
              : linkedLabel
                ? resolveScopedArtifactIdByLabel(scopedArtifacts, linkedLabel)
                : null;
          if (!artifactId) {
            return match;
          }
          return `[${anchorWord}](artifact://${encodeURIComponent(artifactId)})`;
        }
      );

      const markdownLinkTargetSpans = Array.from(textWithInlineArtifactLinks.matchAll(/\]\(([^)]*)\)/g)).map((match) => {
        const full = match[0] ?? '';
        const target = match[1] ?? '';
        const start = (match.index ?? 0) + full.indexOf(target);
        return { start, end: start + target.length };
      });

      return textWithInlineArtifactLinks.replace(uuidTokenPattern, (match, offset: number) => {
        if (markdownLinkTargetSpans.some((span) => offset >= span.start && offset < span.end)) {
          return match;
        }
        const artifact = allArtifacts.find((item) => item.id === match);
        if (artifact) {
          const label = sanitizeArtifactMarkdownLabel(artifact);
          return `[${label || 'artifact'}](artifact://${encodeURIComponent(match)})`;
        }
        const siteName = siteNameById[match.toLowerCase()] ?? siteNameById[match];
        if (siteName) {
          return `[${siteName}](${buildSiteHref(match.toLowerCase())})`;
        }
        return match;
      });
    },
    [allArtifacts, buildSiteHref, getScopedArtifactsForEvent, resolveDirectChatLinkAssignments, resolveScopedArtifactIdByLabel, sanitizeArtifactMarkdownLabel, siteNameById]
  );

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children }: { href?: string; children?: ReactNode }) => {
        const value = href ?? '';
        if (value.startsWith('artifact://')) {
          let artifactId = '';
          try {
            artifactId = decodeURIComponent(value.slice('artifact://'.length));
          } catch {
            return <>{children}</>;
          }
          const artifact = allArtifacts.find((item) => item.id === artifactId);
          if (!artifact) {
            return <>{children}</>;
          }
          return (
            <button type="button" className="chat-site-link" onClick={() => void openInspectorForArtifact(artifact)}>
              {children}
            </button>
          );
        }
        return (
          <a href={value} className="chat-site-link">
            {children}
          </a>
        );
      }
    }),
    [allArtifacts, openInspectorForArtifact]
  );

  const markdownUrlTransform = useCallback((url: string) => {
    if (url.startsWith('artifact://')) {
      return url;
    }
    return defaultUrlTransform(url);
  }, []);

  useEffect(() => {
    [...finalArtifacts, ...finalMessageArtifacts].forEach((artifact) => {
      const existing = artifactPreview[artifactStateKey(artifact)];
      if (!existing) {
        void handleLoadArtifactPreview(artifact);
      }
    });
  }, [artifactPreview, finalArtifacts, finalMessageArtifacts, handleLoadArtifactPreview]);

  useEffect(() => {
    const requestedIds = Object.keys(artifactPreviewReloadRequests);
    if (requestedIds.length === 0) {
      return;
    }
    const artifactsToReload = requestedIds
      .map((artifactId) => {
        if (inspectorArtifact && artifactStateKey(inspectorArtifact) === artifactId) {
          return inspectorArtifact;
        }
        return allArtifacts.find((artifact) => artifactStateKey(artifact) === artifactId) ?? null;
      })
      .filter((artifact): artifact is ChatArtifact => Boolean(artifact?.id));
    if (artifactsToReload.length === 0) {
      return;
    }
    setArtifactPreviewReloadRequests((prev) => {
      const next = { ...prev };
      artifactsToReload.forEach((artifact) => {
        delete next[artifactStateKey(artifact)];
      });
      return next;
    });
    artifactsToReload.forEach((artifact) => {
      void handleLoadArtifactPreview(artifact);
    });
  }, [allArtifacts, artifactPreviewReloadRequests, currentRunId, handleLoadArtifactPreview, inspectorArtifact]);

  const renderRunStatusBadge = useCallback(
    (event: ChatMessageEventWithDelay) => {
      if (!event.runId) {
        return null;
      }
      const hasAssistant = runMessagePresence[event.runId]?.hasAssistant ?? false;
      if (event.role !== 'assistant' && hasAssistant) {
        return null;
      }
      const summary = runStatusById[event.runId];
      let variant: 'pending' | 'success' | 'error' | 'neutral' = 'pending';
      let label: string | null = null;
      let detail: string | null = null;
      let showSpinner = false;
      let icon: string | null = null;

      if (summary?.terminal) {
        const normalized = summary.terminal.status?.toLowerCase();
        if (normalized === 'succeeded') {
          variant = 'success';
          label = 'Completed';
          icon = '✅';
          detail = summary.terminal.detail ?? null;
        } else if (normalized === 'failed') {
          variant = 'error';
          label = 'Failed';
          icon = '❌';
          detail = summary.terminal.detail ?? 'Run reported an error.';
        } else if (normalized === 'cancelled') {
          variant = 'neutral';
          label = 'Cancelled';
          icon = '⏹';
          detail = summary.terminal.detail ?? null;
        } else if (normalized) {
          variant = 'pending';
          label = normalized;
          icon = 'ℹ';
          detail = summary.terminal.detail ?? null;
        }
      } else if (summary?.running) {
        variant = 'pending';
        label = 'Running…';
        showSpinner = true;
        detail = summary.running.detail ?? null;
      } else if (summary?.queued) {
        variant = 'pending';
        label = 'Starting…';
        showSpinner = true;
        detail = summary.queued.detail ?? null;
      } else if (event.runId === streamingRunId) {
        variant = 'pending';
        label = 'Preparing…';
        showSpinner = true;
      } else {
        return null;
      }

      if (!label) {
        return null;
      }

      return (
        <div className={`chat-message__status chat-message__status--${variant}`}>
          {showSpinner ? (
            <span className="chat-message__status-spinner" aria-hidden="true" />
          ) : (
            <span className="chat-message__status-icon" aria-hidden="true">
              {icon ?? 'ℹ'}
            </span>
          )}
          <span className="chat-message__status-line">
            <span className="chat-message__status-label">{label}</span>
            {detail && <span className="chat-message__status-detail"> {detail}</span>}
          </span>
        </div>
      );
    },
    [runMessagePresence, runStatusById, streamingRunId]
  );

  const renderTopologyArtifact = useCallback(
    (
      artifact: ChatArtifact,
      topology: TopologyArtifactContent,
      surface: ChatArtifactSurface,
      options: { compact?: boolean; className?: string; artifactRep?: string } = {}
    ): JSX.Element => {
      const artifactKey = artifactStateKey(artifact);
      const selection = topologySelectionsByArtifactId[artifactKey];
      const saveState = artifactTopologySaveState[artifactKey];
      const summaryLines = saveState?.result ? topologyViewSaveSummaryLines(saveState.result) : [];
      const wrapperClassName = ['chat-topology-artifact', options.className].filter(Boolean).join(' ');
      return (
        <div className={wrapperClassName}>
          <div className="chat-topology-artifact__toolbar">
            <div className="chat-topology-artifact__toolbar-meta">
              <span className="chat-topology-artifact__toolbar-title">
                {textValue(topology.title) || textValue(artifact.summary) || 'Topology artifact'}
              </span>
              {saveState?.result?.view?.viewId && (
                <span className="chat-topology-artifact__toolbar-detail">Saved view {saveState.result.view.viewId}</span>
              )}
            </div>
            <div className="chat-topology-artifact__toolbar-actions">
              <button
                type="button"
                className="chat-timeline__artifact-button"
                onClick={() =>
                  saveState?.open ? closeArtifactTopologySave(artifactKey) : openArtifactTopologySave(artifact, topology)
                }
              >
                {saveState?.open ? 'Hide save panel' : 'Save as custom view'}
              </button>
              {saveState?.result?.view?.viewId && (
                <button
                  type="button"
                  className="chat-timeline__artifact-button"
                  onClick={() => handleOpenSavedTopologyView(saveState.result!, topology)}
                >
                  Open saved view
                </button>
              )}
            </div>
          </div>

          {saveState?.open && (
            <div className="chat-topology-artifact__save-panel">
              <div className="chat-topology-artifact__save-grid">
                <label className="form-field">
                  <span className="form-field__label">Saved view name</span>
                  <input
                    type="text"
                    className="input"
                    value={saveState.name}
                    onChange={(event) =>
                      patchArtifactTopologySaveState(artifactKey, {
                        name: event.target.value,
                        error: null,
                        result: null
                      })
                    }
                    placeholder="Enter a name"
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Save mode</span>
                  <select
                    className="select"
                    value={saveState.saveMode}
                    onChange={(event) =>
                      patchArtifactTopologySaveState(artifactKey, {
                        saveMode: event.target.value as TopologyV2ViewScopeModeschema,
                        error: null,
                        result: null
                      })
                    }
                  >
                    <option value="reusable">Reusable</option>
                    <option value="current_scope">Current scope</option>
                  </select>
                </label>
                <label className="form-field chat-topology-artifact__save-grid-wide">
                  <span className="form-field__label">Description</span>
                  <textarea
                    className="textarea"
                    value={saveState.description}
                    onChange={(event) =>
                      patchArtifactTopologySaveState(artifactKey, {
                        description: event.target.value,
                        error: null,
                        result: null
                      })
                    }
                    rows={2}
                    placeholder="Optional description"
                  />
                </label>
              </div>

              <div className="chat-topology-artifact__save-actions">
                <button
                  type="button"
                  className="button"
                  onClick={() => void handleCreateTopologyArtifactView(artifact, topology, options.artifactRep)}
                  disabled={saveState.busy || !saveState.name.trim()}
                >
                  {saveState.busy ? 'Saving…' : 'Create saved view'}
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => closeArtifactTopologySave(artifactKey)}
                  disabled={saveState.busy}
                >
                  Dismiss
                </button>
              </div>

              {saveState.error && <div className="chat-topology-artifact__save-error">{saveState.error}</div>}
              {saveState.result && (
                <div className="chat-topology-artifact__save-feedback">
                  {summaryLines.length > 0 && (
                    <div className="chat-topology-artifact__save-summary">
                      {summaryLines.map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </div>
                  )}
                  {(saveState.result.warnings ?? []).length > 0 && (
                    <div className="chat-topology-artifact__save-warnings">
                      {(saveState.result.warnings ?? []).map((warning) => (
                        <div key={`${warning.code}:${warning.layerId ?? 'global'}`} className="chat-topology-artifact__save-warning">
                          <strong>{warning.code}</strong>: {warning.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <TopologyArtifactView
            artifactId={artifact.id}
            surface={surface}
            artifactUpdates={topologyArtifactUpdatesByArtifactId[artifact.id] ?? []}
            selectedNodeIds={new Set(selection?.nodeIds ?? [])}
            selectedEdgeIds={new Set(selection?.edgeIds ?? [])}
            onActivate={({ artifactId, topology: activeTopology, surface: activeSurface, overlay }) =>
              activateTopologyArtifact(artifactId, activeTopology, activeSurface, overlay)
            }
            onOverlayStateChange={rememberTopologyOverlaySnapshot}
            onNodeSelect={(event, nodeId) => {
              activateTopologyArtifact(artifact.id, topology, surface);
              updateTopologySelection(artifact.id, event, 'node', nodeId);
            }}
            onEdgeSelect={(event, edgeId) => {
              activateTopologyArtifact(artifact.id, topology, surface);
              updateTopologySelection(artifact.id, event, 'edge', edgeId);
            }}
            onPaneClick={() => {
              activateTopologyArtifact(artifact.id, topology, surface);
              clearTopologySelection(artifact.id);
            }}
            autoActivate
            topology={topology}
            resolveTopologyView={resolveTopologyArtifactView}
            streamTopologyView={streamTopologyArtifactView}
            streamTopologyArtifact={streamTopologyArtifact}
            compact={options.compact}
          />
        </div>
      );
    },
    [
      activateTopologyArtifact,
      artifactTopologySaveState,
      clearTopologySelection,
      closeArtifactTopologySave,
      handleCreateTopologyArtifactView,
      handleOpenSavedTopologyView,
      openArtifactTopologySave,
      patchArtifactTopologySaveState,
      rememberTopologyOverlaySnapshot,
      resolveTopologyArtifactView,
      streamTopologyArtifact,
      streamTopologyArtifactView,
      topologyArtifactUpdatesByArtifactId,
      topologySelectionsByArtifactId,
      updateTopologySelection
    ]
  );

  const renderInlineArtifactPreview = useCallback(
    (artifact: ChatArtifact): ReactNode => {
      const artifactKey = artifactStateKey(artifact);
      const preview = artifactPreview[artifactKey];
      if (preview?.loading) {
        return <p className="chat-message__inline-loading">Loading artifact…</p>;
      }
      if (!preview || preview.error) {
        return null;
      }

      const doc: any = preview.doc;
      const viewOptions: Array<{ rep: string }> = Array.isArray(doc?.views)
        ? (doc.views.filter((v: any) => v && typeof v.rep === 'string' && v.rep.trim()) as Array<{ rep: string }>)
        : [];
      const fallbackRep = doc?.presentationHints?.defaultView ?? (viewOptions.length > 0 ? viewOptions[0].rep : undefined);
      const activeRep = artifactViewReps[artifactKey] ?? fallbackRep;
      const resolvedView =
        activeRep && Array.isArray(doc?.views) ? doc.views.find((v: any) => v && v.rep === activeRep) : undefined;
      const docContent: any = resolvedView?.content ?? doc?.content;
      const mimeHint = (artifact.mimeType ?? '').toLowerCase();

      if (mimeHint.startsWith('image/')) {
        return <img className="chat-message__inline-image" src={preview.content} alt={artifact.summary ?? 'Image artifact'} />;
      }
      if (docContent?.type === 'chart') {
        return (
          <ChartArtifactView
            className="chat-message__inline-chart"
            chart={docContent}
            fetchArtifactDoc={fetchArtifactDocumentById}
            queryMetricRange={queryMetricRange}
            fullTables={false}
          />
        );
      }
      if (isTopologyArtifactContent(docContent)) {
        return renderTopologyArtifact(artifact, docContent as TopologyArtifactContent, ChatActiveArtifactContextschemaSurfaceEnum.ChatCard, {
          className: 'chat-message__inline-topology',
          compact: true,
          artifactRep: activeRep
        });
      }
      if ((docContent as any)?.type === 'table') {
        const rows = Array.isArray((docContent as any).rows) ? ((docContent as any).rows as Record<string, unknown>[]) : [];
        const columns = Array.isArray((docContent as any).columns)
          ? (((docContent as any).columns).filter((c: unknown) => typeof c === 'string') as string[])
          : inferTableColumns(rows);
        const evidence = Array.isArray(artifact.evidence) ? artifact.evidence : [];
        const rowKeys = Array.isArray((docContent as any).rowKeys)
          ? (((docContent as any).rowKeys).filter((k: unknown) => typeof k === 'string') as string[])
          : [];
        const highlighted = new Set(
          evidence
            .filter((ev): ev is EvidencePointer => ev?.kind === 'table' && Array.isArray(ev.rowKeys) && ev.rowKeys.length > 0)
            .flatMap((ev) => ev.rowKeys ?? [])
            .filter((key): key is string => typeof key === 'string' && key.trim().length > 0)
        );
        const visibleRows = rows.slice(0, 5);
        return (
          <div className="chat-message__inline-table">
            <div className="chat-message__inline-table-wrap">
              <table>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={`${artifact.id}-inline-col-${col}`}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row, idx) => {
                    const rowKey = idx >= 0 && idx < rowKeys.length ? rowKeys[idx] : '';
                    const isHL = rowKey && highlighted.has(rowKey);
                    return (
                      <tr
                        key={`${artifact.id}-inline-row-${idx}`}
                        data-row-key={rowKey || undefined}
                        className={isHL ? 'chat-message__inline-table-row--highlight' : ''}>
                        {columns.map((col) => (
                          <td key={`${artifact.id}-inline-row-${idx}-col-${col}`}>{renderTextWithSiteLinks(String(row[col] ?? ''))}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
      if ((docContent as any)?.type === 'commandOutput' || String(artifact.kind ?? '').toLowerCase() === 'commandoutput') {
        return (
          <pre className="chat-message__inline-terminal">
            {renderHighlightedPreview(artifactKey, preview.content)}
          </pre>
        );
      }
      return null;
    },
    [
      artifactPreview,
      artifactViewReps,
      fetchArtifactDocumentById,
      queryMetricRange,
      renderTextWithSiteLinks,
      renderHighlightedPreview,
      renderTopologyArtifact
    ]
  );

  const renderSupportingArtifactPreview = useCallback(
    (artifact: ChatArtifact): ReactNode => {
      const artifactKey = artifactStateKey(artifact);
      const preview = artifactPreview[artifactKey];
      if (preview?.loading) {
        return <p className="chat-message__supporting-loading">Loading artifact…</p>;
      }
      if (!preview || preview.error) {
        return null;
      }

      const doc: any = preview.doc;
      const viewOptions: Array<{ rep: string }> = Array.isArray(doc?.views)
        ? (doc.views.filter((v: any) => v && typeof v.rep === 'string' && v.rep.trim()) as Array<{ rep: string }>)
        : [];
      const fallbackRep = doc?.presentationHints?.defaultView ?? (viewOptions.length > 0 ? viewOptions[0].rep : undefined);
      const activeRep = artifactViewReps[artifactKey] ?? fallbackRep;
      const resolvedView =
        activeRep && Array.isArray(doc?.views) ? doc.views.find((v: any) => v && v.rep === activeRep) : undefined;
      const docContent: any = resolvedView?.content ?? doc?.content;
      const mimeHint = (artifact.mimeType ?? '').toLowerCase();
      const evidence = Array.isArray(artifact.evidence) ? artifact.evidence : [];

      if (mimeHint.startsWith('image/')) {
        return <img className="chat-message__supporting-image" src={preview.content} alt={artifact.summary ?? 'Image artifact'} />;
      }

      if ((docContent as any)?.type === 'table') {
        const rows = Array.isArray((docContent as any).rows) ? ((docContent as any).rows as Record<string, unknown>[]) : [];
        const columns = Array.isArray((docContent as any).columns)
          ? (((docContent as any).columns).filter((c: unknown) => typeof c === 'string') as string[])
          : inferTableColumns(rows);
        const rowKeys = Array.isArray(docContent.rowKeys)
          ? (docContent.rowKeys.filter((k: unknown) => typeof k === 'string') as string[])
          : [];
        const evidenceKeys = evidence
          .filter((ev): ev is EvidencePointer => ev?.kind === 'table' && Array.isArray(ev.rowKeys) && ev.rowKeys.length > 0)
          .flatMap((ev) => ev.rowKeys ?? [])
          .filter((key): key is string => typeof key === 'string' && key.trim().length > 0);
        const selectedEntries =
          evidenceKeys.length > 0 && rowKeys.length === rows.length
            ? rows
                .map((row, idx) => ({ row, rowKey: rowKeys[idx] ?? '' }))
                .filter((entry) => evidenceKeys.includes(entry.rowKey))
            : rows.map((row, idx) => ({ row, rowKey: rowKeys[idx] ?? '' }));
        const visibleEntries = selectedEntries.slice(0, evidenceKeys.length > 0 ? Math.max(1, Math.min(5, evidenceKeys.length)) : 3);
        return (
          <div className="chat-message__supporting-table">
            <div className="chat-message__supporting-table-wrap">
              <table>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={`${artifact.id}-supporting-col-${col}`}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleEntries.map(({ row, rowKey }, idx) => {
                    const isHL = rowKey && evidenceKeys.includes(rowKey);
                    return (
                      <tr
                        key={`${artifact.id}-supporting-row-${idx}`}
                        data-row-key={rowKey || undefined}
                        className={isHL ? 'chat-message__supporting-table-row--highlight' : ''}>
                        {columns.map((col) => (
                          <td key={`${artifact.id}-supporting-row-${idx}-col-${col}`}>{renderTextWithSiteLinks(String(row[col] ?? ''))}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      if ((docContent as any)?.type === 'commandOutput' || String(artifact.kind ?? '').toLowerCase() === 'commandoutput') {
        const lines = preview.content.split('\n');
        const rawEvidence = evidence.find(
          (ev): ev is EvidencePointer => ev?.kind === 'raw' && typeof ev.startLine === 'number' && typeof ev.endLine === 'number'
        );
        const snippet =
          rawEvidence != null
            ? (() => {
                const startLine = rawEvidence.startLine as number;
                const endLine = rawEvidence.endLine as number;
                return lines.slice(Math.max(0, startLine - 1), Math.max(startLine, endLine));
              })()
            : lines.slice(0, 8);
        return <pre className="chat-message__supporting-terminal">{snippet.join('\n')}</pre>;
      }

      if (isChartContent(docContent)) {
        return (
          <div className="chat-message__supporting-chart-hint">
            Chart. Open to inspect or expand.
          </div>
        );
      }

      if (isTopologyArtifactContent(docContent)) {
        return (
          <div className="chat-message__supporting-chart-hint">
            Topology. Open to inspect or expand.
          </div>
        );
      }

      return null;
    },
    [artifactPreview, artifactViewReps, renderTextWithSiteLinks]
  );

  const renderMessageContent = useCallback(
    (event: ChatMessageEventWithDelay) => {
      return (
        <ChatMessageContentBlocks
          event={event}
          answeredPromptKeys={promptAnswerState.answeredPromptKeys}
          supersededPromptKeys={promptAnswerState.supersededPromptKeys}
          latestResponsesByPromptKey={promptAnswerState.latestResponsesByPromptKey}
          promptChoiceDrafts={promptChoiceDrafts}
          promptInputDrafts={promptInputDrafts}
          promptFormDrafts={promptFormDrafts}
          promptSubmissionState={promptSubmissionState}
          setPromptChoiceDrafts={setPromptChoiceDrafts}
          setPromptInputDrafts={setPromptInputDrafts}
          setPromptFormDrafts={setPromptFormDrafts}
          submitSingleChoicePrompt={submitSingleChoicePrompt}
          submitChoicePromptDraft={submitChoicePromptDraft}
          submitConfirmationPrompt={submitConfirmationPrompt}
          submitInputPrompt={submitInputPrompt}
          submitFormPrompt={submitFormPrompt}
          transformMarkdownText={transformMarkdownText}
          markdownComponents={markdownComponents}
          markdownUrlTransform={markdownUrlTransform}
        />
      );
    },
    [
      promptAnswerState,
      markdownComponents,
      promptChoiceDrafts,
      promptFormDrafts,
      promptInputDrafts,
      promptSubmissionState,
      submitChoicePromptDraft,
      submitConfirmationPrompt,
      submitFormPrompt,
      submitInputPrompt,
      submitSingleChoicePrompt,
      transformMarkdownText,
      markdownUrlTransform
    ]
  );

	  const renderMessageBubble = useCallback(
	    (event: ChatMessageEventWithDelay, options?: { artifactVisibility?: 'all' | 'final-only' | 'none' }) => {
	      const artifactVisibility = options?.artifactVisibility ?? 'all';
	      const suppressArtifacts = artifactVisibility === 'none';
	      const collapseInlineArtifacts = artifactVisibility === 'final-only';
	      const showRunMetaControls = chatViewMode !== 'user';
	      const feedback = messageFeedback[event.id];
	      const durationLabel = responseDuration ? formatDuration(responseDuration) : null;
	      const statusBadge = renderRunStatusBadge(event);
	      const hasVisibleMessageContent =
	        (event.contentBlocks?.length ?? 0) > 0 || (event.text ?? '').trim().length > 0 || (event.reactions?.length ?? 0) > 0;
      const fallbackRunId = fallbackArtifactRunByMessageId[event.id];
      const messageArtifacts =
        artifactVisibility === 'final-only'
          ? event.artifacts && event.artifacts.length > 0
            ? event.artifacts
            : []
          : event.artifacts && event.artifacts.length > 0
            ? event.artifacts
            : fallbackRunId
              ? artifactsByRun[fallbackRunId] ?? []
              : [];
	      const resolvedArtifacts = messageArtifacts.map((artifact) => {
	        const runId = event.runId ?? artifact.runId ?? fallbackRunId ?? currentRunId ?? null;
	        const fromRun = runId ? (artifactsByRun[runId] ?? []).find((item) => item.id === artifact.id) : undefined;
	        const fromAny = findArtifactRecord(allArtifacts, artifact.id, runId);
	        const full = fromRun ?? fromAny;
        if (!full) {
          return artifact;
        }
        return {
          ...full,
          summary: pickArtifactSummary(artifact.summary ?? undefined, full.summary ?? undefined),
	          evidence: artifact.evidence ?? full.evidence
	        };
	      });
	      const referencedArtifactIds = resolveReferencedArtifactIds(event.text, {
	        runId: event.runId,
	        artifacts: event.artifacts
	      });
	      const referencedArtifactOrder = new Map(referencedArtifactIds.map((artifactId, index) => [artifactId, index]));
	      const prioritizedArtifacts =
	        referencedArtifactOrder.size === 0
	          ? resolvedArtifacts
	          : [...resolvedArtifacts].sort((left, right) => {
	              const leftOrder = referencedArtifactOrder.get(left.id);
	              const rightOrder = referencedArtifactOrder.get(right.id);
	              if (leftOrder == null && rightOrder == null) {
	                return 0;
	              }
	              if (leftOrder == null) {
	                return 1;
	              }
	              if (rightOrder == null) {
	                return -1;
	              }
	              return leftOrder - rightOrder;
	            });
	      const isCommandOutputArtifact = (artifact: ChatArtifact) => String(artifact.kind ?? '').toLowerCase() === 'commandoutput';
	      const primaryInlineArtifact =
	        event.role === 'assistant'
	          ? prioritizedArtifacts.find((artifact) => {
	              const preview = artifactPreview[artifactStateKey(artifact)];
	              const docContent: any = preview?.doc?.content;
	              const mimeHint = (artifact.mimeType ?? '').toLowerCase();
	              if (mimeHint.startsWith('image/')) {
	                return true;
              }
              if (
                isChartContent(docContent) ||
                isChartArtifact(artifact) ||
                isTopologyArtifactContent(docContent) ||
                isTopologyArtifact(artifact)
              ) {
                return true;
	              }
	              return false;
	            }) ??
	            prioritizedArtifacts.find((artifact) => {
	              const preview = artifactPreview[artifactStateKey(artifact)];
	              const docContent: any = preview?.doc?.content;
	              return docContent?.type === 'table' || (docContent as any)?.type === 'commandOutput' || isCommandOutputArtifact(artifact);
	            })
	          : undefined;
	      const supportingInlineArtifacts =
	        event.role === 'assistant' && primaryInlineArtifact
	          ? prioritizedArtifacts.filter(
	              (artifact) =>
	                artifactIdentityKey(artifact.id, artifact.runId) !==
	                artifactIdentityKey(primaryInlineArtifact.id, primaryInlineArtifact.runId)
	            )
	          : [];
      const inlineArtifactKeys = new Set(
        [primaryInlineArtifact, ...supportingInlineArtifacts]
          .filter((artifact): artifact is ChatArtifact => Boolean(artifact?.id))
          .map((artifact) => artifactIdentityKey(artifact.id, artifact.runId))
      );
	      const listedArtifacts =
	        (
	          artifactVisibility === 'final-only'
	            ? prioritizedArtifacts.filter((artifact) => !isCommandOutputArtifact(artifact))
	            : prioritizedArtifacts
	        ).filter((artifact) => !(collapseInlineArtifacts && inlineArtifactKeys.has(artifactIdentityKey(artifact.id, artifact.runId))));
      const fileAttachments = listedArtifacts.filter((artifact) => isFileArtifactKind(artifact.kind));
      const generatedArtifacts = listedArtifacts.filter(
        (artifact) => !isCommandOutputArtifact(artifact) && !isFileArtifactKind(artifact.kind)
      );
      const artifactGroups = [
        { key: 'attachments', label: 'Attachments', artifacts: fileAttachments },
        { key: 'generated', label: 'Generated artifacts', artifacts: generatedArtifacts }
      ].filter((group) => group.artifacts.length > 0);
      const showArtifactGroups = artifactGroups.length > 1;
      const renderInlineArtifactSection = (artifact: ChatArtifact, previewNode: ReactNode, className: string, key?: string) => (
        <div key={key ?? artifactStateKey(artifact)} className={className}>
          <div className="chat-message__supporting-title">{renderArtifactTitleWithSiteLinks(artifact)}</div>
          {previewNode}
        </div>
      );
      const inlineArtifactSections: JSX.Element[] = [];
      if (!suppressArtifacts && primaryInlineArtifact) {
        inlineArtifactSections.push(
          renderInlineArtifactSection(
            primaryInlineArtifact,
            renderInlineArtifactPreview(primaryInlineArtifact),
            'chat-message__inline-artifact'
          )
        );
      }
      if (!suppressArtifacts && supportingInlineArtifacts.length > 0) {
        supportingInlineArtifacts.forEach((artifact) => {
          const previewNode = renderSupportingArtifactPreview(artifact);
          if (!previewNode) {
            return;
          }
          inlineArtifactSections.push(
            renderInlineArtifactSection(
              artifact,
              previewNode,
              'chat-message__supporting-card',
              `${event.id}-supporting-${artifactIdentityKey(artifact.id, artifact.runId)}`
            )
          );
        });
      }
      const hasRenderableArtifacts = !suppressArtifacts && (inlineArtifactSections.length > 0 || listedArtifacts.length > 0);
      const outputsExpanded = messageOutputsExpanded[event.id] ?? false;
      if (!hasVisibleMessageContent && !hasRenderableArtifacts) {
        return null;
      }
      return (
        <div
          key={event.id}
          className={`chat-message chat-message--${event.role === 'assistant' ? 'assistant' : 'user'}`}>
          <div className="chat-message__body">
	            <div className="chat-message__meta">
	              <span>{event.role === 'assistant' ? 'REAP' : 'You'}</span>
	              <div className="chat-message__meta-right">
	                {statusBadge}
	                {showRunMetaControls && event.runId && !event.isLivePreview && (
	                  <>
	                    <button
                      type="button"
                      className={`chat-message__runid-copy ${copiedRunId === event.runId ? 'chat-message__runid-copy--copied' : ''}`}
                      onClick={() => void handleCopyRunId(event.runId!)}
                      aria-label="Copy run ID"
                      title={copiedRunId === event.runId ? 'Copied run ID' : `Copy run ID: ${event.runId}`}>
                      {copiedRunId === event.runId ? '✓' : '⧉'}
                    </button>
                    {openAiLogsEnabled && (
                      <a
                        className="chat-message__meta-link"
                        href={buildOpenAiResponsesLogsUrl(event.runId)}
                        target="_blank"
                        rel="noreferrer"
                        title={`Open OpenAI logs for run ID: ${event.runId}`}
                        onClick={(clickEvent) => clickEvent.stopPropagation()}
                      >
                        Logs
                      </a>
                    )}
                  </>
                )}
                {event.role === 'assistant' && !event.isLivePreview && durationLabel && (
                  <span className="chat-message__duration">🕒 {durationLabel}</span>
                )}
                {event.isLivePreview && <span className="chat-message__duration">Working…</span>}
                <time>{formatDateTime(event.timestamp)}</time>
              </div>
            </div>
            <div className="chat-message__content">
              {renderMessageContent(event)}
            </div>
            {!suppressArtifacts && inlineArtifactSections.length > 0 && (
              collapseInlineArtifacts ? (
                <div className="chat-message__outputs-group">
                  <button
                    type="button"
                    className="chat-message__artifact-toggle"
                    onClick={() => handleToggleMessageOutputs(event.id)}
                    aria-expanded={outputsExpanded}>
                    <span className="chat-message__artifact-toggle-arrow" aria-hidden="true">
                      {outputsExpanded ? '▼' : '▶'}
                    </span>
                    <span className="chat-message__artifact-toggle-label">Outputs</span>
                  </button>
                  {outputsExpanded && <div className="chat-message__supporting-artifacts">{inlineArtifactSections}</div>}
                </div>
              ) : (
                <>
                  {inlineArtifactSections[0] ?? null}
                  {inlineArtifactSections.length > 1 && (
                    <div className="chat-message__supporting-artifacts">{inlineArtifactSections.slice(1)}</div>
                  )}
                </>
              )
            )}
            {!suppressArtifacts && listedArtifacts.length > 0 && (
              <div className="chat-message__artifacts">
                {(showArtifactGroups ? artifactGroups : [{ key: 'all', label: null, artifacts: listedArtifacts }]).map((group) => (
                  <div key={`artifact-group-${group.key}`} className="chat-message__artifact-group">
                    {group.label && <div className="chat-message__artifact-group-label">{group.label}</div>}
                    <div className="chat-message__artifact-group-list">
                      {group.artifacts.map((artifact) => (
                        <div key={artifactIdentityKey(artifact.id, artifact.runId)} className="chat-message__artifact-wrap">
                          <button
                            type="button"
                            className="chat-message__artifact"
                            onClick={() => void openInspectorForArtifact(artifact)}>
                            {renderArtifactTitleWithSiteLinks(artifact)}
                          </button>
                          {artifact.evidence && artifact.evidence.length > 0 && (
                            <div className="chat-message__evidence">
                              <div className="chat-message__evidence-title">Evidence</div>
                              <ul className="chat-message__evidence-list">
                                {artifact.evidence.map((ev, idx) => {
                                  const label =
                                    ev.kind === 'raw' &&
                                    typeof ev.startLine === 'number' &&
                                    typeof ev.endLine === 'number'
                                      ? `raw lines ${ev.startLine}-${ev.endLine}`
                                      : ev.kind === 'table' && Array.isArray(ev.rowKeys) && ev.rowKeys.length > 0
                                        ? `table rows (${ev.rowKeys.length})`
                                        : `${ev.kind} (${ev.rep})`;
                                  return (
                                    <li key={`${artifact.id}-evidence-${idx}`}>
                                      <button
                                        type="button"
                                        className="chat-message__evidence-pill"
                                        onClick={() => {
                                          void openInspectorForArtifact(artifact, ev);
                                        }}
                                        title={ev.note ?? ''}>
                                        {label}
                                      </button>
                                      {ev.note && <span className="chat-message__evidence-note">{ev.note}</span>}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {event.reactions && event.reactions.length > 0 && (
              <div className="chat-message__reactions">
                {event.reactions.map((reaction, index) => (
                  <span key={`${event.id}-reaction-${index}`}>{reaction}</span>
                ))}
              </div>
            )}
	            {event.role === 'assistant' && !event.isLivePreview && (
	              <div className="chat-message__actions chat-message__actions--right">
                <button
                  type="button"
                  className={`chat-message__action ${feedback === 'liked' ? 'chat-message__action--active' : ''}`}
                  onClick={() => toggleMessageFeedback(event, 'liked')}>
                  👍
                </button>
                <button
                  type="button"
                  className={`chat-message__action ${feedback === 'disliked' ? 'chat-message__action--active' : ''}`}
                  onClick={() => toggleMessageFeedback(event, 'disliked')}>
                  👎
                </button>
              </div>
            )}
          </div>
        </div>
      );
    },
	    [
	      allArtifacts,
	      artifactsByRun,
	      artifactPreview,
	      chatViewMode,
	      copiedRunId,
      currentRunId,
      fallbackArtifactRunByMessageId,
      handleToggleMessageOutputs,
      handleCopyRunId,
      messageFeedback,
      messageOutputsExpanded,
	      openInspectorForArtifact,
	      renderArtifactTitleWithSiteLinks,
	      renderInlineArtifactPreview,
	      renderMessageContent,
	      resolveReferencedArtifactIds,
	      renderSupportingArtifactPreview,
	      renderRunStatusBadge,
	      responseDuration,
	      toggleMessageFeedback
    ]
  );

  const renderApprovalPrompt = useCallback(
    (event: ChatProgressEvent) => {
      const approval = extractApprovalMeta(event);
      if (!approval || !event.runId) {
        return null;
      }
      const decisionKey = approvalEventKey(event.runId, approval.approvalKey);
      const approvalState = approvalStateByKey[decisionKey]?.state ?? 'idle';
      const approvalError = approvalStateByKey[decisionKey]?.error ?? null;
      const approving = approvalState === 'submitting';
      return (
        <div className="chat-message chat-message--assistant chat-message--approval">
          <div className="chat-message__body">
            <div className="chat-message__meta">
              <span>REAP</span>
              <div className="chat-message__meta-right">
                <span className="chat-message__duration">Approval required</span>
                <time>{formatDateTime(event.timestamp)}</time>
              </div>
            </div>
            <div className="chat-message__content">
              <strong>{approval.title ?? 'Approve this action?'}</strong>
              {approval.changeValidationPlan ? (
                <ChangeValidationPlanCard artifact={approval.changeValidationPlan} />
              ) : (
                <p>{approval.description ?? 'The agent needs your approval before continuing.'}</p>
              )}
              {approval.riskLevel && <p>Risk level: {approval.riskLevel}</p>}
            </div>
            <div className="chat-message__actions chat-message__actions--right">
              <button
                type="button"
                className={`chat-message__action ${approvalState === 'approved' ? 'chat-message__action--active' : ''}`}
                disabled={approving || approvalState === 'approved'}
                onClick={() => void submitApprovalDecision(event.runId!, approval.approvalKey, true)}>
                {approving ? 'Submitting…' : approvalState === 'approved' ? 'Approved' : 'Approve'}
              </button>
              <button
                type="button"
                className={`chat-message__action ${approvalState === 'rejected' ? 'chat-message__action--active' : ''}`}
                disabled={approving || approvalState === 'rejected'}
                onClick={() => void submitApprovalDecision(event.runId!, approval.approvalKey, false)}>
                {approving ? 'Submitting…' : approvalState === 'rejected' ? 'Rejected' : 'Reject'}
              </button>
            </div>
            {approvalError && <div className="chat-message__status-detail">{approvalError}</div>}
          </div>
        </div>
      );
    },
    [approvalStateByKey, submitApprovalDecision]
  );

  const renderUserProgressStream = useCallback(
    (runId?: string | null) => {
      if (!runId) {
        return null;
      }
      const entries = buildUserProgressEntries(runEventHistory[runId] ?? []);
      if (entries.length === 0) {
        return null;
      }
      const latest = entries[entries.length - 1];
      const history = entries.slice(0, -1).reverse();
      return (
        <section key={`user-progress-${runId}`} className="chat-user-progress" aria-label="Run progress">
          <div className="chat-user-progress__header">
            <span className="chat-user-progress__label">Progress</span>
            <time>{formatDateTime(latest.timestamp)}</time>
          </div>
          <div className={`chat-user-progress__latest${latest.terminal ? ' chat-user-progress__latest--terminal' : ''}`}>
            <strong>{latest.label}</strong>
            {latest.detail && <span>{latest.detail}</span>}
          </div>
          {history.length > 0 && (
            <details className="chat-user-progress__history">
              <summary>History ({history.length})</summary>
              <div className="chat-user-progress__history-list">
                {history.map((entry) => (
                  <div key={entry.id} className="chat-user-progress__history-item">
                    <div className="chat-user-progress__history-top">
                      <strong>{entry.label}</strong>
                      <time>{formatDateTime(entry.timestamp)}</time>
                    </div>
                    {entry.detail && <div>{entry.detail}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      );
    },
    [runEventHistory]
  );

  const renderSseNote = useCallback((event: ChatSseEvent) => {
    let label = '';
    let detail: React.ReactNode = null;
    let icon = 'ℹ';
    let artifactActions: JSX.Element | null = null;
    switch (event.type) {
      case 'status': {
        const normalized = event.status?.toLowerCase();
        if (normalized === 'succeeded') {
          icon = '✅';
          label = 'Completed';
        } else if (normalized === 'failed' || normalized === 'cancelled') {
          icon = '❌';
          label = normalized === 'cancelled' ? 'Cancelled' : 'Failed';
        } else if (normalized === 'running') {
          icon = '🚦';
          label = 'Running';
        } else if (normalized === 'queued') {
          icon = '🚦';
          label = 'Queued';
        } else {
          icon = '🚦';
          label = event.status ? event.status : 'Status update';
        }
        detail = event.detail ?? null;
        break;
      }
      case 'progress':
        icon = '⏳';
        label = event.stage;
        detail = event.detail ?? null;
        if (!detail && typeof event.percent === 'number') {
          detail = `Progress ${event.percent}%`;
        }
        if (event.runId) {
          const approval = extractApprovalMeta(event);
          if (approval) {
            const decisionKey = approvalEventKey(event.runId, approval.approvalKey);
            const approvalState = approvalStateByKey[decisionKey]?.state ?? 'idle';
            const approvalError = approvalStateByKey[decisionKey]?.error ?? null;
            label = approval.title ?? 'Approval required';
            detail = (
              <div className="chat-approval-request">
                {approval.changeValidationPlan ? (
                  <ChangeValidationPlanCard artifact={approval.changeValidationPlan} />
                ) : (
                  <p>{approval.description ?? 'This action requires your approval before the agent can continue.'}</p>
                )}
                {approval.riskLevel && <p>Risk level: {approval.riskLevel}</p>}
                <div className="chat-approval-request__actions">
                  <button
                    type="button"
                    className="chat-timeline__artifact-button"
                    disabled={approvalState === 'submitting' || approvalState === 'approved'}
                    onClick={() => void submitApprovalDecision(event.runId!, approval.approvalKey, true)}>
                    {approvalState === 'approved' ? 'Approved' : approvalState === 'submitting' ? 'Submitting…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="chat-timeline__artifact-button"
                    disabled={approvalState === 'submitting' || approvalState === 'rejected'}
                    onClick={() => void submitApprovalDecision(event.runId!, approval.approvalKey, false)}>
                    {approvalState === 'rejected' ? 'Rejected' : approvalState === 'submitting' ? 'Submitting…' : 'Reject'}
                  </button>
                </div>
                {approvalError && <p className="chat-timeline__artifact-error">{approvalError}</p>}
              </div>
            );
          }
        }
        break;
      case 'artifact': {
        icon = '📎';
        label = event.kind;
        detail = event.summary ?? null;
        const runId = event.runId ?? currentRunId ?? null;
        const artifacts = runId ? artifactsByRun[runId] ?? [] : currentRunArtifacts;
	        const target = artifacts.find((a) => a.id === (event.artifactId ?? event.id));
	        if (target) {
	          const targetKey = artifactStateKey(target);
	          const preview = artifactPreview[targetKey];
	          const expanded = artifactExpanded[targetKey] ?? false;
	          let previewContent: JSX.Element | null = null;
	          if (preview?.loading) {
	            previewContent = <p>Loading artifact…</p>;
	          } else if (preview?.error) {
	            previewContent = <p className="chat-timeline__artifact-error">{preview.error}</p>;
	          } else if (preview && expanded) {
            const docAny: any = preview.doc;
            const selectedRep =
              artifactViewReps[targetKey] ??
              docAny?.presentationHints?.defaultView ??
              (Array.isArray(docAny?.views) && docAny.views.length > 0 ? docAny.views[0]?.rep : undefined);
            const resolvedView =
              selectedRep && Array.isArray(docAny?.views) ? docAny.views.find((v: any) => v && v.rep === selectedRep) : undefined;
            const docContent: any = resolvedView?.content ?? docAny?.content;
            const mimeHint = (target.mimeType ?? '').toLowerCase();
            if (mimeHint.startsWith('image/')) {
              previewContent = (
                <div style={{ marginTop: '0.75rem' }}>
                  <img src={preview.content} alt={target.summary ?? 'Image artifact'} style={{ maxWidth: '100%' }} />
                </div>
              );
            } else if (mimeHint === 'application/pdf') {
              previewContent = (
                <div style={{ marginTop: '0.75rem' }}>
                  <iframe title="PDF preview" src={preview.content} style={{ width: '100%', height: '50vh' }} />
                </div>
              );
            } else if (docContent?.type === 'chart') {
              previewContent = (
                <div style={{ marginTop: '0.75rem' }}>
                  <ChartArtifactView
                    chart={docContent}
                    fetchArtifactDoc={fetchArtifactDocumentById}
                    queryMetricRange={queryMetricRange}
                    fullTables={false}
                  />
                </div>
              );
            } else if (isTopologyArtifactContent(docContent)) {
              previewContent = (
                <div style={{ marginTop: '0.75rem' }}>
                  {renderTopologyArtifact(target, docContent as TopologyArtifactContent, ChatActiveArtifactContextschemaSurfaceEnum.ChatCard, {
                    compact: true,
                    artifactRep: selectedRep
                  })}
                </div>
              );
            } else {
            const lines = preview.content.split('\n').length;
            const style: React.CSSProperties = {
              maxHeight: lines > 10 ? '240px' : undefined,
              overflow: lines > 10 ? 'auto' : undefined
            };
            const isCommandOutput = String(target.kind ?? '').toLowerCase() === 'commandoutput';
            const preClassName = isCommandOutput
              ? 'chat-timeline__artifact-preview chat-timeline__artifact-preview--terminal'
              : 'chat-timeline__artifact-preview';
            previewContent = (
              <pre className={preClassName} style={style}>
                {renderHighlightedPreview(target.id, preview.content, { showLineNumbers: isFileArtifactKind(target.kind) })}
	              </pre>
	            );
            }
	          }
	          detail = renderArtifactDisplayTitle(target);
	          artifactActions = (
	            <div className="chat-timeline__artifact-actions">
	              <button
                type="button"
                className="chat-timeline__artifact-button"
                style={{ marginRight: '0.5rem' }}
	                onClick={() => handleOpenArtifact(target)}>
	                {expanded ? '▼ View' : '▶ View'}
	              </button>
              {renderArtifactDownloadAction(target, 'chat-timeline__artifact-button')}
              {expanded && previewContent}
            </div>
          );
        }
        break;
      }
      case 'artifact_update':
        icon = '◎';
        label = event.artifactKind === 'topology' ? 'Topology updated' : 'Artifact updated';
        detail = event.summary ?? `Revision ${event.revision}`;
        break;
      case 'error':
        icon = '⚠️';
        label = event.title ?? 'Run issue';
        detail = event.message;
        break;
    }
    return (
      <details key={event.id} className="chat-sse-note">
        <summary>
          <span className="chat-sse-note__label">
            <span className="chat-sse-note__icon">{icon}</span>
            {label}
          </span>
          <time>{formatDateTime(event.timestamp)}</time>
        </summary>
        {detail && <p>{detail}</p>}
        {artifactActions}
      </details>
    );
  }, [
    approvalStateByKey,
    artifactExpanded,
    artifactPreview,
    artifactViewReps,
    artifactsByRun,
    currentRunArtifacts,
    currentRunId,
    handleOpenArtifact,
    fetchArtifactDocumentById,
    queryMetricRange,
    renderArtifactDownloadAction,
    renderHighlightedPreview,
    renderTopologyArtifact,
    submitApprovalDecision
  ]);

  const renderPlanStepStatus = useCallback((status?: string): string => {
    switch ((status ?? '').toLowerCase()) {
      case 'ok':
        return '✓';
      case 'error':
        return '✕';
      case 'running':
        return '⏳';
      case 'skipped':
        return '⏭';
      case 'queued':
        return '•';
      default:
        return '•';
    }
  }, []);

  const renderAgentCards = useCallback((cards: AgentCardState[], runId?: string) => {
    if (cards.length === 0) {
      return null;
    }
    const hasAssistant = runId ? (runMessagePresence[runId]?.hasAssistant ?? false) : false;
    const defaultPanelOpen = !hasAssistant || (runId != null && runId === streamingRunId);
    const panelOpen = runId ? (agentPanelOpenOverrides[runId] ?? defaultPanelOpen) : defaultPanelOpen;
    const panelLabel = runId ? `Agent activity (${cards.length})` : `Agent activity (${cards.length})`;
    const isTerminalStage = (stage?: string) => {
      const normalized = String(stage ?? '').trim().toLowerCase();
      return normalized === 'finalize' || normalized === 'finalise' || normalized === 'completed' || normalized === 'complete';
    };
    return (
      <section className="chat-agent-panel">
        <div className="chat-agent-panel__header">
          <span>{panelLabel}</span>
          {runId && (
            <button
              type="button"
              className="chat-agent-panel__toggle"
              aria-expanded={panelOpen}
              onClick={() =>
                setAgentPanelOpenOverrides((prev) => ({ ...prev, [runId]: !(prev[runId] ?? defaultPanelOpen) }))
              }>
              {panelOpen ? 'Hide' : 'Show'}
            </button>
          )}
        </div>
        {!panelOpen ? null : (
          <div className="chat-agent-panel__list">
          {cards.map((card) => {
            const indentStyle = card.indent > 0 ? { marginLeft: `${card.indent * 16}px` } : undefined;
            const activeStageIndex = card.currentStage ? card.stages.indexOf(card.currentStage) : -1;
            const overrideKey = `${runId ?? 'run'}:${card.key}`;
            const isChild = Boolean(card.parentId) || card.indent > 0;
            const defaultCollapsed = isChild && isTerminalStage(card.currentStage);
            const collapsed = agentCardCollapsedOverrides[overrideKey] ?? defaultCollapsed;
            return (
              <div
                key={card.key}
                className={`chat-agent-card ${collapsed ? 'chat-agent-card--collapsed' : ''}`}
                style={indentStyle}>
                <div className="chat-agent-card__header">
                  <div className="chat-agent-card__title">
                    <strong>{card.displayName}</strong>
                    {card.currentStage && <span className="chat-agent-card__stage">{card.currentStage}</span>}
                  </div>
                  <div className="chat-agent-card__header-actions">
                    {(() => {
                      const artifactRunId = runId ?? currentRunId ?? '';
                      const candidates =
                        artifactRunId && artifactsByRun[artifactRunId]
                          ? card.artifacts
                              .map((artifactEvent) => {
                                const id = artifactEvent.artifactId ?? artifactEvent.id;
                                return artifactsByRun[artifactRunId].find((item) => item.id === id);
                              })
                              .filter(Boolean) as ChatArtifact[]
                          : [];
                      const fromLabels = candidates.map((a) => formatDeviceLabel(a.labels)).find(Boolean) ?? null;
                      const fromTaskKey = extractDeviceHintFromTaskKey(card.call?.taskKey);
                      const label = fromLabels ?? fromTaskKey;
                      return label ? <span className="chat-agent-card__device">{label}</span> : null;
                    })()}
                    {card.call?.taskKey && <span className="chat-agent-card__task">{card.call.taskKey}</span>}
                    <button
                      type="button"
                      className="chat-agent-card__toggle"
                      aria-expanded={!collapsed}
                      onClick={() =>
                        setAgentCardCollapsedOverrides((prev) => ({
                          ...prev,
                          [overrideKey]: !(prev[overrideKey] ?? defaultCollapsed)
                        }))
                      }>
                      {collapsed ? 'Show' : 'Hide'}
                    </button>
                  </div>
                </div>
                {collapsed ? null : (
                  <>
                    {card.stages.length > 0 && (
                      <div className="chat-agent-card__stages">
                        {card.stages.map((stage, index) => {
                          const state = activeStageIndex === index ? 'active' : activeStageIndex > index ? 'done' : 'pending';
                          return (
                            <span key={stage} className={`chat-agent-card__stage-pill chat-agent-card__stage-pill--${state}`}>
                              {stage}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {card.narrationUpdates.length > 0 && (
                      <div className="chat-agent-card__narration-list">
                        {card.narrationUpdates.map((update) => (
                          <div key={update.id} className="chat-agent-card__narration">
                            {update.detail}
                          </div>
                        ))}
                      </div>
                    )}
                    {card.latestLifecycleProgress?.detail && (
                      <div className="chat-agent-card__progress-detail">{card.latestLifecycleProgress.detail}</div>
                    )}
                    {card.trace?.text && (
                      <div key={card.trace.id} className="chat-agent-card__trace chat-agent-card__trace--fade">
                        {card.trace.text}
                      </div>
                    )}
                    {card.recentActivity.length > 0 && (
                      <div className="chat-agent-card__activity">
                        <span className="chat-agent-card__activity-label">Recent activity</span>
                        <div className="chat-agent-card__activity-buffer">
                          {card.recentActivity
                            .slice()
                            .reverse()
                            .map((item) => (
                              <div key={item.id} className={`chat-agent-card__activity-item chat-agent-card__activity-item--${item.type}`}>
                                {item.label}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {card.artifacts.length > 0 && (
                      <div className="chat-agent-card__artifacts">
                        <span className="chat-agent-card__artifacts-label">Artifacts</span>
                        <div className="chat-agent-card__artifacts-list">
                          {card.artifacts.map((artifactEvent) => {
                            const artifactRunId = artifactEvent.runId ?? runId ?? currentRunId ?? '';
                            const artifact =
                              artifactRunId && artifactsByRun[artifactRunId]
                                ? artifactsByRun[artifactRunId].find(
                                    (item) => item.id === (artifactEvent.artifactId ?? artifactEvent.id)
                                  )
                                : undefined;
                            const label =
                              artifact?.summary ?? artifactEvent.summary ?? artifactEvent.artifactId ?? artifactEvent.id ?? 'Artifact';
                            return (
                              <button
                                key={`${artifactEvent.id}-${artifactEvent.artifactId ?? 'artifact'}`}
                                type="button"
                                className="chat-agent-card__artifact-pill"
                                onClick={() => {
                                  if (artifact) {
                                    void openInspectorForArtifact(artifact);
                                  }
                                }}
                                disabled={!artifact}>
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {card.plans.length > 0 && (
                      <div className="chat-agent-card__plans">
                        {card.plans.map((plan) => {
                          const planKey = `${card.key}:${plan.planId}`;
                          const isOpen = planOpenOverrides[planKey] ?? plan.isActive;
                          return (
                            <details key={plan.planId} className="chat-agent-plan" open={isOpen}>
                              <summary>
                                <span className="chat-agent-plan__label">
                                  {plan.round ? `Round ${plan.round}` : 'Plan'} {plan.title ? `· ${plan.title}` : ''}
                                </span>
                                {!isOpen && <span className="chat-agent-plan__folded">Folded</span>}
                              </summary>
                              {plan.steps.length === 0 ? (
                                <p className="chat-agent-plan__empty">No steps listed.</p>
                              ) : (
                                <ol className="chat-agent-plan__steps">
                                  {plan.steps.map((step) => {
                                    const status = String(step.status ?? 'queued').toLowerCase();
                                    const hasPercent = typeof step.percent === 'number';
                                    const showSpinner = status === 'running' && !hasPercent;
                                    return (
                                      <li
                                        key={step.id}
                                        className={`chat-agent-plan__step chat-agent-plan__step--${status}`}>
                                        <span className="chat-agent-plan__status">
                                          {showSpinner ? (
                                            <span className="chat-agent-plan__spinner" />
                                          ) : (
                                            renderPlanStepStatus(step.status)
                                          )}
                                        </span>
                                        <div className="chat-agent-plan__content">
                                          <span className="chat-agent-plan__title">{step.title}</span>
                                          {step.detail && <span className="chat-agent-plan__detail">{step.detail}</span>}
                                          {hasPercent && (
                                            <span className="chat-agent-plan__percent">{Math.round(step.percent ?? 0)}%</span>
                                          )}
                                          {hasPercent && (
                                            <span className="chat-agent-plan__progress">
                                              <span className="chat-agent-plan__progress-fill" style={{ width: `${step.percent}%` }} />
                                            </span>
                                          )}
                                        </div>
                                      </li>
                                    );
                                  })}
                                </ol>
                              )}
                            </details>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        )}
      </section>
    );
  }, [
    agentCardCollapsedOverrides,
    agentPanelOpenOverrides,
    artifactsByRun,
    currentRunId,
    openInspectorForArtifact,
    planOpenOverrides,
    renderPlanStepStatus,
    runMessagePresence,
    streamingRunId
  ]);

  const renderTimelineEvent = useCallback(
    (event: ChatTimelineEventWithDelay) => {
      if (event.type === 'message') {
        return renderMessageBubble(event);
      }
      if (event.type === 'progress') {
        const approval = extractApprovalMeta(event);
        if (approval) {
          return renderApprovalPrompt(event);
        }
      }
      return renderSseNote(event as ChatSseEvent);
    },
    [renderApprovalPrompt, renderMessageBubble, renderSseNote]
  );

  const userTimelineNodes = useMemo(() => {
    const nodes: JSX.Element[] = [];

    for (let index = 0; index < timelineEvents.length; index += 1) {
      const event = timelineEvents[index];
      const key = event.id ?? `${event.type}-${event.timestamp}-${index}`;

      if (event.type === 'message' && event.role === 'user') {
        const turnNodes: JSX.Element[] = [
          <Fragment key={key}>{renderMessageBubble(event, { artifactVisibility: 'none' })}</Fragment>
        ];
        const runId = event.runId ?? null;
        if (runId) {
          const progressNode = renderUserProgressStream(runId);
          if (progressNode) {
            turnNodes.push(progressNode);
          }
        }

        for (let cursor = index + 1; cursor < timelineEvents.length; cursor += 1) {
          const nextEvent = timelineEvents[cursor];
          if (nextEvent.type === 'message' && nextEvent.role === 'user') {
            break;
          }
          const nextKey = nextEvent.id ?? `${nextEvent.type}-${nextEvent.timestamp}-${cursor}`;
          if (nextEvent.type === 'message') {
            if (nextEvent.role === 'assistant' && !nextEvent.isLivePreview) {
              turnNodes.push(
                <Fragment key={nextKey}>{renderMessageBubble(nextEvent, { artifactVisibility: 'final-only' })}</Fragment>
              );
            }
            index = cursor;
            continue;
          }
          if (nextEvent.type === 'progress' && extractApprovalMeta(nextEvent)) {
            turnNodes.push(<Fragment key={nextKey}>{renderApprovalPrompt(nextEvent)}</Fragment>);
            index = cursor;
          }
        }

        nodes.push(
          <div key={`user-turn-${runId ?? event.id ?? index}`} className="chat-thread-turn chat-thread-turn--user-view">
            {turnNodes}
          </div>
        );
        continue;
      }

      if (event.type === 'message') {
        if (event.role === 'assistant' && !event.isLivePreview) {
          nodes.push(<Fragment key={key}>{renderMessageBubble(event, { artifactVisibility: 'final-only' })}</Fragment>);
        }
        continue;
      }

      if (event.type === 'progress' && extractApprovalMeta(event)) {
        nodes.push(<Fragment key={key}>{renderApprovalPrompt(event)}</Fragment>);
      }
    }

    return nodes;
  }, [renderApprovalPrompt, renderMessageBubble, renderUserProgressStream, timelineEvents]);

  const timelineNodes = useMemo(() => {
    if (chatViewMode === 'timeline') {
      return timelineEvents.map((event, index) => (
        <Fragment key={event.id ?? `${event.type}-${event.timestamp}-${index}`}>{renderTimelineEvent(event)}</Fragment>
      ));
    }
    const nodes: JSX.Element[] = [];
    const seenRuns = new Set<string>();
    for (let index = 0; index < timelineEvents.length; index += 1) {
      const event = timelineEvents[index];
      if (event.type === 'message' && event.role === 'user' && event.runId) {
        const runId = event.runId;
        const turnNodes: JSX.Element[] = [
          <Fragment key={event.id ?? `${event.type}-${event.timestamp}-${index}`}>{renderTimelineEvent(event)}</Fragment>
        ];
        const activityNodes: JSX.Element[] = [];
        let assistantNode: JSX.Element | null = null;

        for (let cursor = index + 1; cursor < timelineEvents.length; cursor += 1) {
          const nextEvent = timelineEvents[cursor];
          const nextKey = nextEvent.id ?? `${nextEvent.type}-${nextEvent.timestamp}-${cursor}`;
          if (getTimelineEventRunId(nextEvent) !== runId) {
            break;
          }
          if (nextEvent.type === 'message' && nextEvent.role === 'assistant') {
            assistantNode = <Fragment key={nextKey}>{renderTimelineEvent(nextEvent)}</Fragment>;
            index = cursor;
            break;
          }
          if (nextEvent.type === 'message') {
            break;
          }
          activityNodes.push(<Fragment key={nextKey}>{renderTimelineEvent(nextEvent)}</Fragment>);
          index = cursor;
        }

        seenRuns.add(runId);
        const cards = agentCardsByRun[runId];
        if (activityNodes.length > 0 || (cards?.length ?? 0) > 0) {
          turnNodes.push(
            <div key={`activity-${runId}-${event.id ?? index}`} className="chat-thread-turn__activity">
              {activityNodes}
              {cards?.length ? renderAgentCards(cards, runId) : null}
            </div>
          );
        }
        if (assistantNode) {
          turnNodes.push(assistantNode);
        }
        nodes.push(
          <div key={`turn-${runId}-${event.id ?? index}`} className="chat-thread-turn">
            {turnNodes}
          </div>
        );
        continue;
      }

      nodes.push(
        <Fragment key={event.id ?? `${event.type}-${event.timestamp}-${index}`}>{renderTimelineEvent(event)}</Fragment>
      );
    }
    const unmatchedRuns = Object.entries(agentCardsByRun)
      .filter(([runId, cards]) => cards.length > 0 && !seenRuns.has(runId))
      .sort(([, left], [, right]) => {
        const leftTime = Date.parse(left[0]?.firstSeen ?? '') || 0;
        const rightTime = Date.parse(right[0]?.firstSeen ?? '') || 0;
        return leftTime - rightTime;
      });
    unmatchedRuns.forEach(([runId, cards]) => {
      seenRuns.add(runId);
      nodes.push(
        <Fragment key={`agents-unmatched-${runId}`}>{renderAgentCards(cards, runId)}</Fragment>
      );
    });
    return nodes;
  }, [agentCardsByRun, chatViewMode, renderAgentCards, renderTimelineEvent, timelineEvents]);

  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const attachComposerRef = useCallback((node: HTMLTextAreaElement | null) => {
    composerRef.current = node;
  }, []);
  const focusComposer = useCallback(() => {
    if (composerRef.current) {
      composerRef.current.focus();
    }
  }, []);

  const clearPendingComposerFiles = useCallback(() => {
    setPendingComposerFiles([]);
  }, []);

  const openThread = useCallback(
    (thread: ChatThreadSummary) => {
      creatingThreadDraftRef.current = null;
      persistThreadUiState();
      clearPendingComposerFiles();
      setCreatingThread(false);
      setSuppressAutoThreadSelection(false);
      setChatLoadErrorMessage(null);
      const openedThreadId = thread.id;
      if (openedThreadId) {
        setThreadAttentionById((prev) => {
          if (prev[openedThreadId] === 'action_required' || !prev[openedThreadId]) {
            return prev;
          }
          const next = { ...prev };
          delete next[openedThreadId];
          return next;
        });
      }
      setSelectedThread(thread);
      updateThreadIdInLocation(thread.id ?? null, { markAsInternal: true });
      setThreadListCollapsed(true);
      closeInspector();
    },
    [clearPendingComposerFiles, closeInspector, persistThreadUiState, updateThreadIdInLocation]
  );

  const openThreadDeleteDialog = useCallback((thread: ChatThreadSummary) => {
    if (!thread.id) {
      return;
    }
    setPendingDeleteThread(thread);
  }, []);

  const confirmDeleteThread = useCallback(async () => {
    const thread = pendingDeleteThread;
    const threadId = thread?.id?.trim();
    if (!thread || !threadId || deletingThreadId) {
      return;
    }

    setDeletingThreadId(threadId);
    try {
      await requestOrgApi(`chat/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' });

      activeStreamSessionsRef.current.forEach((session, runId) => {
        if (session.threadId === threadId) {
          stopStreamingRun(runId);
        }
      });
      threadUiStateCacheRef.current.delete(threadId);
      setThreadAttentionById((previous) => {
        if (!previous[threadId]) {
          return previous;
        }
        const next = { ...previous };
        delete next[threadId];
        return next;
      });
      setLocallyDeletedThreadIds((previous) => new Set([...previous, threadId]));
      setThreads((previous) => previous.filter((entry) => entry.id !== threadId));
      setThreadsMeta((previous) =>
        previous && typeof previous.exactTotal === 'number'
          ? { ...previous, exactTotal: Math.max(0, previous.exactTotal - 1) }
          : previous
      );

      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        updateThreadIdInLocation(null, { markAsInternal: true });
        clearActiveThreadUiState();
      }

      setPendingDeleteThread(null);
      setThreadDeleteToast({ tone: 'success', message: `${normalizeThreadTitle(thread.title)} deleted successfully.` });
      reloadThreads();
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Failed to delete chat thread.');
      setThreadDeleteToast({ tone: 'error', message });
      console.error('Failed to delete thread', error);
    } finally {
      setDeletingThreadId(null);
    }
  }, [clearActiveThreadUiState, deletingThreadId, pendingDeleteThread, reloadThreads, requestOrgApi, selectedThread?.id, stopStreamingRun, updateThreadIdInLocation]);

  const handleSelectComposerFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const nextPending: PendingComposerFile[] = files.map((file) => ({
        localId: createClientRequestId(),
        source: 'local',
        file,
        status: 'pending'
      }));
      setComposerError(null);
      setPendingComposerFiles((prev) => [...prev, ...nextPending]);
    },
    []
  );

  const handleAttachPromotedComposerFile = useCallback((fileRecord: UploadedFileRecord) => {
    const resourceId = (fileRecord.id ?? '').trim();
    if (!resourceId) {
      return;
    }
    const attachment: ChatInteractAttachmentPayload = {
      resourceType: 'file',
      resourceId,
      name: (fileRecord.filename ?? fileRecord.name ?? resourceId).trim() || resourceId,
      mimeType: (fileRecord.mimeType ?? '').trim() || undefined,
      sizeBytes: typeof fileRecord.sizeBytes === 'number' ? fileRecord.sizeBytes : undefined,
    };
    setComposerError(null);
    setPendingComposerFiles((previous) => {
      if (previous.some((item) => item.source === 'promoted' && item.uploadedAttachment?.resourceId === resourceId)) {
        return previous;
      }
      return [
        ...previous,
        {
          localId: createClientRequestId(),
          source: 'promoted',
          fileRecord,
          status: 'uploaded',
          uploadedAttachment: attachment,
        },
      ];
    });
  }, []);

  useEffect(() => {
    if (!requestedComposerAttachment) {
      consumedComposerAttachmentRef.current = null;
      return;
    }
    if (!orgId) {
      return;
    }
    if (requestedOrgId && requestedOrgId !== orgId) {
      return;
    }
    if (consumedComposerAttachmentRef.current === requestedComposerAttachment.fileId) {
      return;
    }

    consumedComposerAttachmentRef.current = requestedComposerAttachment.fileId;
    if (!selectedThread && !creatingThread) {
      startNewThread();
    }
    handleAttachPromotedComposerFile({
      id: requestedComposerAttachment.fileId,
      orgId,
      name: requestedComposerAttachment.name,
      filename: requestedComposerAttachment.name,
      mimeType: requestedComposerAttachment.mimeType,
      sizeBytes: 0,
      checksum: '',
      status: 'ready',
    });
    const isTechSupport = requestedComposerAttachment.category === 'techsupport';
    const isNetflow = requestedComposerAttachment.category === 'netflow';

    const defaultPrompt = isTechSupport
      ? `Analyze tech-support ${requestedComposerAttachment.name}.`
      : isNetflow
      ? `Analyze flow metrics ${requestedComposerAttachment.name}.`
      : `Analyze packet capture ${requestedComposerAttachment.name}.`;

    const defaultTitle = isTechSupport
      ? `Tech-support analysis: ${requestedComposerAttachment.name}`
      : isNetflow
      ? `Flow analysis: ${requestedComposerAttachment.name}`
      : `PCAP analysis: ${requestedComposerAttachment.name}`;

    setComposerText((previous) => (
      previous.trim().length > 0 ? previous : defaultPrompt
    ));
    setNewThreadTitle((previous) => (
      previous.trim().length > 0 ? previous : defaultTitle
    ));

    const params = new URLSearchParams(location.search);
    params.delete('attachFileId');
    params.delete('attachFileName');
    params.delete('attachMimeType');
    params.delete('attachCategory');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [
    creatingThread,
    handleAttachPromotedComposerFile,
    location.pathname,
    location.search,
    navigate,
    orgId,
    requestedComposerAttachment,
    requestedOrgId,
    selectedThread,
    startNewThread,
  ]);

  const removePendingComposerFile = useCallback((localId: string) => {
    setPendingComposerFiles((prev) => prev.filter((item) => item.localId !== localId));
  }, []);

  const uploadComposerFiles = useCallback(
    async (threadId: string, files: PendingComposerFile[]): Promise<ChatInteractAttachmentPayload[]> => {
      const uploaded: ChatInteractAttachmentPayload[] = [];
      for (const item of files) {
        if (item.source === 'promoted') {
          if (item.uploadedAttachment) {
            uploaded.push(item.uploadedAttachment);
          }
          continue;
        }
        if (item.uploadedAttachment && item.uploadedThreadId === threadId) {
          uploaded.push(item.uploadedAttachment);
          continue;
        }
        if (!item.file) {
          continue;
        }
        setPendingComposerFiles((prev) =>
          prev.map((entry) => (entry.localId === item.localId ? { ...entry, status: 'uploading', error: undefined } : entry))
        );
        const formData = new FormData();
        formData.append('file', item.file, item.file.name);
        formData.append('threadId', threadId);
        formData.append('name', item.file.name);
        const response = await requestOrgApi('files', {
          method: 'POST',
          body: formData
        });
        const payload = parseUploadedFileRecord(await response.json());
        if (!payload) {
          throw new Error(`Upload response for ${item.file.name} did not match Fileschema.`);
        }
        const resourceId = (payload.id ?? '').trim();
        if (!resourceId) {
          throw new Error(`Upload response for ${item.file.name} did not include a file id.`);
        }
        const attachment: ChatInteractAttachmentPayload = {
          resourceType: 'file',
          resourceId,
          name: (payload.filename ?? item.file.name).trim() || item.file.name,
          mimeType: (payload.mimeType ?? inferReadableUploadMime(item.file)).trim() || undefined,
          sizeBytes: typeof payload.sizeBytes === 'number' ? payload.sizeBytes : item.file.size
        };
        uploaded.push(attachment);
        setPendingComposerFiles((prev) =>
          prev.map((entry) =>
            entry.localId === item.localId
              ? {
                  ...entry,
                  status: 'uploaded',
                  uploadedAttachment: attachment,
                  uploadedThreadId: threadId,
                  error: undefined
                }
              : entry
          )
        );
      }
      return uploaded;
    },
    [requestOrgApi]
  );

  const handleSendMessage = useCallback(async () => {
    const trimmed = composerText.trim();
    if ((!trimmed && pendingComposerFiles.length === 0) || !orgId) {
      return;
    }
    setComposerError(null);
    try {
      const pendingFilesSnapshot = [...pendingComposerFiles];
      let threadId: string | undefined;
      if (creatingThread) {
        let createdThread = creatingThreadDraftRef.current;
        if (!createdThread?.id) {
          createdThread = await createThreadMutation(newThreadTitle);
          creatingThreadDraftRef.current = createdThread;
        }
        if (createdThread?.id) {
          threadId = createdThread.id;
          const attachments =
            pendingFilesSnapshot.length > 0 ? await uploadComposerFiles(createdThread.id, pendingFilesSnapshot) : undefined;
          await interactWithThread(
            createdThread.id,
            trimmed ? [{ type: 'text', text: trimmed }] : [],
            { includeContext: false, attachments }
          );
          creatingThreadDraftRef.current = null;
          setCreatingThread(false);
          setNewThreadTitle('');
          setSelectedThread(createdThread);
          setThreads((prev) => mergeThreadsById(prev, [createdThread]));
          pendingThreadTitleRefreshRef.current.add(createdThread.id);
          reloadThreads();
        }
      } else if (selectedThread?.id) {
        threadId = selectedThread.id;
        const attachments =
          pendingFilesSnapshot.length > 0 ? await uploadComposerFiles(selectedThread.id, pendingFilesSnapshot) : undefined;
        await interactWithThread(
          selectedThread.id,
          trimmed ? [{ type: 'text', text: trimmed }] : [],
          { attachments }
        );
      }
      if (threadId) {
        setComposerText('');
        clearPendingComposerFiles();
        scrollTimelineToBottom(true);
      }
    } catch (error) {
      setPendingComposerFiles((prev) =>
        prev.map((item) => (item.status === 'uploading' ? { ...item, status: 'pending' } : item))
      );
      const message =
        error instanceof ApiResponseError
          ? `Request failed (${error.response.status})`
          : error instanceof Error
          ? error.message
          : 'Failed to send message.';
      setComposerError(message);
    }
  }, [
    composerText,
    createThreadMutation,
    clearPendingComposerFiles,
    creatingThread,
    interactWithThread,
    newThreadTitle,
    orgId,
    pendingComposerFiles,
    reloadThreads,
    scrollTimelineToBottom,
    selectedThread?.id,
    uploadComposerFiles
  ]);

  const handleComposerKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const target = event.currentTarget;
        const start = target.selectionStart ?? target.value.length;
        const end = target.selectionEnd ?? target.value.length;
        const nextValue = `${target.value.slice(0, start)}\n${target.value.slice(end)}`;
        target.value = nextValue;
        setComposerText(nextValue);
        requestAnimationFrame(() => {
          if (composerRef.current) {
            composerRef.current.selectionStart = composerRef.current.selectionEnd = start + 1;
          }
        });
        return;
      }
      event.preventDefault();
      if (isStreaming || (!composerText.trim() && pendingComposerFiles.length === 0)) {
        return;
      }
      void handleSendMessage();
    },
    [composerText, handleSendMessage, isStreaming, pendingComposerFiles.length]
  );

  useEffect(() => {
    if (runRating == null || !currentRunId || !orgId) {
      return;
    }
    const ratingValue = runRating <= 2 ? -1 : runRating === 3 ? 0 : 1;
    requestOrgApi(`chat/runs/${currentRunId}/ratings`, {
      method: 'POST',
      body: JSON.stringify({ rating: ratingValue })
    }).catch((error) => {
      console.error('Failed to submit run rating', error);
    });
  }, [currentRunId, orgId, requestOrgApi, runRating]);


  if (!orgId) {
    return (
      <div className="chat-page chat-page--empty">
        <OrgModulePlaceholder intent="Select an organization to load chat threads." />
      </div>
    );
  }

  if (isCurrentThreadListScope && backendMissing && !isTransientThreadListError(threadListError)) {
    return (
      <div className="chat-page chat-page--empty">
        <OrgModulePlaceholder intent="Implement the chat API to bring live runs online." />
      </div>
    );
  }

  const detailStatusLabel = selectedThread ? deriveThreadStatus(selectedThread) : null;
  const canSendComposer = Boolean(composerText.trim()) || pendingComposerFiles.length > 0;

  const renderComposerFiles = () => {
    if (pendingComposerFiles.length === 0) {
      return null;
    }
    return (
      <div className="chat-module__composer-files">
        {pendingComposerFiles.map((item) => (
          <div key={item.localId} className="chat-module__composer-file">
            <div className="chat-module__composer-file-meta">
                <span className="chat-module__composer-file-name">
                  {item.source === 'promoted'
                    ? (item.fileRecord?.filename ??
                      item.fileRecord?.name ??
                      (typeof item.uploadedAttachment?.resourceId === 'string' ? item.uploadedAttachment.resourceId : 'Promoted file'))
                    : item.file?.name ?? 'Local file'}
                </span>
              <span className="chat-module__composer-file-size">
                {Math.max(
                  1,
                  Math.round(
                    ((item.source === 'promoted' ? item.fileRecord?.sizeBytes : item.file?.size) ?? 0) / 1024
                  )
                )}{' '}
                KB
              </span>
              {item.status === 'uploading' && <span className="chat-module__composer-file-status">Uploading…</span>}
              {item.source === 'promoted' && item.status !== 'uploading' && (
                <span className="chat-module__composer-file-status">Promoted file</span>
              )}
            </div>
            {item.status !== 'uploading' && (
              <button
                type="button"
                className="chat-module__composer-file-remove"
                onClick={() => removePendingComposerFile(item.localId)}
                aria-label={`Remove ${
                  item.source === 'promoted'
                    ? (item.fileRecord?.filename ?? item.fileRecord?.name ?? 'promoted file')
                    : item.file?.name ?? 'file'
                }`}>
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDetailPane = () => {
    if (creatingThread) {
      return (
        <div className="chat-module__detail">
          <div className="chat-module__detail-header">
            <div>
              <h2>Start a conversation</h2>
              <p className="chat-module__detail-meta">Draft your first message and press send to kick off a run.</p>
            </div>
          </div>
          <div className="chat-new-thread-form">
            <label>
              <span>Thread title</span>
              <input
                type="text"
                value={newThreadTitle}
                onChange={(event) => setNewThreadTitle(event.target.value)}
                placeholder="Example: Ops Assistant"
              />
            </label>
            <textarea
              className="chat-module__composer-textarea"
              rows={5}
              placeholder="Type the first message to start this thread."
              value={composerText}
              onChange={(event) => setComposerText(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              onMouseEnter={focusComposer}
              ref={attachComposerRef}
              disabled={isStreaming}
            />
            {renderComposerFiles()}
            <div className="chat-module__composer-row">
              <div className="chat-module__composer-actions">
                <label className="chat-module__trace-level">
                  <span>Detail</span>
                  <select value={traceLevel} onChange={(event) => setTraceLevel(event.target.value as RunTraceLevel)} disabled={isStreaming}>
                    {RUN_TRACE_LEVEL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <ComposerAttachmentPicker
                  disabled={isStreaming}
                  requestOrgApi={requestOrgApi}
                  onSelectLocalFiles={handleSelectComposerFiles}
                  onSelectPromotedFile={handleAttachPromotedComposerFile}
                  triggerClassName="chat-module__composer-secondary"
                  triggerAriaLabel="Attach file or promoted file"
                  triggerContent="+"
                />
              </div>
              <div className="chat-module__run-rating-stars">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`chat-module__run-rating-star ${
                      runRating === value ? 'chat-module__run-rating-star--active' : ''
                    }`}
                    onClick={() => setRunRating((prev) => (prev === value ? null : value))}
                    aria-label={`Rate ${value} star`}>
                    {value <= (runRating ?? 0) ? '★' : '☆'}
                  </button>
                ))}
              </div>
              <button type="button" onClick={handleSendMessage} disabled={!canSendComposer || isStreaming}>
                Send message
              </button>
            </div>
            {composerError && <p className="chat-module__composer-error">{composerError}</p>}
          </div>
        </div>
      );
    }

    const requestedThreadStillPending =
      requestedThreadId &&
      requestedThreadLoadState?.threadId === requestedThreadId &&
      !hasRequestedThreadInThreads;
    if (requestedThreadStillPending && !selectedThread) {
      if (requestedThreadLoadState.status === 'loading') {
        return <OrgModulePlaceholder intent="Loading shared chat thread…" />;
      }
      if (requestedThreadLoadState.status === 'retrying') {
        return <OrgModulePlaceholder intent="Chat refresh failed temporarily. Retrying…" />;
      }
      if (requestedThreadLoadState.status === 'failed') {
        return <OrgModulePlaceholder intent="We couldn’t load this chat right now. Please refresh and try again." />;
      }
    }

    if (selectedThread) {
      const statusChip = detailStatusLabel ? (
        <span className={`chat-detail-status chat-detail-status--${detailStatusLabel.tone}`}>{detailStatusLabel.label}</span>
      ) : null;
      return (
        <div className="chat-module__detail">
          <div className="chat-module__detail-header">
            <div>
              <h2>{normalizeThreadTitle(selectedThread.title)}</h2>
              <p className="chat-module__detail-meta">
                {statusChip}
                {selectedThread.visibility ?? 'org'} · Last message{' '}
                {formatDateTime(selectedThread.lastMessageAt ?? selectedThread.updatedAt ?? null)}
              </p>
            </div>
          </div>
          <div className="chat-module__timeline" ref={timelineRef} onScroll={handleTimelineScroll}>
            {messagesCursor && (
              <button
                type="button"
                className="chat-module__load-older"
                onClick={handleLoadOlderMessages}
                disabled={loadingOlderMessages}>
                {loadingOlderMessages ? 'Loading older messages…' : 'Load older messages'}
              </button>
            )}
            {timelineEvents.length === 0 ? (
              <p className="chat-module__timeline-empty">
                {chatViewMode === 'timeline' ? 'Waiting for run events...' : 'Waiting for messages...'}
              </p>
            ) : (
              chatViewMode === 'user' ? userTimelineNodes : timelineNodes
            )}
          </div>
          <div className="chat-module__composer">
            <textarea
              className="chat-module__composer-textarea"
              rows={3}
              placeholder="Type a message to continue the thread."
              value={composerText}
              onChange={(event) => setComposerText(event.target.value)}
              onKeyDown={handleComposerKeyDown}
              onMouseEnter={focusComposer}
              ref={attachComposerRef}
              disabled={isStreaming}
            />
            {renderComposerFiles()}
            <div className="chat-module__composer-row">
              <div className="chat-module__composer-actions">
                <label className="chat-module__trace-level">
                  <span>Detail</span>
                  <select value={traceLevel} onChange={(event) => setTraceLevel(event.target.value as RunTraceLevel)} disabled={isStreaming}>
                    {RUN_TRACE_LEVEL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <ComposerAttachmentPicker
                  disabled={isStreaming}
                  requestOrgApi={requestOrgApi}
                  onSelectLocalFiles={handleSelectComposerFiles}
                  onSelectPromotedFile={handleAttachPromotedComposerFile}
                  triggerClassName="chat-module__composer-secondary"
                  triggerAriaLabel="Attach file or promoted file"
                  triggerContent="+"
                />
              </div>
              <div className="chat-module__run-rating-stars">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`chat-module__run-rating-star ${
                      runRating === value ? 'chat-module__run-rating-star--active' : ''
                    }`}
                    onClick={() => setRunRating((prev) => (prev === value ? null : value))}
                    aria-label={`Rate ${value} star`}>
                    {value <= (runRating ?? 0) ? '★' : '☆'}
                  </button>
                ))}
              </div>
              <button type="button" onClick={handleSendMessage} disabled={!canSendComposer || isStreaming}>
                Send message
              </button>
            </div>
            {composerError && <p className="chat-module__composer-error">{composerError}</p>}
            {isStreaming && <p className="chat-module__composer-note">Waiting for assistant to finish streaming…</p>}
          </div>
        </div>
      );
    }

    return <OrgModulePlaceholder intent={loading ? 'Loading threads…' : 'Select a thread to view the timeline.'} />;
  };

  const inspectorSelectedArtifact = useMemo(() => {
    if (!inspectorArtifactId) {
      return null;
    }
    if (inspectorArtifact && artifactStateKey(inspectorArtifact) === inspectorArtifactId) {
      return inspectorArtifact;
    }
    return allArtifacts.find((artifact) => artifactStateKey(artifact) === inspectorArtifactId) ?? inspectorArtifact ?? null;
  }, [allArtifacts, currentRunId, inspectorArtifact, inspectorArtifactId]);
  const inspectorSelectedArtifactKey = useMemo(
    () => (inspectorSelectedArtifact ? artifactStateKey(inspectorSelectedArtifact) : null),
    [inspectorSelectedArtifact]
  );

  const lastAutoScrollRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!inspectorOpen || inspectorTab !== 'preview' || !inspectorSelectedArtifact?.id) {
      return;
    }
    const artifactId = artifactStateKey(inspectorSelectedArtifact);
    const preview = artifactPreview[artifactId];
    if (!preview || preview.loading || preview.error) {
      return;
    }

    const docAny: any = preview.doc;
    const selectedRep =
      artifactViewReps[artifactId] ??
      docAny?.presentationHints?.defaultView ??
      (Array.isArray(docAny?.views) && docAny.views.length > 0 ? docAny.views[0]?.rep : undefined);
    const resolvedView =
      selectedRep && Array.isArray(docAny?.views) ? docAny.views.find((v: any) => v && v.rep === selectedRep) : undefined;
    const docContent: any = resolvedView?.content ?? docAny?.content;

    const rawHL = artifactHighlights[artifactId];
    const tableHL = artifactTableHighlights[artifactId];

    const signature =
      docContent?.type === 'table' && Array.isArray(tableHL) && tableHL.length > 0
        ? `table:${selectedRep ?? ''}:${tableHL[0]}`
        : (docContent as any)?.type === 'commandOutput' && rawHL
          ? `raw:${selectedRep ?? ''}:${rawHL.startLine}-${rawHL.endLine}`
          : '';
    if (!signature) {
      return;
    }
    if (lastAutoScrollRef.current[artifactId] === signature) {
      return;
    }
    lastAutoScrollRef.current[artifactId] = signature;

    requestAnimationFrame(() => {
      if (docContent?.type === 'table' && Array.isArray(tableHL) && tableHL.length > 0) {
        const wrap = document.querySelector(`[data-artifact-tablewrap="${artifactId}"]`) as HTMLElement | null;
        if (!wrap) {
          return;
        }
        const keys = new Set(tableHL.map((k) => String(k)));
        const rows = Array.from(wrap.querySelectorAll('tr[data-row-key]')) as HTMLElement[];
        const target = rows.find((row) => {
          const k = row.getAttribute('data-row-key');
          return k != null && keys.has(k);
        });
        target?.scrollIntoView({ block: 'center' });
        return;
      }

      if ((docContent as any)?.type === 'commandOutput' && rawHL) {
        const prewrap = document.querySelector(`[data-artifact-prewrap="${artifactId}"]`) as HTMLElement | null;
        if (!prewrap) {
          return;
        }
        const target = prewrap.querySelector(`[data-line="${rawHL.startLine}"]`) as HTMLElement | null;
        target?.scrollIntoView({ block: 'center' });
      }
    });
  }, [
    artifactHighlights,
    artifactPreview,
    artifactTableHighlights,
    artifactViewReps,
    inspectorOpen,
    inspectorSelectedArtifactKey,
    inspectorTab
  ]);

  const maybeAutoLoadFullOnScroll = useCallback(
    (artifact: ChatArtifact, event: UIEvent<HTMLElement>) => {
      const artifactKey = artifactStateKey(artifact);
      const preview = artifactPreview[artifactKey];
      if (!preview || preview.loading || preview.error || preview.isFull) {
        return;
      }
      const doc: any = preview.doc;
      const selectedRep =
        artifactViewReps[artifactKey] ??
        doc?.presentationHints?.defaultView ??
        (Array.isArray(doc?.views) && doc.views.length > 0 ? doc.views[0]?.rep : undefined);
      const resolvedView =
        selectedRep && Array.isArray(doc?.views) ? doc.views.find((v: any) => v && v.rep === selectedRep) : undefined;
      const docContent: any = resolvedView?.content ?? doc?.content;
      if (docContent?.type !== 'commandOutput' || !docContent.hasMore) {
        return;
      }
      if (typeof artifact.size !== 'number' || artifact.size > INSPECTOR_AUTO_FULL_MAX_BYTES) {
        return;
      }
      const target = event.currentTarget;
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (!nearBottom) {
        return;
      }
      void handleLoadFullArtifact(artifact);
    },
    [artifactPreview, artifactViewReps, handleLoadFullArtifact]
  );

  const renderInspectorPreview = useCallback(
    (artifact: ChatArtifact, maximized: boolean) => {
      const artifactKey = artifactStateKey(artifact);
      const preview = artifactPreview[artifactKey];
      if (!preview) {
        return (
          <div className="chat-inspector__empty">
            <p>Loading preview…</p>
          </div>
        );
      }
      if (preview.loading) {
        return (
          <div className="chat-inspector__empty">
            <p>Loading artifact…</p>
          </div>
        );
      }
      if (preview.error) {
        return (
          <div className="chat-inspector__empty">
            <p className="chat-timeline__artifact-error">{preview.error}</p>
          </div>
        );
      }

      const mimeHint = (artifact.mimeType ?? '').toLowerCase();
      if (isFileArtifactKind(artifact.kind)) {
        return (
          <div className={maximized ? 'chat-inspector__preview chat-inspector__preview--max' : 'chat-inspector__preview'}>
            <ChatFilePreviewPane
              artifactId={artifact.id}
              file={preview.fileRecord}
              mimeType={artifact.mimeType}
              sizeBytes={artifact.size}
              text={preview.content}
              fileText={preview.fileText}
              objectUrl={preview.objectUrl}
              loading={preview.loading}
              onDownload={() => void handleDownloadArtifact(artifact)}
              onLoadMore={() => void handleLoadFullArtifact(artifact)}
              renderPreview={renderHighlightedPreview}
            />
          </div>
        );
      }
      if (mimeHint.startsWith('image/')) {
        return (
          <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
            <img src={preview.content} alt={artifact.summary ?? 'Image artifact'} style={{ maxWidth: '100%' }} />
          </div>
        );
      }
      if (mimeHint === 'application/pdf') {
        return (
          <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
            <iframe title="PDF preview" src={preview.content} style={{ width: '100%', height: maximized ? '80vh' : '50vh' }} />
          </div>
        );
      }

      const doc: any = preview.doc;
      const viewOptions: Array<{ rep: string; title?: string }> = Array.isArray(doc?.views)
        ? (doc.views
            .filter((v: any) => v && typeof v.rep === 'string' && v.rep.trim())
            .map((v: any) => ({ rep: String(v.rep), title: typeof v.title === 'string' ? v.title : undefined })) as Array<{
            rep: string;
            title?: string;
          }>)
        : [];
      const fallbackRep = doc?.presentationHints?.defaultView ?? (viewOptions.length > 0 ? viewOptions[0].rep : undefined);
      const activeRep = artifactViewReps[artifactKey] ?? fallbackRep;
      const resolvedView =
        activeRep && Array.isArray(doc?.views) ? doc.views.find((v: any) => v && v.rep === activeRep) : undefined;
      const docContent: any = resolvedView?.content ?? doc?.content;

      const viewSelector =
        viewOptions.length > 1 ? (
          <div className="chat-inspector__view-selector">
            <label>
              View:{' '}
              <select
                value={activeRep}
                onChange={(event) => setArtifactViewReps((prev) => ({ ...prev, [artifactKey]: event.target.value }))}>
                {viewOptions.map((v) => (
                  <option key={`${artifactKey}-view-${v.rep}`} value={v.rep}>
                    {v.title ? `${v.title} (${v.rep})` : v.rep}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null;

      if (docContent?.type === 'chart') {
        return (
          <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
            {viewSelector}
            <ChartArtifactView
              chart={docContent}
              fetchArtifactDoc={fetchArtifactDocumentById}
              queryMetricRange={queryMetricRange}
              fullTables={maximized}
            />
          </div>
        );
      }

      if (isTopologyArtifactContent(docContent)) {
        return (
          <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
            {viewSelector}
            {renderTopologyArtifact(
              artifact,
              docContent as TopologyArtifactContent,
              maximized
                ? ChatActiveArtifactContextschemaSurfaceEnum.Fullscreen
                : ChatActiveArtifactContextschemaSurfaceEnum.Inspector,
              { compact: !maximized, artifactRep: activeRep }
            )}
          </div>
        );
      }

      if ((docContent as any)?.type === 'table') {
        const rows = Array.isArray((docContent as any).rows) ? ((docContent as any).rows as Record<string, unknown>[]) : [];
        const rowKeys = Array.isArray((docContent as any).rowKeys) ? ((docContent as any).rowKeys as string[]) : [];
        const highlighted = new Set((artifactTableHighlights[artifactKey] ?? []).map((v) => String(v)));
        const columns = Array.isArray((docContent as any).columns)
          ? (((docContent as any).columns).filter((c: unknown) => typeof c === 'string') as string[])
          : inferTableColumns(rows);
        return (
          <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
	            {viewSelector}
	            <div className="chat-artifact-table">
	              <div className="chat-artifact-table__wrap" data-artifact-tablewrap={artifactKey}>
	                <table>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={`${artifact.id}-col-${col}`}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
	                    {rows.map((row, idx) => {
	                      const rowKey = idx >= 0 && idx < rowKeys.length ? rowKeys[idx] : '';
	                      const isHL = rowKey && highlighted.has(rowKey);
	                      return (
	                        <tr
	                          key={`${artifact.id}-row-${idx}`}
	                          data-row-key={rowKey || undefined}
	                          className={isHL ? 'chat-artifact-table__row--highlight' : ''}>
	                          {columns.map((col) => (
	                            <td key={`${artifact.id}-row-${idx}-col-${col}`}>{String(row[col] ?? '')}</td>
	                          ))}
	                        </tr>
	                      );
	                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      }

      const isCommandOutput = String(artifact.kind ?? '').toLowerCase() === 'commandoutput';
      const wrap = inspectorWrap[artifactKey] ?? false;
      const className = [
        'chat-inspector__pre',
        isCommandOutput ? 'chat-inspector__pre--terminal' : '',
        wrap ? 'chat-inspector__pre--wrap' : ''
      ]
        .filter(Boolean)
        .join(' ');

      const needsFull = (docContent as any)?.type === 'commandOutput' && Boolean((docContent as any).hasMore) && !preview.isFull;
      const canAuto = typeof artifact.size === 'number' && artifact.size <= INSPECTOR_AUTO_FULL_MAX_BYTES;
      const terminalText =
        (docContent as any)?.type === 'commandOutput'
          ? (() => {
              const fullText = typeof (docContent as any).fullText === 'string' ? (docContent as any).fullText : '';
              const previewText = typeof (docContent as any).previewText === 'string' ? (docContent as any).previewText : '';
              if (preview.isFull && fullText) {
                return fullText;
              }
              if (previewText) {
                return previewText;
              }
              return preview.content;
            })()
          : preview.content;
	      return (
	        <div className={`chat-inspector__preview ${maximized ? 'chat-inspector__preview--max' : ''}`}>
	          {viewSelector}
	          <div
	            className="chat-inspector__pre-wrap"
	            data-artifact-prewrap={artifactKey}
	            onScroll={(e) => maybeAutoLoadFullOnScroll(artifact, e)}>
	            <pre className={className}>{renderHighlightedPreview(artifactKey, terminalText)}</pre>
	          </div>
          {needsFull && !canAuto && (
            <div className="chat-inspector__cta">
              <span>Reached end of preview.</span>
              <button type="button" onClick={() => void handleLoadFullArtifact(artifact)} disabled={preview.loading}>
                Load full output
              </button>
            </div>
          )}
        </div>
      );
    },
    [
      artifactPreview,
      artifactTableHighlights,
      artifactViewReps,
      fetchArtifactDocumentById,
      handleLoadFullArtifact,
      inspectorWrap,
      maybeAutoLoadFullOnScroll,
      queryMetricRange,
      renderHighlightedPreview,
      renderTopologyArtifact
    ]
  );

  const renderThreadColumn = () => {
    return (
      <div className="chat-thread-column">
        <div className="chat-thread-column__toolbar">
          <div className="chat-thread-column__toolbar-title">
            <strong>Chat threads</strong>
            <span>{threadListError && !retainPreviousThreads ? 'Threads unavailable' : `${totalThreadsCount} threads`}</span>
          </div>
          <div className="chat-thread-column__toolbar-actions">
            <button
              type="button"
              className="chat-thread-column__toolbar-icon"
              aria-label="Collapse threads"
              onClick={() => setThreadListCollapsed(true)}>
              ⟨
            </button>
            <button type="button" className="chat-thread-column__toolbar-icon" aria-label="Create thread" onClick={startNewThread}>
              +
            </button>
            <button
              type="button"
              className="chat-thread-column__toolbar-icon"
              aria-label="Reload threads"
              onClick={refreshThreadsAndHistory}>
              ⟳
            </button>
            <button
              type="button"
              className="chat-thread-column__toolbar-icon"
              aria-label="Filter threads"
              onClick={() => setShowFilters((prev) => !prev)}>
              ⚙
            </button>
          </div>
        </div>

        <>
          {showFilters && (
            <div className="chat-thread-filter-panel">
              <div className="chat-thread-filter-panel__section">
                <p>Owner</p>
                <label>
                  <input type="radio" name="owner-filter" checked={ownerFilter === 'mine'} onChange={() => setOwnerFilter('mine')} />
                  My threads
                </label>
                <label>
                  <input type="radio" name="owner-filter" checked={ownerFilter === 'all'} onChange={() => setOwnerFilter('all')} />
                  All org threads
                </label>
              </div>
              <div className="chat-thread-filter-panel__section">
                <p>Visibility</p>
                {(['all', 'org', 'private'] as const).map((value) => (
                  <label key={value}>
                    <input
                      type="radio"
                      name="visibility-filter"
                      checked={visibilityFilter === value}
                      onChange={() => setVisibilityFilter(value)}
                    />
                    {value === 'all' ? 'All' : value}
                  </label>
                ))}
              </div>
              <div className="chat-thread-filter-panel__section">
                <label>
                  <input type="checkbox" checked={onlyPinned} onChange={() => setOnlyPinned((prev) => !prev)} /> Pinned only
                </label>
              </div>
            </div>
          )}
          <div className="chat-thread-column__search">
            <label>
              <span className="sr-only">Search threads</span>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search threads"
              />
            </label>
          </div>
          {threadListError && !retainPreviousThreads && <p className="chat-thread-column__error">Failed to load: {threadListError.message}</p>}
          <div className="chat-thread-column__list" ref={threadListRef} onScroll={handleThreadListScroll}>
            {threadListLoading && <p className="chat-thread-column__loading">Loading threads…</p>}
            {!threadListLoading && !threadListError && filteredThreads.length === 0 ? (
              <OrgModulePlaceholder
                intent={ownerFilter === 'mine' ? 'No chat threads for your user yet. Start one with the + button.' : 'No chat threads yet. Start one with the + button.'}
              />
            ) : (
              filteredThreads.map((thread) => {
                const status = deriveThreadStatus(thread);
                const isSelected = selectedThread?.id === thread.id && !creatingThread;
                const pinned = resolvePinned(thread);
                return (
                  <div
                    key={thread.id ?? `${thread.title}-${thread.lastMessageAt}`}
                    role="button"
                    tabIndex={0}
                    className={`chat-thread-card ${isSelected ? 'chat-thread-card--selected' : ''}`}
                    onClick={() => {
                      openThread(thread);
                    }}
                    onKeyDown={(event) => {
                      if (event.currentTarget !== event.target) {
                        return;
                      }
                      if (event.key !== 'Enter' && event.key !== ' ') {
                        return;
                      }
                      event.preventDefault();
                      openThread(thread);
                    }}>
                    <div className="chat-thread-card__line">
                      <strong>{normalizeThreadTitle(thread.title)}</strong>
                      <div className="chat-thread-card__icons">
                        <button
                          type="button"
                          className={`chat-thread-card__pin ${pinned ? 'chat-thread-card__pin--active' : ''}`}
                          aria-label={pinned ? 'Unpin thread' : 'Pin thread'}
                          onClick={(event) => togglePinned(thread, event)}>
                          📌
                        </button>
                        {status && (
                          <span className="chat-thread-card__status-indicator" title={status.label}>
                            <span
                              aria-hidden="true"
                              className={`chat-thread-card__status-icon chat-thread-card__status-icon--${status.tone}`}
                            />
                            {status.tone === 'warning' && (
                              <span className="chat-thread-card__status-label">{status.label}</span>
                            )}
                          </span>
                        )}
                        <button
                          type="button"
                          className="chat-thread-card__delete"
                          aria-label={deletingThreadId === thread.id ? 'Deleting thread' : 'Delete thread'}
                          disabled={deletingThreadId === thread.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            openThreadDeleteDialog(thread);
                          }}>
                          🗑
                        </button>
                      </div>
                    </div>
                    <div className="chat-thread-card__line chat-thread-card__line--secondary">
                      <span>{formatDateTime(thread.lastMessageAt ?? thread.updatedAt ?? null)}</span>
                      <span>{thread.visibility ?? 'org'}</span>
                    </div>
                  </div>
                );
              })
            )}
            {threadsLoadingMore && <p className="chat-thread-column__loading">Loading more threads…</p>}
            {threadsLoadMoreError && <p className="chat-thread-column__error">Failed to load more threads.</p>}
          </div>
        </>
      </div>
    );
  };

  return (
    <>
      <div
        className={[
          'chat-page',
          threadListCollapsed ? 'chat-page--threads-collapsed' : '',
          inspectorOpen ? 'chat-page--inspector-open' : ''
        ]
          .filter(Boolean)
          .join(' ')}>
        {!threadListCollapsed && renderThreadColumn()}

        <div className="chat-module__detail-column">
          {chatRefreshNotice && (
            <p className="org-shell__banner" role="status">
              {chatRefreshNotice}
            </p>
          )}
          {activeChatBannerMessage && (
            <p className="org-shell__banner" role="alert">
              {activeChatBannerMessage}
            </p>
          )}
          {threadListCollapsed && (
            <button
              type="button"
              className="chat-threads-fab"
              aria-label="Show chat threads"
              title="Show chat threads"
              onClick={() => setThreadListCollapsed(false)}>
              ⟩ Threads
            </button>
          )}
          <div className="chat-module__global-actions">
            <div className="chat-module__view-toggle" role="tablist" aria-label="Chat view mode">
              {([
                ['user', 'User view'],
                ['agent', 'Agent view'],
                ['timeline', 'Timeline view']
              ] as Array<[ChatViewMode, string]>).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={chatViewMode === mode}
                  className={`chat-module__toolbar-btn${chatViewMode === mode ? ' chat-module__toolbar-btn--active' : ''}`}
                  onClick={() => setChatViewMode(mode)}>
                  {label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`chat-module__toolbar-icon ${copiedThreadLink ? 'chat-module__toolbar-icon--copied' : ''}`}
              onClick={handleCopyThreadLink}
              aria-label={copiedThreadLink ? 'Copied chat thread link' : 'Copy chat thread link'}
              disabled={!selectedThread?.id}
              title={
                copiedThreadLink
                  ? 'Copied chat thread link'
                  : selectedThread?.visibility === 'private'
                  ? 'Copy chat thread link. Private threads may not be accessible to other org users.'
                  : 'Copy chat thread link'
              }>
              {copiedThreadLink ? '✓' : '⧉'}
            </button>
            <button type="button" className="chat-module__toolbar-btn" onClick={openRagLookupModal}>
              Validate Rag Lookup
            </button>
            <button
              type="button"
              className="chat-module__toolbar-btn"
              disabled={!canToggleArtifacts}
              onClick={() => {
                if (inspectorOpen && inspectorTab === 'artifacts') {
                  closeInspector();
                  return;
                }
                void openInspectorArtifactsTab();
              }}>
              {inspectorOpen && inspectorTab === 'artifacts' ? 'Hide artifacts' : 'Artifacts'}
            </button>
          </div>
          {renderDetailPane()}
        </div>

        {inspectorOpen && (
          <div ref={inspectorRef} className="chat-inspector">
            <div className="chat-inspector__header">
              <div className="chat-inspector__tabs">
                <button
                  type="button"
                  className={`chat-inspector__tab ${inspectorTab === 'preview' ? 'chat-inspector__tab--active' : ''}`}
                  onClick={() => setInspectorTab('preview')}
                  disabled={!inspectorSelectedArtifact}>
                  Preview
                </button>
                <button
                  type="button"
                  className={`chat-inspector__tab ${inspectorTab === 'artifacts' ? 'chat-inspector__tab--active' : ''}`}
                  onClick={() => setInspectorTab('artifacts')}>
                  Files & artifacts
                </button>
              </div>
              <div className="chat-inspector__header-actions">
                {inspectorSelectedArtifact && (
                  <button type="button" className="chat-inspector__icon-btn" onClick={() => setInspectorMaximized(true)}>
                    Maximize
                  </button>
                )}
                <button type="button" className="chat-inspector__icon-btn" onClick={closeInspector} aria-label="Close inspector">
                  ×
                </button>
              </div>
            </div>

            {inspectorTab === 'artifacts' && (
              <div className="chat-inspector__list">
                <div className="chat-inspector__list-header">
                  <strong>Files & artifacts</strong>
                  {artifactsLoading && <span>Loading…</span>}
                </div>
                {finalFileAttachments.length === 0 && finalGeneratedArtifacts.length === 0 && intermediateArtifacts.length === 0 ? (
                  <p className="chat-inspector__empty">
                    {isStreaming ? 'Files and artifacts will appear here as the run streams.' : 'No files or artifacts captured for this run yet.'}
                  </p>
                ) : (
                  <>
                    {finalFileAttachments.length > 0 && (
                      <div className="chat-inspector__artifact-section">
                        <div className="chat-inspector__artifact-section-label">Attachments</div>
                        {finalFileAttachments.map((artifact) => {
                          const sizeLabel = formatArtifactSize(artifact.size);
                          return (
                            <button
                              key={`attachment-${artifactStateKey(artifact)}`}
                              type="button"
                              className={`chat-inspector__list-item ${
                                inspectorArtifactId === artifactStateKey(artifact) ? 'chat-inspector__list-item--active' : ''
                              }`}
                              onClick={() => void openInspectorForArtifact(artifact, undefined, 'artifacts')}>
                              <span className="chat-inspector__list-icon">{resolveArtifactIcon(artifact)}</span>
                              <span className="chat-inspector__list-title">{renderArtifactDisplayTitle(artifact)}</span>
                              <span className="chat-inspector__list-meta">
                                {sizeLabel ? sizeLabel : ''}
                                {artifact.mimeType ? ` ${artifact.mimeType}` : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {finalGeneratedArtifacts.length > 0 && (
                      <div className="chat-inspector__artifact-section">
                        <div className="chat-inspector__artifact-section-label">Final artifacts</div>
                        {finalGeneratedArtifacts.map((artifact) => {
                          const sizeLabel = formatArtifactSize(artifact.size);
                          return (
                            <button
                              key={`final-${artifactStateKey(artifact)}`}
                              type="button"
                              className={`chat-inspector__list-item ${
                                inspectorArtifactId === artifactStateKey(artifact) ? 'chat-inspector__list-item--active' : ''
                              }`}
                              onClick={() => void openInspectorForArtifact(artifact, undefined, 'artifacts')}>
                              <span className="chat-inspector__list-icon">{resolveArtifactIcon(artifact)}</span>
                              <span className="chat-inspector__list-title">{renderArtifactDisplayTitle(artifact)}</span>
                              <span className="chat-inspector__list-meta">
                                {sizeLabel ? sizeLabel : ''}
                                {artifact.mimeType ? ` ${artifact.mimeType}` : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {intermediateArtifacts.length > 0 && (
                      <div className="chat-inspector__artifact-section">
                        <div className="chat-inspector__artifact-section-label">Intermediate artifacts</div>
                        {intermediateArtifacts.map((artifact) => {
                          const sizeLabel = formatArtifactSize(artifact.size);
                          return (
                            <button
                              key={`intermediate-${artifactStateKey(artifact)}`}
                              type="button"
                              className={`chat-inspector__list-item ${
                                inspectorArtifactId === artifactStateKey(artifact) ? 'chat-inspector__list-item--active' : ''
                              }`}
                              onClick={() => void openInspectorForArtifact(artifact, undefined, 'artifacts')}>
                              <span className="chat-inspector__list-icon">{resolveArtifactIcon(artifact)}</span>
                              <span className="chat-inspector__list-title">{renderArtifactDisplayTitle(artifact)}</span>
                              <span className="chat-inspector__list-meta">
                                {sizeLabel ? sizeLabel : ''}
                                {artifact.mimeType ? ` ${artifact.mimeType}` : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {inspectorTab === 'preview' && (
              <div className="chat-inspector__body">
                {!inspectorSelectedArtifact ? (
                  <div className="chat-inspector__empty">
                    <p>Select an artifact to preview.</p>
                  </div>
                ) : (
                  <>
                    <div className="chat-inspector__meta">
                      <strong>{renderArtifactDisplayTitle(inspectorSelectedArtifact)}</strong>
                      <div className="chat-inspector__meta-actions">
                        {String(inspectorSelectedArtifact.kind ?? '').toLowerCase() === 'commandoutput' && (
                          <button
                            type="button"
                            className="chat-inspector__pill"
                            onClick={() =>
                              setInspectorWrap((prev) => ({
                                ...prev,
                                [artifactStateKey(inspectorSelectedArtifact)]: !(prev[artifactStateKey(inspectorSelectedArtifact)] ?? false)
                              }))
                            }>
                            {inspectorWrap[artifactStateKey(inspectorSelectedArtifact)] ? 'No wrap' : 'Wrap'}
                          </button>
                        )}
                        {renderArtifactDownloadAction(
                          inspectorSelectedArtifact,
                          'chat-inspector__pill',
                          'chat-export-format__select chat-export-format__select--inspector',
                        )}
                      </div>
                    </div>
                    {renderInspectorPreview(inspectorSelectedArtifact, false)}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {ragLookupModalOpen && (
        <div
          className="chat-rag-lookup-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-rag-lookup-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeRagLookupModal();
            }
          }}>
          <div className="chat-rag-lookup-modal__content">
            <div className="chat-rag-lookup-modal__header">
              <div>
                <h3 id="chat-rag-lookup-title">Validate Rag Lookup</h3>
                <p>Run direct retrieval and inspect returned chunks for debugging.</p>
              </div>
              <button type="button" className="chat-inspector__icon-btn" onClick={closeRagLookupModal} aria-label="Close rag lookup modal">
                ×
              </button>
            </div>

            <form
              className="chat-rag-lookup-modal__form"
              onSubmit={(event) => {
                event.preventDefault();
                void runRagLookupValidation();
              }}>
              <label>
                <span>Query</span>
                <textarea
                  rows={3}
                  value={ragLookupQuery}
                  onChange={(event) => setRagLookupQuery(event.target.value)}
                  placeholder="Enter a retrieval query"
                  disabled={ragLookupLoading}
                />
              </label>
              <label>
                <span>Top K (optional)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={ragLookupTopKInput}
                  onChange={(event) => setRagLookupTopKInput(event.target.value)}
                  placeholder="Leave empty to use backend default"
                  disabled={ragLookupLoading}
                />
              </label>
              <label>
                <span>Collections (required)</span>
                <fieldset className="chat-rag-lookup-modal__radio-group" disabled={ragLookupLoading || ragLookupCollectionsLoading}>
                  <div className="chat-rag-lookup-modal__radio-options">
                    {ragLookupCollectionsLoading ? (
                      <p>Loading collections…</p>
                    ) : ragLookupCollectionOptions.length === 0 ? (
                      <p>No collections available.</p>
                    ) : (
                      ragLookupCollectionOptions.map((collection) => (
                        <label key={collection} className="chat-rag-lookup-modal__radio-option">
                          <input
                            type="checkbox"
                            name="rag-lookup-collections"
                            value={collection}
                            checked={ragLookupCollections.includes(collection)}
                            onChange={() =>
                              setRagLookupCollections((previous) =>
                                previous.includes(collection)
                                  ? previous.filter((value) => value !== collection)
                                  : [...previous, collection]
                              )
                            }
                          />
                          <span>{collection}</span>
                        </label>
                      ))
                    )}
                  </div>
                </fieldset>
              </label>
              {ragLookupCollections.some((collection) => collection.toLowerCase().includes('config')) && (
                <label>
                  <span>Config collection document_file (optional)</span>
                  <select
                    value={ragLookupChunkConfigDocumentFile}
                    onChange={(event) => setRagLookupChunkConfigDocumentFile(event.target.value)}
                    disabled={ragLookupLoading || ragLookupCollectionsLoading}>
                    <option value="">Do not filter by document_file</option>
                    {RAG_LOOKUP_CHUNK_CONFIG_DOCUMENT_FILE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {ragLookupCollections.some((collection) => collection.toLowerCase().includes('troubleshooting')) && (
                <label>
                  <span>Troubleshooting collection document_file (optional)</span>
                  <select
                    value={ragLookupChunkTroubleshootingDocumentFile}
                    onChange={(event) => setRagLookupChunkTroubleshootingDocumentFile(event.target.value)}
                    disabled={ragLookupLoading || ragLookupCollectionsLoading}>
                    <option value="">Do not filter by document_file</option>
                    {RAG_LOOKUP_CHUNK_TROUBLESHOOTING_DOCUMENT_FILE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span>Vendors (required)</span>
                <fieldset className="chat-rag-lookup-modal__radio-group" disabled={ragLookupLoading}>
                  <div className="chat-rag-lookup-modal__radio-options">
                    {RAG_LOOKUP_VENDOR_OPTIONS.map((option) => (
                      <label key={option.value} className="chat-rag-lookup-modal__radio-option">
                        <input
                          type="radio"
                          name="rag-lookup-vendor"
                          value={option.value}
                          checked={ragLookupVendor === option.value}
                          onChange={(event) => setRagLookupVendor(event.target.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </label>
              <div className="chat-rag-lookup-modal__form-actions">
                <button
                  type="submit"
                  className="chat-module__toolbar-btn"
                  disabled={ragLookupLoading || ragLookupCollectionsLoading || ragLookupCollections.length === 0}>
                  {ragLookupLoading ? 'Validating…' : 'Run validation'}
                </button>
              </div>
            </form>

            {ragLookupError && <p className="chat-rag-lookup-modal__error">{ragLookupError}</p>}

            {!ragLookupResult ? (
              <p className="chat-rag-lookup-modal__empty">Submit a query to inspect retrieved chunks.</p>
            ) : (
              <div className="chat-rag-lookup-modal__results">
                <div className="chat-rag-lookup-modal__meta">
                  <span>
                    Query: <strong>{ragLookupResult.query}</strong>
                  </span>
                  <span>
                    Top K: <strong>{ragLookupResult.topK}</strong>
                  </span>
                  <span>
                    Latency: <strong>{ragLookupResult.latencyMs} ms</strong>
                  </span>
                  <span>
                    Chunks: <strong>{ragLookupResult.chunks.length}</strong>
                  </span>
                </div>
                {ragLookupResult.chunks.length === 0 ? (
                  <p className="chat-rag-lookup-modal__empty">No chunks were returned for this query.</p>
                ) : (
                  <div className="chat-rag-lookup-modal__chunk-list">
                    {ragLookupResult.chunks.map((chunk, index) => {
                      const chunkKey = chunk.id?.trim() || `chunk-${index + 1}`;
                      return (
                        <article key={`${chunkKey}-${index}`} className="chat-rag-lookup-modal__chunk">
                          <div className="chat-rag-lookup-modal__chunk-header">
                            <strong>
                              #{index + 1} {chunk.id || 'Chunk'}
                            </strong>
                            <span>Score {Number.isFinite(chunk.score) ? chunk.score.toFixed(4) : '0.0000'}</span>
                          </div>
                          <p className="chat-rag-lookup-modal__chunk-source">{`Collection: ${chunk.source || 'Not provided'}`}</p>
                          <pre className="chat-rag-lookup-modal__chunk-content">{chunk.content || 'Chunk content is empty.'}</pre>
                          <div className="chat-rag-lookup-modal__chunk-actions">
                            <button
                              type="button"
                              className="chat-module__toolbar-btn"
                              onClick={() => void copyRagLookupChunk(chunk, index)}>
                              {ragLookupCopiedChunkId === chunkKey ? 'Copied' : 'Copy JSON'}
                            </button>
                          </div>
                          <details className="chat-rag-lookup-modal__chunk-metadata">
                            <summary>Metadata JSON</summary>
                            <pre>{JSON.stringify(chunk.metadata ?? {}, null, 2)}</pre>
                          </details>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        open={pendingDeleteThread != null}
        title="Delete chat thread?"
        description={`Delete "${normalizeThreadTitle(pendingDeleteThread?.title)}"? This cannot be undone.`}
        deleting={deletingThreadId != null}
        onCancel={() => setPendingDeleteThread(null)}
        onConfirm={() => void confirmDeleteThread()}
      />
      {threadDeleteToast && (
        <StatusToast
          tone={threadDeleteToast.tone}
          message={threadDeleteToast.message}
          occurrence={threadDeleteToast}
          onDismiss={() => setThreadDeleteToast(null)}
        />
      )}

      {inspectorOpen && inspectorMaximized && inspectorSelectedArtifact && (
        <div className="chat-inspector-modal" role="dialog" aria-modal="true">
          <div className="chat-inspector-modal__content">
            <div className="chat-inspector-modal__header">
              <strong>{renderArtifactDisplayTitle(inspectorSelectedArtifact)}</strong>
              <div className="chat-inspector-modal__actions">
                {String(inspectorSelectedArtifact.kind ?? '').toLowerCase() === 'commandoutput' && (
                  <button
                    type="button"
                    className="chat-inspector__pill"
                    onClick={() =>
                      setInspectorWrap((prev) => ({
                        ...prev,
                        [artifactStateKey(inspectorSelectedArtifact)]: !(prev[artifactStateKey(inspectorSelectedArtifact)] ?? false)
                      }))
                    }>
                    {inspectorWrap[artifactStateKey(inspectorSelectedArtifact)] ? 'No wrap' : 'Wrap'}
                  </button>
                )}
                {renderArtifactDownloadAction(
                  inspectorSelectedArtifact,
                  'chat-inspector__pill',
                  'chat-export-format__select chat-export-format__select--inspector',
                )}
                <button type="button" className="chat-inspector__icon-btn" onClick={() => setInspectorMaximized(false)} aria-label="Close maximize">
                  ×
                </button>
              </div>
            </div>
            {renderInspectorPreview(inspectorSelectedArtifact, true)}
          </div>
        </div>
      )}

    </>
  );
};

export default ChatAgentsPage;
